import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { CalendarDays, User } from "lucide-react";
import moment from "moment-timezone";
import { DynamicContext } from "../../../Context/DynamicContext";
import DashboardCard from "../../../Common/Card/DashboardCard";
import { generateFunnelColors } from "../../../Common/constants/funnelColorVerify";
import Table from "../../../Common/Table/Table";
import {
  StandardPopup,
  ConfirmationPopup,
} from "../../../Common/PopupStyles/PopupComponent";
import ToggleSwitcher from "../../../Common/Toggle-Switcher/ToggleSwitcher";
import PipelineStatusCard from "../UserDashboard/PipelineStatusCard";
import {
  applyEllipsis,
  filterPayload,
  formatInterviewerName,
  toCamelCase,
  toCapitalize,
  toSnakeCase,
} from "../../../Common/Common-Function/CommonFunctions";
import {
  deleteRequest,
  get,
  patch,
  post,
  put,
} from "../../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  LIST_MEMEBERS_OF_TENANT,
  GET_CLIENTS_LIST,
  SEARCH_CLIENTS,
  ADMIN_RECRUITMENT_OVERVIEW,
  ADMIN_JOB_PIPELINE,
  ADMIN_RECRUITMENT_PERFORMANCE,
  ADMIN_TEAM_WORKLOAD,
  ADMIN_RECRUITMENT_FUNNEL,
  WEB_SOCKET_URL,
  NOTIFICATIONS_GET,
  GET_JOB_DETAILS_BY_ID,
  UPDATE_JOB,
  DELETE_JOB_DETAILS,
  SIMPLE_SEARCH_CANDIDATES,
  GET_TENANT_DETAILS,
  GET_CLIENT_DETAILS,
  GET_CANDIDATE_BY_ID,
  GET_INTERVIEW_BY_TIME_RANGE,
  GET_INTERVIEWS_BY_ID,
  METRICS_RECRUITER_DASHBOARD_FOLLOWUPS,
  UPDATE_CANDIDATE_DETAILS,
  DELETE_CANDIDATE_DETAILS,
  ADMIN_METRICS_JOB_APPLICATIONS,
  ADMIN_METRICS_RECRUITER_APPLICATIONS,
  GET_OVERALL_CANDIDATES_COUNT,
  GET_OVERALL_CLIENTS_COUNT,
  GET_OVERALL_ACTIVE_JOBS_COUNT,
  VENDOR_PIPELINE_METRICS,
  VENDOR_PIPELINES_ACTIONS,
  GET_VENDOR_DETAILS_BY_ID,
  UPDATE_VENDORS_INFO_FIELDS,
  RECOMMEND_JOBS,
  VENDORS_SEARCH,
  GET_VENDORS_LIST,
  GET_SAVED_VIEWS,
  CREATE_SAVED_VIEWS,
  UPDATE_SAVED_VIEWS,
  DELETE_SAVED_VIEWS,
} from "../../../helper/url_helper";
import { TENANT_CORE_API_KEY } from "../../../Tenant-Config/TenantConfig";
import "./AdminDashboard.css";
import AdminFollowUps from "./AdminFollowUps";
import NotificationPopup from "../../../Common/Notification-Popup/NotificationPopup";
import notificationIcon from "../../../Assets/Images/notifications-read-light.svg";
import notificationDarkIcon from "../../../Assets/Images/notifications-read-dark.svg";
import activeCandidatesIcon from "../../../Assets/Images/active-candidates-dashboard-icon.svg";
import activeCandidatesDarkIcon from "../../../Assets/Images/active-candidates-dashboard-icon-dark.svg";
import activeJobsIcon from "../../../Assets/Images/active-jobs-dashboard-icon.svg";
import activeJobsDarkIcon from "../../../Assets/Images/active-jobs-dashboard-icon-dark.svg";
import totalClientLightIcon from "../../../Assets/Images/Total_Clients_Light.svg";
import totalClientDarkIcon from "../../../Assets/Images/Total_Clients_Dark.svg";
import EyeIcon from "../../../Assets/Images/Eye-Icon.svg";
import accountIcon from "../../../Assets/Images/accountIcon.png";
import SettingsDarkIcon from "../../../Assets/Images/settings-dark.svg";
import SettingsIcon from "../../../Assets/Images/mingcute_settings-1-line.svg";
import jobIcon from "../../../Assets/Images/jobTypeIcon.png";
import mailIcon from "../../../Assets/Images/mail_icon.png";
import phoneIcon from "../../../Assets/Images/phone_icon.png";
import TextInput from "../../../Common/TextInput/TextInput";
import PhoneNumberInput from "../../../Common/PhoneNumberInput/PhoneNumberInput";
import DynamicAutocomplete from "../../../Common/AutoSuggestion/DynamicAutocomplete";
import MonthFilter from "../../../Common/Month-Filter/MonthFilter";
import DynamicMultipleSelectCheckmarks from "../../Multiple-Select-Checkmarks/MultipleSelectCheckmarks";
import HelloIcon from "../../../Assets/Images/Hello-Icon.svg";
import JobsDetailsContainer from "../../../Common/Jobs-Details-Container/JobsDetailsContainer";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import VybogTALLoaderAnimation from "../../../Common/Dynamic-Animations/DynamicAnimations";
import NoJobsDarkIcon from "../../../Assets/Images/no-jobs-dark.svg";
import NoJobLightIcon from "../../../Assets/Images/no-jobs-light.svg";
import NoReqDarkIcon from "../../../Assets/Images/no-recruiter-performance-dark.svg";
import NoReqLightIcon from "../../../Assets/Images/no-recruiter-performance-light.svg";
import NoFunnelDarkIcon from "../../../Assets/Images/no-recruitment-funnel-dark.svg";
import NoFunnelLightIcon from "../../../Assets/Images/no-recruitment-funnel-light.svg";
import NoWorkDarkIcon from "../../../Assets/Images/no-team-workload-dark.svg";
import NoWorkLightIcon from "../../../Assets/Images/no-team-workload-light.svg";
import {
  candidatesCache,
  channelIdCache,
} from "../../Applicant-Tracking/Jobs/RecommendCandidates";
import SidePopup from "../../../Common/SidePopup/SidePopup";
import CandidateDetailsContainer from "../../../Common/Candidate-Details-Container/CandidateDetailsContainer";
import AnimationPlayer from "../../../Common/Animation-Player/AnimationPlayer";
import NoDataFoundAnimation from "../../../Assets/Json/nodata-found-robot-animation.json";
import Button from "../../../Common/Button/Button";
import editIcon from "../../../Assets/Images/edit-track-icon.svg";
import deleteIcon from "../../../Assets/Images/delete-red-icon.png";
import ReachOutIcon from "../../../Assets/Images/reach-out-icon.svg";
import StageFilterPopup from "../../../Common/StageFilterPopup/StageFilterPopup";
import TrialSnackbar from "../FreeTrialToast";
import addMemberImage from "../../../Assets/Images/add-member-image.png";
import SuperAdminDashboard from "../Super-Admin-Dashboard/SuperAdminDashboard";
import { fetchCallStages } from "../../../Common/DynamicCallStage/DynamicCallStageMapping";
import VendorInfo from "../../Applicant-Tracking/Vendors/VendorInfo";
import VendorsCandidateList from "../../Applicant-Tracking/Vendors/VendorsCandidateList";
import VendorDocuments from "../../Applicant-Tracking/Vendors/VendorDocuments";
import ContactCard from "../../../Common/Contact-Card/ContactCard";
import VendorTrackingHistory from "../../Applicant-Tracking/Vendors/VendorTrackingHistory";
import "../../Applicant-Tracking/Vendors/Vendors.css";
import noVendorDarkIcon from "../../../Assets/Images/no-vendor-dark.svg";
import noVendorLightIcon from "../../../Assets/Images/no-vendor-light.svg";
import CriticalIcon from "../../../Assets/Images/critical-icon.svg";
import HighIcon from "../../../Assets/Images/high-icon.svg";
import LowIcon from "../../../Assets/Images/low-icon.svg";
import MediumIcon from "../../../Assets/Images/medium-icon.svg";
import OptionsData from "../../../Assets/Json/StaticOptions.json";
import DynamicSelectInput from "../../../Common/Select-Input/SelectInput";
import sortAscDarkIcon from "../../../Assets/Images/sort-asc-dark.svg";
import sortAscLightIcon from "../../../Assets/Images/sort-asc-light.svg";
import sortDscDarkIcon from "../../../Assets/Images/sort-des-dark.svg";
import sortDscLightIcon from "../../../Assets/Images/sort-des-light.svg";
import trackedTodayIcon from "../../../Assets/Images/dashboard-tt.svg";
import trackedTodayDarkIcon from "../../../Assets/Images/dashboard-tt-dark.svg";
import candidateIcon from "../../../Assets/Images/candidate-schedule-icon.png";
import scheduleJobIcon from "../../../Assets/Images/job-schedule-icon.png";
import emailIcon from "../../../Assets/Images/email-schedule-icon.png";
import contactNoIcon from "../../../Assets/Images/phone-schedule-icon.png";
import interviewPersonIcon from "../../../Assets/Images/interview-person-schedule-icon.png";
import dateIcon from "../../../Assets/Images/calendar-schedule-icon.png";
import linkIcon from "../../../Assets/Images/link-schedule-icon.png";
import meetingTimeIcon from "../../../Assets/Images/meeting-time-icon.svg";
import viewMeetingIcon from "../../../Assets/Images/view-meeting-icon.svg";
import NoMeetingAnimation from "../../../Assets/Json/No-Meetings-Animation.json";
import { convertUTCTimeToUserTimezone } from "../../../Common/Time-Zone-Utils/timezoneUtils";

const parseDateOnly = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const areSameIdLists = (first = [], second = []) => {
  const a = [...first].map(String).sort();
  const b = [...second].map(String).sort();
  return a.length === b.length && a.every((id, index) => id === b[index]);
};
const getFirstValue = (source, keys, fallback = 0) => {
  if (!source || typeof source !== "object") return fallback;

  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }

  if (source.data && typeof source.data === "object") {
    return getFirstValue(source.data, keys, fallback);
  }

  return fallback;
};

const getFirstText = (source, keys, fallback = "--") =>
  getFirstValue(source, keys, fallback) || fallback;

const getFirstArray = (source, keys) => {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];

  for (const key of keys) {
    if (Array.isArray(source[key])) return source[key];
  }

  if (source.data && typeof source.data === "object") {
    return getFirstArray(source.data, keys);
  }

  return [];
};

const JOB_PIPELINE_ROW_KEYS = [
  "jobs",
  "pipeline",
  "job_pipeline",
  "jobPipeline",
  "items",
  "results",
  "rows",
];

const getClientOptionId = (client = {}) =>
  getFirstValue(client, ["id", "client_id", "clientId", "value"], "");

const getClientOptionName = (client = {}) =>
  getFirstValue(
    client,
    [
      "name",
      "clientName",
      "client_name",
      "company_name",
      "companyName",
      "label",
    ],
    "",
  );

const formatClientOption = (client) => ({
  label: getClientOptionName(client),
  value: getClientOptionId(client),
});

const getSavedViewClientId = (filters = {}) =>
  getFirstValue(filters, ["clientId", "client_id", "clientID"], "") ||
  getClientOptionId(filters.client);

const getSavedViewClientName = (filters = {}) =>
  getFirstValue(filters, ["clientName", "client_name", "companyName"], "") ||
  getClientOptionName(filters.client);

const isValidDashboardFilterValue = (value) =>
  value !== undefined && value !== null && value !== "" && value !== "All";

const normalizeRecruiterIds = (value) => {
  if (!Array.isArray(value)) return [];

  return value.filter(isValidDashboardFilterValue);
};

const buildJobPipeline = (pipelineResponse) => {
  const pipelineRows = getFirstArray(pipelineResponse, JOB_PIPELINE_ROW_KEYS);

  if (!pipelineRows.length) return [];

  return pipelineRows.map((item, index) => ({
    id: getFirstText(item, ["id", "job_id", "jobId"], ""),
    job: getFirstText(
      item,
      [
        "jobName",
        "job_name",
        "job",
        "job_title",
        "jobTitle",
        "title",
        "position",
      ],
      `Job ${index + 1}`,
    ),
    client: getFirstText(item, [
      "clientName",
      "client",
      "client_name",
      "company",
      "company_name",
    ]),
    priority: item?.priority,
    req: getFirstValue(item, [
      "req",
      "requirements",
      "required",
      "openings",
      "positions",
      "required_count",
    ]),
    stages: Array.isArray(item?.stages) ? item.stages : [],
    created_at: item?.created_at,
    updated_at: item?.updated_at,
    active: item?.active,
    totalRecruiters: item?.totalRecruiters,
    assignedRecruiters: item?.assignedRecruiters,
    trackedRecruiters: item?.trackedRecruiters,
    completion:
      parseFloat(
        String(
          getFirstValue(item, [
            "completion",
            "completion_percentage",
            "completionPercentage",
            "progress",
          ]),
        ).replace("%", ""),
      ) || 0,
  }));
};

const getStageDefinitionsFromRows = (rows = []) => {
  const stageMap = new Map();

  rows.forEach((row) => {
    if (!Array.isArray(row?.stages)) return;

    row.stages.forEach((stage) => {
      const systemKey = stage?.system_key || stage?.systemKey;
      if (!systemKey || stageMap.has(systemKey)) return;

      stageMap.set(systemKey, {
        ...stage,
        system_key: systemKey,
        name: stage?.name || systemKey,
      });
    });
  });

  return Array.from(stageMap.values());
};

const getCustomizeColumnsFromTableColumns = (columns = []) =>
  columns.reduce((acc, column) => {
    acc[column.columnKey] =
      typeof column.name === "string" ? column.name : column.columnKey;
    return acc;
  }, {});

const getSavedColumnKeys = (savedColumns = [], availableKeys = []) =>
  savedColumns
    .map((column) => column?.system_key || column?.systemKey || column)
    .filter((columnKey) => availableKeys.includes(columnKey));

const getSelectedCustomizeColumns = (columnsForCustomize, selectedKeys) =>
  Object.entries(columnsForCustomize)
    .filter(([key]) => selectedKeys.includes(key))
    .map(([systemKey, name]) => ({
      system_key: systemKey,
      name,
    }));

const buildVendorPipeline = (pipelineResponse) => {
  const pipelineRows = pipelineResponse?.vendors || [];

  if (!pipelineRows.length) return [];

  return pipelineRows.map((item, index) => ({
    id: getFirstText(item, ["id"], ""),
    vendor: getFirstText(item, ["vendorName"], `Vendor ${index + 1}`),
    statuses: Array.isArray(item?.statuses) ? item.statuses : [],
  }));
};

const parsePercentage = (value) =>
  parseFloat(String(value ?? 0).replace("%", "")) || 0;

const roundCompletion = (value) => Math.round(parsePercentage(value));

const buildMetricTrend = ({ notePrefix, noteValue, noteSuffix }) => {
  if (noteValue === null || noteValue === undefined) return "";

  return [notePrefix, noteValue, noteSuffix].filter(Boolean).join(" ");
};

const buildRecruiterPerformance = (performanceResponse) => {
  const recruiterRows = getFirstArray(performanceResponse, [
    "recruiters",
    "recruiter_performance",
    "recruiterPerformance",
    "performance",
    "items",
    "results",
    "rows",
  ]);

  if (!recruiterRows.length) return [];

  return recruiterRows.map((item, index) => {
    const fullName = [item?.firstName, item?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const name =
      fullName ||
      getFirstText(
        item,
        ["name", "recruiter", "recruiter_name", "recruiterName", "full_name"],
        `Recruiter ${index + 1}`,
      );

    return {
      id: getFirstText(item, ["id", "recruiter_id", "recruiterId"], name),
      name,
      firstName: item?.firstName || "",
      lastName: item?.lastName || "",
      jobs: getFirstValue(item, ["jobs", "job_count", "jobCount"]),
      reviewed: getFirstValue(item, [
        "reviewed",
        "reviewed_count",
        "reviewedCount",
      ]),
      assignedJobs: getFirstValue(item, [
        "assignedJobs",
        "assigned_jobs",
        "assignedJobCount",
        "assigned_job_count",
      ]),
      trackedJobs: getFirstValue(item, [
        "trackedJobs",
        "tracked_jobs",
        "trackedJobCount",
        "tracked_job_count",
      ]),
      resumesParsed: getFirstValue(item, [
        "resumesParsed",
        "resumes_parsed",
        "resumeParsed",
      ]),
      jobsCreated: getFirstValue(item, [
        "jobsCreated",
        "jobs_created",
        "jobCreated",
      ]),
      candidatesCreated: getFirstValue(item, [
        "candidatesCreated",
        "candidates_created",
        "candidateCreated",
      ]),
      completion: parsePercentage(
        getFirstValue(item, [
          "completion",
          "completion_percentage",
          "completionPercentage",
          "progress",
        ]),
      ),
      stages: item?.stages || [],
    };
  });
};

const buildTeamWorkload = (workloadResponse) => {
  const workloadColors = [
    "#ff4d5f",
    "#f5a623",
    "#8b7cf6",
    "#7d6ee7",
    "#6f63db",
  ];
  const workloadRows = getFirstArray(workloadResponse, [
    "workload",
    "team_workload",
    "teamWorkload",
    "items",
    "results",
    "rows",
  ]);

  if (!workloadRows.length) return [];

  return workloadRows.map((item, index) => {
    const fullName = [item?.firstName, item?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const name =
      fullName ||
      getFirstText(
        item,
        ["name", "recruiter", "recruiter_name", "recruiterName", "full_name"],
        `Recruiter ${index + 1}`,
      );
    const jobs = getFirstValue(item, ["jobs", "job_count", "jobCount"]);
    const candidates = getFirstValue(item, [
      "candidates",
      "candidate_count",
      "candidateCount",
    ]);

    return {
      id: getFirstText(item, ["id", "recruiter_id", "recruiterId"], name),
      stage: name,
      count: jobs,
      barValue: jobs,
      countLabel: `${jobs} jobs - ${candidates} candidates`,
      color: workloadColors[index % workloadColors.length],
    };
  });
};

const buildRecruitmentFunnel = (funnelResponse, callStageMap = {}) => {
  let funnelRows = getFirstArray(funnelResponse, [
    "funnel",
    "recruitment_funnel",
    "recruitmentFunnel",
    "stages",
    "items",
    "results",
    "rows",
  ]);

  if (!funnelRows.length) {
    const objectSource =
      funnelResponse?.data && typeof funnelResponse.data === "object"
        ? funnelResponse.data
        : funnelResponse;
    const excludedKeys = new Set([
      "data",
      "message",
      "status",
      "success",
      "total",
      "total_candidates",
      "totalCandidates",
    ]);

    funnelRows =
      objectSource && typeof objectSource === "object"
        ? Object.entries(objectSource)
            .filter(
              ([key, value]) =>
                !excludedKeys.has(key) &&
                value !== null &&
                !Array.isArray(value),
            )
            .map(([key, value]) =>
              typeof value === "object"
                ? { stage: key, ...value }
                : { stage: key, count: value },
            )
        : [];
  }

  if (!funnelRows.length) return [];

  const stages = funnelRows.map((item, index) => {
    const stageSystemKey = getFirstText(
      item,
      ["system_key", "systemKey", "stage", "status", "call_stage", "callStage"],
      "",
    );
    const stage =
      callStageMap[stageSystemKey] ||
      getFirstText(
        item,
        ["stage", "status", "name", "label", "call_stage", "callStage"],
        stageSystemKey || `Stage ${index + 1}`,
      );
    const count = getFirstValue(item, [
      "count",
      "total",
      "value",
      "candidates",
      "candidate_count",
      "candidateCount",
    ]);
    const percentage = getFirstValue(
      item,
      [
        "percentage",
        "percent",
        "conversion",
        "conversion_rate",
        "conversionRate",
      ],
      null,
    );
    const parsedPercentage =
      percentage === null ? null : parsePercentage(percentage);

    return {
      id: getFirstText(item, ["id", "stage_id", "stageId"], stage),
      stage,
      systemKey: stageSystemKey,
      count,
      barValue: parsedPercentage ?? count,
      countLabel:
        parsedPercentage === null ? count : `${count} (${parsedPercentage}%)`,
    };
  });

  const colors = generateFunnelColors(stages);
  return stages.map(({ systemKey, ...stage }, index) => ({
    ...stage,
    ...colors[index],
  }));
};

const dashboardHeaderFilterAutocompleteSx = {
  width: "240px",
  ".MuiOutlinedInput-root": {
    borderRadius: "999px",
    minHeight: "42px",
    backgroundColor: "var(--body__background, #ffffff)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--border__color, #dfe5f2) !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6860ee !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6860ee !important",
    },
  },
  ".MuiInputBase-input": {
    color: "var(--header__color)",
    padding: "0 12px !important",
    fontSize: "13px",
    fontFamily: "inherit",
  },
};

const AutocompleteComponentsProps = {
  popper: {
    sx: {
      "& .MuiAutocomplete-paper": {
        backgroundColor: "var(--table__background, #ffffff)",
        color: "var(--header__color, #2b2929)",
        border: "1px solid var(--border__color, #dfe5f2)",
        boxShadow: "0 12px 28px rgba(0, 0, 0, 0.22)",
      },
      "& .MuiAutocomplete-listbox": {
        backgroundColor: "var(--table__background, #ffffff)",
        color: "var(--header__color, #2b2929)",
        padding: "6px 0",
      },
      "& .MuiAutocomplete-option": {
        color: "var(--header__color, #2b2929)",
        fontSize: "13px",
        "&[aria-selected='true']": {
          backgroundColor: "var(--filter-button, rgba(104, 96, 238, 0.12))",
        },
        "&.Mui-focused, &:hover": {
          backgroundColor: "var(--filter-button, rgba(104, 96, 238, 0.12))",
        },
      },
      "& .MuiAutocomplete-noOptions": {
        color: "#6860ee",
        backgroundColor: "var(--table__background, #ffffff)",
      },
      "& .MuiAutocomplete-loading": {
        color: "#6860ee",
        fontStyle: "italic",
        backgroundColor: "var(--table__background, #ffffff)",
      },
    },
  },
};

const DASHBOARD_DATE_FILTER_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Week", value: "this_week" },
  { label: "Month", value: "this_month" },
  { label: "All", value: "all" },
  { label: "Custom", value: "custom" },
];

const JOB_PIPELINE_COLUMN_SAVED_VIEW_SCOPE = "admin_dashboard_job_pipeline";
const RECRUITER_PERFORMANCE_COLUMN_SAVED_VIEW_SCOPE =
  "admin_dashboard_recruiter_performance";

const PRIORITY_ICON_MAP = { CriticalIcon, HighIcon, MediumIcon, LowIcon };

function ColumnCustomizeDropdown({
  buttonRef,
  columns,
  draftColumns,
  dropdownRef,
  idPrefix,
  isDarkMode,
  isOpen,
  hasSavedView,
  onCreateSaveView,
  onSelectAll,
  onSelectNone,
  onToggleColumn,
  onToggleDropdown,
  onUpdateSaveView,
}) {
  return (
    <div className="filter-container admin-dashboard-column-filter-container">
      <button
        className="column-visibility-btn"
        onClick={onToggleDropdown}
        ref={buttonRef}
      >
        <img
          src={isDarkMode ? SettingsDarkIcon : SettingsIcon}
          alt="SettingsIcon"
          className="speedDial-popup-icon"
        />
        Customize
      </button>
      {isOpen && (
        <div className="column-visibility-dropdown" ref={dropdownRef}>
          <div className="column-checkbox-item">
            <button
              onClick={onSelectAll}
              className="view-button"
              style={{
                height: "36px",
                width: "100px",
                background: "#6860ee",
                borderRadius: "8px",
                cursor: "pointer",
                padding: "8px 14px",
                fontFamily: "Satoshi",
                fontWeight: 600,
                fontSize: "14px",
                outline: "none",
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                justifyContent: "center",
                gap: 10,
                border: "1px solid #6860EE",
                color: "#FFFFFF",
              }}
            >
              Select All
            </button>
            <button
              onClick={onSelectNone}
              className="view-button"
              style={{
                height: "36px",
                width: "100px",
                background: "var(--body__background)",
                borderRadius: "8px",
                cursor: "pointer",
                padding: "8px 14px",
                fontFamily: "Satoshi",
                fontWeight: 600,
                fontSize: "14px",
                outline: "none",
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                justifyContent: "center",
                gap: 10,
                border: "1px solid #6860EE",
                color: "#6860EE",
              }}
            >
              Clear All
            </button>
          </div>
          {Object.entries(columns).map(([key, name]) => (
            <div key={key} className="column-checkbox-item">
              <input
                type="checkbox"
                id={`${idPrefix}-${key}`}
                checked={draftColumns.includes(key)}
                onChange={() => onToggleColumn(key)}
              />
              <label htmlFor={`${idPrefix}-${key}`}>{toCapitalize(name)}</label>
            </div>
          ))}
          <div
            style={{
              marginTop: "5px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {hasSavedView ? (
              <Button label="Update saved view" event={onUpdateSaveView} />
            ) : (
              <Button label="Create saved view" event={onCreateSaveView} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const AdminDashboard = () => {
  const { StaticOptions = {} } = OptionsData || {};
  const { priority = [] } = StaticOptions || {};
  const {
    userDetails,
    tenantDetails,
    setTenantDetails,
    isDarkMode,
    notificationsState,
    setNotificationsState,
    AdminAccess,
    subscriptionDetails,
    SuperAdminAccess,
    dashboardHasAnimated,
    isVendorFeatureEnabled,
    isHiringTeamTenant,
  } = useContext(DynamicContext);
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    overview: null,
    jobPipeline: { jobs: [] },
    performance: { recruiters: [] },
    workload: { workload: [] },
    funnel: { funnel: [] },
  });
  const [overallCounts, setOverallCounts] = useState({
    candidates: null,
    clients: null,
    activeJobs: null,
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [isRecruitmentPerformanceLoading, setIsRecruitmentPerformanceLoading] =
    useState(true);
  const [selectedClient, setSelectedClient] = useState("All");
  const [selectedRecruiter, setSelectedRecruiter] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const [selectedDashboardDate, setSelectedDashboardDate] = useState("today");
  const [savedViews, setSavedViews] = useState([]);
  const [selectedSavedViewId, setSelectedSavedViewId] = useState(null);
  const [isLoadingSavedViews, setIsLoadingSavedViews] = useState(false);
  const hasAppliedDefaultViewRef = useRef(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [draftVisibleColumns, setDraftVisibleColumns] = useState([]);
  const [savedJobPipelineColumns, setSavedJobPipelineColumns] = useState([]);
  const [savedJobPipelineColumnViewId, setSavedJobPipelineColumnViewId] =
    useState(null);
  const dropdownRef = useRef(null);
  const dropdownBtnRef = useRef(null);
  const [recruiterDropdownOpen, setRecruiterDropdownOpen] = useState(false);
  const [visibleRecruiterColumns, setVisibleRecruiterColumns] = useState([]);
  const [draftVisibleRecruiterColumns, setDraftVisibleRecruiterColumns] =
    useState([]);
  const [
    savedRecruiterPerformanceColumns,
    setSavedRecruiterPerformanceColumns,
  ] = useState([]);
  const [
    savedRecruiterPerformanceColumnViewId,
    setSavedRecruiterPerformanceColumnViewId,
  ] = useState(null);
  const recruiterDropdownRef = useRef(null);
  const recruiterDropdownBtnRef = useRef(null);
  const [filteredClientOptions, setFilteredClientOptions] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [selectedClientFallbackOption, setSelectedClientFallbackOption] =
    useState(null);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [membersList, setMembersList] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false);
  const [showAddMemberBanner, setShowAddMemberBanner] = useState(false);
  const [showTrialSnackbar, setShowTrialSnackbar] = useState(false);
  const [daysLeftInTrial, setDaysLeftInTrial] = useState(0);
  const hasShownSnackbarRef = useRef(false);
  const [selectedPipelineJob, setSelectedPipelineJob] = useState(null);
  const [pipelineAssignedCandidateCount, setPipelineAssignedCandidateCount] =
    useState(0);
  const [originalPipelineJobData, setOriginalPipelineJobData] = useState(null);
  const [editedPipelineJobData, setEditedPipelineJobData] = useState(null);
  const [isEditingPipelineJob, setIsEditingPipelineJob] = useState(false);
  const [jobDetailsValidation, setJobDetailsValidation] = useState(null);
  const [openJobDeleteDialog, setOpenJobDeleteDialog] = useState(false);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const [isFetchingRecommendedCandidates, setIsFetchingRecommendedCandidates] =
    useState(false);
  const [recommendedCandidateIds, setRecommendedCandidateIds] = useState([]);
  const [isPipelineJobDetailsOpen, setIsPipelineJobDetailsOpen] =
    useState(false);
  const [pipelineJobDetailsActiveTab, setPipelineJobDetailsActiveTab] =
    useState("Job Details");
  const [recruiterPerformanceTotalPages, setRecruiterPerformanceTotalPages] =
    useState(0);
  const [recruiterPerformanceCurrentPage, setRecruiterPerformanceCurrentPage] =
    useState(1);
  const [isJobPipelineLoading, setIsJobPipelineLoading] = useState(true);
  const [jobPipelineTotalPages, setJobPipelineTotalPages] = useState(0);
  const [jobPipelineCurrentPage, setJobPipelineCurrentPage] = useState(1);
  const [jobPipelineSort, setJobPipelineSort] = useState(null);
  const [jobPipelineAPIRes, setJobPipelineAPIRes] = useState([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [savedViewName, setSavedViewName] = useState("");
  const [savedViewIsDefault, setSavedViewIsDefault] = useState(false);
  const [isCreatingSavedView, setIsCreatingSavedView] = useState(false);
  const [savedViewPendingDelete, setSavedViewPendingDelete] = useState(null);
  const [isSavedViewDeleteDialogOpen, setIsSavedViewDeleteDialogOpen] =
    useState(false);
  const [isDeletingSavedView, setIsDeletingSavedView] = useState(false);
  const [selectedJobPipelineColumn, setSelectedJobPipelineColumn] =
    useState("");
  const [recruiterPerformanceSort, setRecruiterPerformanceSort] =
    useState(null);
  const [
    selectedRecruiterPerformanceColumn,
    setSelectedRecruiterPerformanceColumn,
  ] = useState("");
  const skipJobPipelinePageChangeRef = useRef(false);
  const skipRecruiterPerformancePageChangeRef = useRef(false);
  const skipVendorPipelinePageChangeRef = useRef(false);
  const [isTeamWorkloadLoading, setIsTeamWorkloadLoading] = useState(true);
  const [isTeamWorkloadFetchingMore, setIsTeamWorkloadFetchingMore] =
    useState(false);
  const [teamWorkloadCurrentPage, setTeamWorkloadCurrentPage] = useState(0);
  const [teamWorkloadHasMore, setTeamWorkloadHasMore] = useState(true);
  const [isRecruitmentFunnelLoading, setIsRecruitmentFunnelLoading] =
    useState(true);
  const [isVendorPipelineLoading, setIsVendorPipelineLoading] = useState(true);
  const [vendorPipelineTotalPages, setVendorPipelineTotalPages] = useState(0);
  const [vendorPipelineCurrentPage, setVendorPipelineCurrentPage] = useState(1);
  const [vendorPipelineActions, setVendorPipelineActions] = useState([]);
  const [vendorPipelineActionsTotal, setVendorPipelineActionsTotal] =
    useState(0);
  const [vendorPipelineActionsLoading, setVendorPipelineActionsLoading] =
    useState(false);
  const [vendorPipelineActionVendor, setVendorPipelineActionVendor] =
    useState("");
  const [vendorPipelineSort, setVendorPipelineSort] = useState(null);
  const [vendorPipelineActionStatus, setVendorPipelineActionStatus] =
    useState("");
  const [showVendorDetails, setShowVendorDetails] = useState(false);
  const [selectedVendorDetails, setSelectedVendorDetails] = useState(null);
  const [selectedVendorPipelineColumn, setSelectedVendorPipelineColumn] =
    useState("");
  const [vendorDetailsLoading, setVendorDetailsLoading] = useState(false);
  const [vendorDetailsActiveTab, setVendorDetailsActiveTab] = useState("Info");
  const [isEditingVendor, setIsEditingVendor] = useState(false);
  const [isVendorDeleteDialogOpen, setIsVendorDeleteDialogOpen] =
    useState(false);
  const [editAchFile, setEditAchFile] = useState(null);
  const [editVoidChequeFile, setEditVoidChequeFile] = useState(null);
  const [editAuditDocument, setEditAuditDocument] = useState(null);

  const [showRecruiterDetails, setShowRecruiterDetails] = useState(false);
  const [selectedRecruiterDetails, setSelectedRecruiterDetails] =
    useState(null);

  const [isVendorCandidateModalOpen, setIsVendorCandidateModalOpen] =
    useState(false);
  const [selectedVendorCandidateForModal, setSelectedVendorCandidateForModal] =
    useState(null);
  const [vendorCandidateActiveTab, setVendorCandidateActiveTab] = useState(0);
  const [isEditingVendorCandidate, setIsEditingVendorCandidate] =
    useState(false);
  const [originalVendorCandidateData, setOriginalVendorCandidateData] =
    useState(null);
  const [editedVendorCandidateData, setEditedVendorCandidateData] =
    useState(null);
  const [vendorCandidateEmailError, setVendorCandidateEmailError] =
    useState("");
  const [vendorCandidateEmailTouched, setVendorCandidateEmailTouched] =
    useState(false);
  const [assignedVendor, setAssignedVendor] = useState(null);

  const cepheaVendorIds = [
    "e1245788277a47f285ac0374ce5fad34",
    "335e835f724947ee9eea9f2b6fea6504",
    "9a1d629dab1a46a7b4381db90451b543",
  ];
  const isCepheaVendor = (vendorId) => {
    return cepheaVendorIds.includes(vendorId);
  };

  const vendorFormik = useFormik({
    initialValues: {
      email: "",
      nameOfVendor: "",
      contactPersonName: "",
      contactDesignation: "",
      mobile: "",
      country: "",
      state: "",
      city: "",
      address: "",
      zipCode: "",
      remarks: "",
      vendorStatus: "",
      vendorPriority: "",
      federalTax: "",
      bankName: "",
      accountName: "",
      accountNumber: "",
      routingNumber: "",
      natureOfAccount: "",
      farcaCode: "",
      exemptPayeeCode: "",
    },
    validationSchema: Yup.object({
      nameOfVendor: Yup.string().trim().required("Vendor Name is required"),
      email: Yup.string()
        .email("Invalid email")
        .trim()
        .required("Email is required"),
      mobile: Yup.string()
        .trim()
        .required("Mobile is required")
        .matches(/^(\+)?[0-9\s\-\\(\\)]+$/, "Invalid phone number"),
      contactPersonName: Yup.string()
        .trim()
        .required("Contact Person Name is required"),
      address: Yup.string().trim(),
      city: Yup.string().trim(),
      state: Yup.string().trim(),
      zipCode: Yup.string()
        .trim()
        .matches(/^[0-9]*$/, "Only digits are allowed for Zipcode."),
      federalTax: Yup.string().trim(),
      bankName: Yup.string().trim(),
      accountName: Yup.string().trim(),
      accountNumber: Yup.string().trim(),
      routingNumber: Yup.string().trim(),
      natureOfAccount: Yup.string().trim(),
      farcaCode: Yup.string().trim(),
      exemptPayeeCode: Yup.string().trim(),
      vendorStatus: Yup.string().required("Status is required"),
      vendorPriority: Yup.string().required("Priority is required"),
    }),
  });

  useEffect(() => {
    if (selectedVendorDetails) {
      const initialValues = {
        email: selectedVendorDetails?.email || "",
        nameOfVendor: selectedVendorDetails?.name || "",
        contactPersonName: selectedVendorDetails?.contact_person || "",
        contactDesignation: selectedVendorDetails?.contact_designation || "",
        mobile: selectedVendorDetails?.phone || "",
        country: selectedVendorDetails?.country || "",
        state: selectedVendorDetails?.state || "",
        city: selectedVendorDetails?.city || "",
        address: selectedVendorDetails?.address || "",
        zipCode: selectedVendorDetails?.zip_code || "",
        remarks: selectedVendorDetails?.remarks || "",
        vendorStatus: selectedVendorDetails?.status || "",
        vendorPriority: selectedVendorDetails?.priority || "",
        federalTax: selectedVendorDetails?.federal_tax || "",
        bankName: selectedVendorDetails?.bank_name || "",
        accountName: selectedVendorDetails?.account_name || "",
        accountNumber: selectedVendorDetails?.account_number || "",
        routingNumber: selectedVendorDetails?.routing_number || "",
        natureOfAccount: selectedVendorDetails?.account_nature || "",
        farcaCode: selectedVendorDetails?.farca_reporting_code || "",
        exemptPayeeCode: selectedVendorDetails?.exempt_payee_code || "",
      };
      vendorFormik.setValues(initialValues);
      vendorFormik.setTouched({});
    }
  }, [selectedVendorDetails]);
  const [showCandidateDetails, setShowCandidateDetails] = useState(false);
  const [followUpsLoader, setFollowUpsLoader] = useState(true);
  const [followUps, setFollowUps] = useState({
    today: [],
    overdue: [],
    tomorrow: [],
  });
  const followUpsRequestRef = useRef(0);

  useEffect(
    () => () => {
      followUpsRequestRef.current += 1;
    },
    [],
  );
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentTimezone] = useState(() => moment.tz.guess());
  const [selectedInterviewCandidate, setSelectedInterviewCandidate] =
    useState(null);
  const [showInterviewDetails, setShowInterviewDetails] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState(true);
  const [scheduleError, setScheduleError] = useState("");
  const [scheduleRefresh, setScheduleRefresh] = useState(0);
  const [loadingInterviewId, setLoadingInterviewId] = useState(null);
  const interviewRequestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const fetchTodaysInterviews = async () => {
      setIsScheduleLoading(true);
      setScheduleError("");
      try {
        const today = moment().tz(currentTimezone).format("YYYY-MM-DD");
        const response = await get(
          VYLABS_BASE_URL,
          GET_INTERVIEW_BY_TIME_RANGE,
          {
            params: {
              start_date: today,
              end_date: today,
              limit: 30,
              offset: 0,
            },
          },
        );
        const interviews = Array.isArray(response?.interviews)
          ? response.interviews
          : [];
        const events = interviews.map((interview) => ({
          ...interview,
          start: convertUTCTimeToUserTimezone(
            interview.date,
            interview.time_start,
            currentTimezone,
          ),
          end: convertUTCTimeToUserTimezone(
            interview.date,
            interview.time_end,
            currentTimezone,
          ),
          interviewer_name: formatInterviewerName(interview.interviewer_name),
        }));
        if (!cancelled) setCalendarEvents(events);
      } catch (error) {
        if (!cancelled) {
          setCalendarEvents([]);
          setScheduleError(
            "Unable to load today's interviews. Please try again.",
          );
        }
      } finally {
        if (!cancelled) setIsScheduleLoading(false);
      }
    };
    fetchTodaysInterviews();
    return () => {
      cancelled = true;
      interviewRequestRef.current += 1;
    };
  }, [currentTimezone, scheduleRefresh]);

  const openInterviewDetails = async (event) => {
    const requestId = ++interviewRequestRef.current;
    setLoadingInterviewId(event.id);
    try {
      const response = await get(VYLABS_BASE_URL, GET_INTERVIEWS_BY_ID, {
        params: { id: event.id },
      });
      if (requestId !== interviewRequestRef.current) return;
      const { candidate, job } = response || {};
      if (!candidate)
        throw new Error("Interview candidate details unavailable");
      setSelectedInterviewCandidate({
        name: candidate.name || event.candidate_name || "Unknown Candidate",
        email: candidate.email || "N/A",
        contactNo: candidate.phone || "N/A",
        position: job?.title || "N/A",
        interviewPerson: event.interviewer_name || "N/A",
        date: moment(event.start).tz(currentTimezone).format("MMMM D, YYYY"),
        startTime: moment(event.start).tz(currentTimezone).format("h:mm A"),
        endTime: moment(event.end).tz(currentTimezone).format("h:mm A"),
        meetingLink: event.meeting_link || "",
      });
      setShowInterviewDetails(true);
    } catch (error) {
      if (requestId === interviewRequestRef.current) {
        Toast("error", "Unable to load interview details. Please try again.");
      }
    } finally {
      if (requestId === interviewRequestRef.current)
        setLoadingInterviewId(null);
    }
  };

  const EventComponent = ({ event }) => (
    <Button
      type="button"
      className="interview-event-container"
      event={() => openInterviewDetails(event)}
      disabled={loadingInterviewId !== null}
      ariaLabel={`View interview for ${event.candidate_name || "Unknown Candidate"}`}
      backgroundColor="var(--body__background)"
      color="var(--header__color)"
      hoverd={false}
    >
      <div className="event-candidate">
        <User size={14} />
        <span>{event.candidate_name || "Unknown Candidate"}</span>
      </div>
      <div className="event-time-view">
        <div className="event-time">
          <img src={meetingTimeIcon} alt="" />
          {moment(event.start).tz(currentTimezone).format("h:mm A")} -{" "}
          {moment(event.end).tz(currentTimezone).format("h:mm A")}
        </div>
        <div className="view-meeting">
          <span>{loadingInterviewId === event.id ? "Loading..." : "View"}</span>
          <img src={viewMeetingIcon} alt="" />
        </div>
      </div>
    </Button>
  );
  const [storeApplicationsByJob, setStoreApplicationsByJob] = useState();
  const [storeJobTitle, setStoreJobTitle] = useState();
  const [storeJobTitleForTable, setStoreJobTitleForTable] = useState();
  const [isApplicationCandidatePopupOpen, setIsApplicationCandidatePopupOpen] =
    useState(false);
  const [selectedApplicationCandidate, setSelectedApplicationCandidate] =
    useState(null);
  const [applicationCandidateLoading, setApplicationCandidateLoading] =
    useState(false);
  const [isEditingApplicationCandidate, setIsEditingApplicationCandidate] =
    useState(false);
  const [
    originalApplicationCandidateData,
    setOriginalApplicationCandidateData,
  ] = useState(null);
  const [editedApplicationCandidateData, setEditedApplicationCandidateData] =
    useState(null);
  const [applicationCandidateEmailError, setApplicationCandidateEmailError] =
    useState("");
  const [
    applicationCandidateEmailTouched,
    setApplicationCandidateEmailTouched,
  ] = useState(false);
  const [
    isDeleteApplicationCandidatePopupOpen,
    setIsDeleteApplicationCandidatePopupOpen,
  ] = useState(false);
  const [jobPipelineJobId, setJobPipelineJobId] = useState();
  const [jobPipelineStage, setJobPipelineStage] = useState();
  const [jobPipelineStageName, setJobPipelingStageName] = useState();
  // "assigned" | "tracked" | null - used only when the recruiter's jobs list is opened
  const [recruiterJobsType, setRecruiterJobsType] = useState(null);
  const [applicationSource, setApplicationSource] = useState(null);
  const [recruiterApplicationsJobs, setRecruiterApplicationsJobs] = useState();
  const [
    isRecruiterApplicationsJobsClicked,
    setIsRecruiterApplicationsJobsClicked,
  ] = useState(false);
  const [
    jobAndRecruiterApplicationsTotal,
    setJobAndRecruiterApplicationsTotal,
  ] = useState(0);
  const [
    jobAndRecruiterApplicationsCurrentPage,
    setJobAndRecruiterApplicationsCurrentPage,
  ] = useState(1);
  const [
    jobAndRecruiterApplicationsLoading,
    setJobAndRecruiterApplicationsLoading,
  ] = useState(true);
  const [getApplicationsByJobCalled, setGetApplicationsByJobCalled] =
    useState(false);
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [renderAdmin, setRenderAdmin] = useState(true);
  const [callStageMap, setCallStageMap] = useState({});

  useEffect(() => {
    const loadCallStages = async () => {
      const stages = await fetchCallStages();
      setCallStageMap(stages);
    };

    loadCallStages();
  }, []);

  const notificationSocketRef = useRef(null);
  const applicationCandidateDetailsRef = useRef(null);
  const notificationPingIntervalRef = useRef(null);
  const notificationPingTimeoutRef = useRef(null);
  const notificationConnectTimeoutRef = useRef(null);
  const notificationReconnectTimeoutRef = useRef(null);
  const notificationReconnectAttemptsRef = useRef(0);
  const notificationConnectingRef = useRef(false);
  const notificationUnmountedRef = useRef(false);
  const isMountedRef = useRef(true);
  const teamWorkloadScrollRef = useRef(null);
  const teamWorkloadPayloadRef = useRef({});
  const teamWorkloadRequestInFlightRef = useRef(false);
  const previousDashboardDateRef = useRef("today");
  const customFilterAppliedRef = useRef(false);
  const dashboardDateToolbarRef = useRef(null);
  const [customDatePopupStyle, setCustomDatePopupStyle] = useState();

  const notificationUnreadCount = notificationsState?.unread_count || 0;
  const NOTIFICATION_PING_INTERVAL = 30000;
  const NOTIFICATION_PING_TIMEOUT = 10000;
  const MAX_NOTIFICATION_RECONNECT_ATTEMPTS = 5;
  const BASE_NOTIFICATION_RECONNECT_DELAY = 1000;
  const RECRUITER_PERFORMANCE_LIMIT = 5;
  const JOB_PIPELINE_LIMIT = 5;
  const RECRUITMENT_FUNNEL_LIMIT = 30;
  const VENDOR_PIPELINE_METRICS_LIMIT = 5;
  const TEAM_WORKLOAD_LIMIT = 10;
  const JOB_AND_RECRUITER_APPLICATIONS_LIMIT = 10;
  const firstName = userDetails?.first_name || "User";
  const tenantName =
    tenantDetails?.name ||
    tenantDetails?.company_name ||
    tenantDetails?.tenant_name ||
    "Vy Systems";

  const formik = useFormik({
    initialValues: {
      title: "",
      client_id: "",
      minYearsExperience: "",
      maxYearsExperience: "",
      educationLevel: "",
      company: "",
      jobLocation: "",
      jobType: "",
      skills: [],
      jobDescription: "",
      jobRole: "",
      salary_min: "",
      salary_max: "",
      tags: [],
      languageProficiency: "",
      openings: "",
      jobPriority: "",
      assignedTo: [],
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Job Title is required"),
      jobRole: Yup.string().required("Job Role is required"),
      jobType: Yup.string().required("Job Type is required"),
      jobLocation: Yup.string().required("Job Location is required"),
      minYearsExperience: Yup.number()
        .typeError("Minimum years of experience must be a number")
        .integer("Years of experience must be an integer")
        .min(0, "Experience cannot be negative")
        .nullable()
        .required("Min years of exp is required"),
      maxYearsExperience: Yup.number()
        .typeError("Maximum years of experience must be a number")
        .integer("Years of experience must be an integer")
        .min(0, "Experience cannot be negative")
        .min(
          Yup.ref("minYearsExperience"),
          "Maximum years of experience cannot be less than minimum years of experience",
        )
        .nullable()
        .required("Max years of exp is required")
        .max(99, "Please enter a two digit number"),
      salary_min: Yup.number(),
      salary_max: Yup.number(),
      jobDescription: Yup.string().nullable(),
      jobPriority: Yup.string().nullable(),
      assignedTo: Yup.array().nullable(),
      skills: Yup.mixed()
        .test(
          "is-array-or-string",
          "Skills must be an array or string",
          function (value) {
            return Array.isArray(value) || typeof value === "string";
          },
        )
        .required("Skills are required"),
      tags: Yup.array().nullable(),
      openings: Yup.number()
        .required("Number of openings is required")
        .integer("Number of openings must be an integer")
        .min(1, "Number of openings must be at least 1")
        .typeError("Number of openings must be a number"),
    }),
  });

  const formikRef = useRef(formik);
  formikRef.current = formik;

  const toggleNotificationPopup = (event) => {
    event.stopPropagation();
    setIsNotificationPopupOpen(!isNotificationPopupOpen);
  };

  const fetchTenantDetails = useCallback(async () => {
    if (!userDetails?.tenant_id) return;

    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_CORE_API_KEY,
        },
      };
      const response = await get(
        VYLABS_BASE_URL,
        GET_TENANT_DETAILS,
        requestConfig,
      );

      setTenantDetails(response?.tenant || null);
      localStorage.setItem("tenant", JSON.stringify(response));
    } catch (err) {
      console.error("Admin dashboard GET_TENANT_DETAILS Error:", err);
    }
  }, [setTenantDetails, userDetails?.tenant_id]);

  const fetchMembers = useCallback(async () => {
    if (!AdminAccess) {
      setShowAddMemberBanner(false);
      return;
    }

    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_CORE_API_KEY,
        },
      };
      const response = await get(
        VYLABS_BASE_URL,
        `${LIST_MEMEBERS_OF_TENANT}?tenant_id=${userDetails?.tenant_id}&offset=0&limit=50`,
        requestConfig,
      );
      if (response?.users && Array.isArray(response.users)) {
        const hasAdmin = response.users.some(
          (user) => user?.user?.role === "ADMIN",
        );
        const hasMember = response.users.some(
          (user) => user?.user?.role === "MEMBER",
        );
        setShowAddMemberBanner(hasAdmin && !hasMember);
      } else {
        setShowAddMemberBanner(false);
      }
    } catch (err) {
      console.error(
        "Admin dashboard LIST_MEMEBERS_OF_TENANT Error fetching members:",
        err,
      );
      setShowAddMemberBanner(false);
    }
  }, [AdminAccess, userDetails?.tenant_id]);

  const calculateDaysLeft = () => {
    if (
      subscriptionDetails?.status === "trialing" &&
      subscriptionDetails?.trial_end
    ) {
      const trialEnd = new Date(subscriptionDetails.trial_end);
      const now = new Date();
      const timeDiff = trialEnd.getTime() - now.getTime();
      const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
      return Math.max(0, daysLeft);
    }
    return 0;
  };

  const handleCloseSnackbar = () => {
    setShowTrialSnackbar(false);
  };

  const handleAddMemberNavigate = () => {
    navigate("/settings/users", { state: { openAddMemberDialog: true } });
  };

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    if (
      subscriptionDetails?.status === "trialing" &&
      !hasShownSnackbarRef.current
    ) {
      const daysLeft = calculateDaysLeft();
      setDaysLeftInTrial(daysLeft);

      if (daysLeft > 0) {
        setShowTrialSnackbar(true);
        hasShownSnackbarRef.current = true;
      }
    }
  }, [subscriptionDetails]);

  const selectedRecruiterRef = useRef(selectedRecruiter);
  useEffect(() => {
    selectedRecruiterRef.current = selectedRecruiter;
  }, [selectedRecruiter]);

  const memberSearchDebounceRef = useRef(null);
  const memberSearchRequestIdRef = useRef(0);
  const vendorSearchRequestIdRef = useRef(0);

  const formatMembers = useCallback((response) => {
    const rawList = Array.isArray(response?.users) ? response.users : [];

    return rawList
      .filter((item) => item?.user || item?.id)
      .map((item) => {
        const u = item?.user || item;
        const name =
          [u?.first_name, u?.last_name].filter(Boolean).join(" ") || "N/A";
        return {
          ...u,
          label: name,
          value: u?.id,
          user: item?.user ? item : { user: u },
        };
      });
  }, []);

  const getProtectedMemberIds = useCallback(
    () => [
      ...(selectedRecruiterRef.current || []),
      ...(formikRef.current?.values?.assignedTo || []),
    ],
    [],
  );

  const fetchAllMembers = useCallback(
    async (filterValue = "") => {
      if (!userDetails?.tenant_id) {
        setMembersList([]);
        return [];
      }

      const trimmedFilter =
        typeof filterValue === "string" ? filterValue.trim() : "";
      const requestId = ++memberSearchRequestIdRef.current;

      try {
        setIsLoadingMembers(true);
        const query = trimmedFilter
          ? `${LIST_MEMEBERS_OF_TENANT}?tenant_id=${userDetails.tenant_id}&filter=${encodeURIComponent(
              trimmedFilter,
            )}&offset=0&limit=30`
          : `${LIST_MEMEBERS_OF_TENANT}?tenant_id=${userDetails.tenant_id}&sort_by=name&offset=0&limit=30`;

        const requestConfig = {
          headers: {
            "X-API-Key": TENANT_CORE_API_KEY,
          },
        };

        const response = await get(VYLABS_BASE_URL, query, requestConfig);

        if (requestId !== memberSearchRequestIdRef.current) {
          return [];
        }

        const formattedMembers = formatMembers(response);

        setMembersList((currentMembers) => {
          const selected = getProtectedMemberIds();
          if (!selected.length) {
            return formattedMembers;
          }

          const existingSelectedMembers = currentMembers.filter(
            (m) =>
              m?.value &&
              selected.some((sId) => String(sId) === String(m.value)),
          );

          const missingSelected = existingSelectedMembers.filter(
            (sm) =>
              !formattedMembers.some(
                (fm) => String(fm.value) === String(sm.value),
              ),
          );

          return [...missingSelected, ...formattedMembers];
        });

        return formattedMembers;
      } catch (err) {
        console.error("Admin dashboard Error fetching all members:", err);
        if (requestId === memberSearchRequestIdRef.current) {
          setMembersList((currentMembers) => {
            const selected = getProtectedMemberIds();
            if (!selected.length) return [];
            return currentMembers.filter(
              (m) =>
                m?.value &&
                selected.some((sId) => String(sId) === String(m.value)),
            );
          });
        }
        return [];
      } finally {
        if (requestId === memberSearchRequestIdRef.current) {
          setIsLoadingMembers(false);
        }
      }
    },
    [formatMembers, getProtectedMemberIds, userDetails?.tenant_id],
  );

  const handleDashboardRecruiterSearch = useCallback(
    (searchTerm) => {
      const query = (searchTerm || "").trim();

      if (memberSearchDebounceRef.current) {
        clearTimeout(memberSearchDebounceRef.current);
        memberSearchDebounceRef.current = null;
      }

      if (!query) {
        fetchAllMembers("");
        return;
      }

      memberSearchDebounceRef.current = setTimeout(() => {
        fetchAllMembers(query);
      }, 300);
    },
    [fetchAllMembers],
  );

  useEffect(() => {
    return () => {
      if (memberSearchDebounceRef.current) {
        clearTimeout(memberSearchDebounceRef.current);
      }
    };
  }, []);

  const formatVendorOptions = useCallback((response) => {
    const rawVendors = Array.isArray(response?.vendors) ? response.vendors : [];

    return rawVendors
      .filter((vendor) => vendor?.id && vendor?.name)
      .map((vendor) => ({
        label: `${vendor.name} - ${vendor.contact_person || "N/A"}`,
        value: vendor.id,
        vendor,
      }));
  }, []);

  const fetchVendors = useCallback(
    async (searchValue = "") => {
      const requestId = ++vendorSearchRequestIdRef.current;
      const query = typeof searchValue === "string" ? searchValue.trim() : "";

      try {
        setIsLoadingVendors(true);

        const response = query
          ? await get(
              VYLABS_BASE_URL,
              `${VENDORS_SEARCH}?vendor_name=${encodeURIComponent(query)}`,
            )
          : await post(VYLABS_BASE_URL, GET_VENDORS_LIST, {
              limit: 10,
              offset: 0,
            });

        if (requestId !== vendorSearchRequestIdRef.current) return [];

        const options = formatVendorOptions(response);
        setVendorList(options);
        return options;
      } catch (error) {
        console.error("Admin dashboard vendor search error:", error);

        if (requestId === vendorSearchRequestIdRef.current) {
          setVendorList([]);
        }

        return [];
      } finally {
        if (requestId === vendorSearchRequestIdRef.current) {
          setIsLoadingVendors(false);
        }
      }
    },
    [formatVendorOptions],
  );

  const handleDashboardVendorOpen = useCallback(async () => {
    if (vendorList.length) return;

    await fetchVendors("");
  }, [fetchVendors, vendorList.length]);

  const handleDashboardVendorSearch = useCallback(
    async (_, value, reason) => {
      const searchValue = value?.trim() || "";

      // Search on every entered letter: a -> ap -> app -> ...
      // When the user clicks X, restore the initial vendor list.
      if (reason === "input" || reason === "clear") {
        await fetchVendors(searchValue);
      }
    },
    [fetchVendors],
  );

  const handleDashboardVendorFilterChange = useCallback((vendor) => {
    setSelectedVendor(vendor || null);
  }, []);

  const fetchAllClientsList = useCallback(async (inputValue = "") => {
    try {
      setIsLoadingClients(true);
      let response;

      if (inputValue) {
        response = await get(
          VYLABS_BASE_URL,
          `${SEARCH_CLIENTS}?company_name=${encodeURIComponent(inputValue)}`,
        );
      } else {
        response = await get(VYLABS_BASE_URL, `${GET_CLIENTS_LIST}`);
      }

      const { clients = [] } = response || {};
      const fetchedClients = Array.isArray(clients)
        ? clients
            .map(formatClientOption)
            .filter((client) => client?.label && client?.value)
        : [];

      if (!inputValue) {
        setClientList(fetchedClients);
      }

      return fetchedClients;
    } catch (err) {
      console.error("Admin dashboard fetchAllClientsList error", err);
      if (!inputValue) {
        setClientList([]);
      }
      return [];
    } finally {
      setIsLoadingClients(false);
    }
  }, []);

  const fetchClientOptionById = useCallback(async (clientId) => {
    if (!isValidDashboardFilterValue(clientId)) return null;

    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${GET_CLIENT_DETAILS}?id=${encodeURIComponent(clientId)}`,
      );
      const client = response?.client || response?.data || response;
      const option = formatClientOption(client);

      return option?.label && option?.value ? option : null;
    } catch (err) {
      console.error("Admin dashboard fetchClientOptionById error", err);
      return null;
    }
  }, []);

  const closeNotificationWebSocket = useCallback(() => {
    if (notificationSocketRef.current) {
      notificationSocketRef.current.close(
        1000,
        "Admin dashboard notification cleanup",
      );
      notificationSocketRef.current = null;
    }
    if (notificationPingIntervalRef.current) {
      clearInterval(notificationPingIntervalRef.current);
      notificationPingIntervalRef.current = null;
    }
    if (notificationPingTimeoutRef.current) {
      clearTimeout(notificationPingTimeoutRef.current);
      notificationPingTimeoutRef.current = null;
    }
    if (notificationConnectTimeoutRef.current) {
      clearTimeout(notificationConnectTimeoutRef.current);
      notificationConnectTimeoutRef.current = null;
    }
    if (notificationReconnectTimeoutRef.current) {
      clearTimeout(notificationReconnectTimeoutRef.current);
      notificationReconnectTimeoutRef.current = null;
    }
    notificationConnectingRef.current = false;
  }, []);

  const startNotificationPingInterval = useCallback((ws) => {
    if (notificationPingIntervalRef.current) {
      clearInterval(notificationPingIntervalRef.current);
    }

    const sendPing = () => {
      if (ws.readyState !== WebSocket.OPEN) return;
      console.warn("Sending ping");
      ws.send("ping");
      if (notificationPingTimeoutRef.current) {
        clearTimeout(notificationPingTimeoutRef.current);
      }
      notificationPingTimeoutRef.current = setTimeout(() => {
        ws.close(1000, "Notification ping timeout");
      }, NOTIFICATION_PING_TIMEOUT);
    };

    if (ws.readyState === WebSocket.OPEN) {
      console.warn("Sending initial ping");
    }
    sendPing();
    notificationPingIntervalRef.current = setInterval(
      sendPing,
      NOTIFICATION_PING_INTERVAL,
    );
  }, []);

  const updateNotificationStateFromPayload = useCallback(
    (notificationPayload) => {
      if (!notificationPayload?.id) return;

      setNotificationsState((prev) => {
        const previousNotifications = Array.isArray(prev?.notifications)
          ? prev.notifications
          : [];
        const alreadyExists = previousNotifications.some(
          (item) => item.id === notificationPayload.id,
        );
        const notifications = alreadyExists
          ? previousNotifications.map((item) =>
              item.id === notificationPayload.id
                ? { ...item, ...notificationPayload }
                : item,
            )
          : [notificationPayload, ...previousNotifications];

        return {
          ...prev,
          notifications,
          unread_count:
            typeof notificationPayload.unread_count === "number"
              ? notificationPayload.unread_count
              : (prev?.unread_count || 0) + (alreadyExists ? 0 : 1),
        };
      });
    },
    [setNotificationsState],
  );

  const initializeNotificationWebSocket = useCallback(() => {
    const token = localStorage.getItem("token");
    const isPublicRoute = [
      "/",
      "/login",
      "/signup",
      "/admin-signup",
      "/member-signup",
      "/forgot-password",
      "/reset-password",
    ].includes(window.location.pathname);
    if (
      !token ||
      !userDetails?.id ||
      isPublicRoute ||
      notificationConnectingRef.current
    ) {
      return;
    }
    if (
      notificationSocketRef.current &&
      notificationSocketRef.current.readyState === WebSocket.OPEN
    ) {
      return;
    }

    notificationConnectingRef.current = true;
    if (notificationSocketRef.current) {
      notificationSocketRef.current.close();
    }
    if (notificationPingIntervalRef.current) {
      clearInterval(notificationPingIntervalRef.current);
      notificationPingIntervalRef.current = null;
    }
    if (notificationPingTimeoutRef.current) {
      clearTimeout(notificationPingTimeoutRef.current);
      notificationPingTimeoutRef.current = null;
    }
    if (notificationConnectTimeoutRef.current) {
      clearTimeout(notificationConnectTimeoutRef.current);
      notificationConnectTimeoutRef.current = null;
    }
    if (notificationReconnectTimeoutRef.current) {
      clearTimeout(notificationReconnectTimeoutRef.current);
      notificationReconnectTimeoutRef.current = null;
    }

    const wsProtocol =
      window.location.protocol === "https:" ? "wss://" : "wss://";
    const baseHost = String(WEB_SOCKET_URL)
      .replace(/^wss?:\/\//, "")
      .replace(/^https?:\/\//, "")
      .replace(/^\/+/, "");
    const wsUrl = `${wsProtocol}${baseHost}/ws?token=${encodeURIComponent(
      "Bearer " + token,
    )}&channel_id=${encodeURIComponent(userDetails.id)}`;

    try {
      console.log("Creating new WebSocket connection...");
      const ws = new WebSocket(wsUrl);
      notificationSocketRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connection established");
        notificationReconnectAttemptsRef.current = 0;
        notificationConnectingRef.current = false;
        startNotificationPingInterval(ws);
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") {
          console.warn("Pong received");
          if (notificationPingTimeoutRef.current) {
            clearTimeout(notificationPingTimeoutRef.current);
            notificationPingTimeoutRef.current = null;
          }
          return;
        }

        try {
          console.log("Raw WebSocket message:", event.data);
          const cleanedMessage = event.data.trim();
          const messageParts = cleanedMessage.split(/\|(.+)/, 2);
          if (messageParts.length !== 2) return;

          const topic = messageParts[0];
          const baseTopic = topic.split(":")[0];
          if (baseTopic !== "notification") {
            console.log("Ignored WebSocket topic:", baseTopic);
            return;
          }

          const notificationPayload = JSON.parse(messageParts[1]);
          console.log("Received notification:", notificationPayload);
          updateNotificationStateFromPayload(notificationPayload);
        } catch (error) {
          console.error(
            "Error processing notification WebSocket message:",
            error,
          );
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket connection closed", event);
        notificationConnectingRef.current = false;
        if (notificationPingIntervalRef.current) {
          clearInterval(notificationPingIntervalRef.current);
          notificationPingIntervalRef.current = null;
        }
        if (notificationPingTimeoutRef.current) {
          clearTimeout(notificationPingTimeoutRef.current);
          notificationPingTimeoutRef.current = null;
        }

        if (
          !notificationUnmountedRef.current &&
          event.code !== 1000 &&
          notificationReconnectAttemptsRef.current <
            MAX_NOTIFICATION_RECONNECT_ATTEMPTS
        ) {
          const delay = Math.min(
            BASE_NOTIFICATION_RECONNECT_DELAY *
              Math.pow(2, notificationReconnectAttemptsRef.current),
            30000,
          );
          notificationReconnectTimeoutRef.current = setTimeout(() => {
            notificationReconnectAttemptsRef.current += 1;
            initializeNotificationWebSocket();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.warn("Notification WebSocket error:", error);
        notificationConnectingRef.current = false;
      };
    } catch (error) {
      console.error("Error creating notification WebSocket:", error);
      notificationConnectingRef.current = false;
    }
  }, [
    startNotificationPingInterval,
    updateNotificationStateFromPayload,
    userDetails?.id,
  ]);

  const dashboardCards = useMemo(
    () => [
      {
        title: "Active Jobs",
        value: getFirstValue(dashboardData.overview, [
          "active_jobs",
          "activeJobs",
          "active_jobs_count",
          "activeJobsCount",
          "active",
        ]),
        trend: buildMetricTrend({
          noteValue: getFirstValue(
            dashboardData.overview,
            ["in_progress", "inProgress", "jobs_in_progress", "jobsInProgress"],
            null,
          ),
          noteSuffix: "in progress",
        }),
      },
      {
        title: "Open Positions",
        value: getFirstValue(dashboardData.overview, [
          "open_positions",
          "openPositions",
          "open_positions_count",
          "openPositionsCount",
        ]),
        trend: buildMetricTrend({
          notePrefix: "Across",
          noteValue: getFirstValue(
            dashboardData.overview,
            ["total_jobs", "totalJobs", "jobs_count", "jobsCount"],
            null,
          ),
          noteSuffix: "jobs",
        }),
      },
      {
        title: "Offers Pending",
        value: getFirstValue(dashboardData.overview, [
          "offers_pending",
          "offersPending",
          "offeredCandidates",
          "offered_candidates",
          "offered_candidates_count",
          "offeredCandidatesCount",
          "pending_offers",
          "pendingOffers",
        ]),
        trend: buildMetricTrend({
          noteValue: getFirstValue(
            dashboardData.overview,
            [
              "expiring_today",
              "expiringToday",
              "offers_expiring_today",
              "offersExpiringToday",
            ],
            null,
          ),
          noteSuffix: "expiring today",
        }),
      },
      {
        title: "Positions Filled",
        value: getFirstValue(dashboardData.overview, [
          "positions_filled",
          "positionsFilled",
          "filled_positions",
          "filledPositions",
        ]),
        trend: buildMetricTrend({
          noteValue: getFirstValue(
            dashboardData.overview,
            [
              "positions_remaining",
              "positionsRemaining",
              "remaining_positions",
              "remainingPositions",
            ],
            null,
          ),
          noteSuffix: "remaining",
        }),
      },
      {
        title: "Recruiters Working",
        value: getFirstValue(dashboardData.overview, [
          "recruiters_working",
          "recruitersWorking",
          "active_recruiters",
          "activeRecruiters",
        ]),
        trend: buildMetricTrend({
          noteValue: getFirstValue(
            dashboardData.overview,
            [
              "recruiters_active_now",
              "recruitersActiveNow",
              "active_now",
              "activeNow",
            ],
            null,
          ),
          noteSuffix: "active now",
        }),
      },
      ...(isVendorFeatureEnabled
        ? [
            {
              title: "Vendor Tracks",
              value: getFirstValue(dashboardData.overview, ["vendorTracks"]),
              trend: buildMetricTrend({
                noteValue: null,
                noteSuffix: "active vendor tracks",
              }),
            },
          ]
        : []),
    ],
    [dashboardData.overview, overallCounts, isVendorFeatureEnabled],
  );

  const metrics = React.useMemo(() => {
    return [
      {
        title: "Total Candidates",
        value: overallCounts.candidates ?? 0,
        trend: "Candidates available in the system",
        headerIcon: isDarkMode
          ? activeCandidatesDarkIcon
          : activeCandidatesIcon,
        iconBgClass: "icon-bg-candidates",
        cardBackgroundImage: undefined,
        animate: dashboardHasAnimated,
      },
      {
        title: "Total Active Jobs",
        value: overallCounts.activeJobs ?? 0,
        trend: "Currently open job listings",
        headerIcon: isDarkMode ? activeJobsDarkIcon : activeJobsIcon,
        iconBgClass: "icon-bg-jobs",
        cardBackgroundImage: undefined,
        animate: dashboardHasAnimated,
      },
      {
        title: isHiringTeamTenant ? "Total Hiring Team" : "Total Clients",
        value: overallCounts.clients ?? 0,
        trend: isHiringTeamTenant
          ? "Active Hiring Team organizations"
          : "Active client organizations",
        headerIcon: isDarkMode ? totalClientDarkIcon : totalClientLightIcon,
        iconBgClass: "icon-bg-clients",
        cardBackgroundImage: undefined,
        animate: dashboardHasAnimated,
      },
    ];
  }, [overallCounts, isDarkMode, dashboardHasAnimated]);

  const jobs = useMemo(
    () => buildJobPipeline(dashboardData.jobPipeline),
    [dashboardData.jobPipeline],
  );
  const recruiterPerformance = useMemo(
    () => buildRecruiterPerformance(dashboardData.performance),
    [dashboardData.performance],
  );
  const vendorsFromBuild = useMemo(
    () => buildVendorPipeline(dashboardData.vendorPipeline),
    [dashboardData.vendorPipeline],
  );
  const teamWorkload = useMemo(
    () => buildTeamWorkload(dashboardData.workload),
    [dashboardData.workload],
  );
  const recruitmentFunnel = useMemo(
    () => buildRecruitmentFunnel(dashboardData.funnel, callStageMap),
    [dashboardData.funnel, callStageMap],
  );
  const clientOptions = useMemo(
    () => clientList.filter((client) => client?.label && client?.value),
    [clientList],
  );
  const selectedClientOption = useMemo(() => {
    if (!isValidDashboardFilterValue(selectedClient)) return null;

    return (
      filteredClientOptions.find(
        (client) => String(client.value) === String(selectedClient),
      ) ||
      clientOptions.find(
        (client) => String(client.value) === String(selectedClient),
      ) ||
      (String(selectedClientFallbackOption?.value) === String(selectedClient)
        ? selectedClientFallbackOption
        : null) ||
      null
    );
  }, [
    clientOptions,
    filteredClientOptions,
    selectedClient,
    selectedClientFallbackOption,
  ]);
  const recruiterMultiSelectOptions = useMemo(
    () => membersList.filter((member) => member?.label && member?.value),
    [membersList],
  );
  const handleDashboardDateFilterChange = useCallback(
    (dateFilter) => {
      if (dateFilter === "custom") {
        previousDashboardDateRef.current = selectedDashboardDate;
        setSelectedDashboardDate("custom");
        setIsFilterPopupOpen(true);

        return;
      }
      setSelectedDashboardDate(dateFilter);
    },
    [selectedDashboardDate],
  );

  const updateCustomDatePopupPosition = useCallback(() => {
    const toolbar = dashboardDateToolbarRef.current;
    if (!toolbar) return;

    const customButton =
      toolbar.querySelector(".month-filter__option--active") ||
      toolbar.querySelector(".month-filter__option:last-child");

    if (!customButton) return;

    const rect = customButton.getBoundingClientRect();
    const popupWidth = Math.min(520, window.innerWidth - 32);
    const left = Math.min(
      Math.max(16, rect.left + rect.width / 2 - popupWidth / 2),
      window.innerWidth - popupWidth - 16,
    );

    setCustomDatePopupStyle({
      top: `${rect.bottom + 10}px`,
      left: `${left}px`,
      right: "auto",
      width: `${popupWidth}px`,
      maxWidth: "calc(100vw - 32px)",
    });
  }, []);

  const handleDashboardClientFilterChange = useCallback((client) => {
    setSelectedClient(client?.value || "All");
    setSelectedClientFallbackOption(client || null);
  }, []);

  const handleDashboardClientSearch = useCallback(
    async (_, value, reason) => {
      if (reason === "blur" || reason === "reset") return;

      const searchValue = value?.trim() || "";

      if (reason === "input" && selectedClient !== "All") {
        setSelectedClient("All");
        setSelectedClientFallbackOption(null);
      }

      const fetchedClients = await fetchAllClientsList(searchValue);

      setFilteredClientOptions(fetchedClients);
    },
    [fetchAllClientsList, selectedClient],
  );

  const handleDashboardClientOpen = useCallback(async () => {
    if (clientList.length) {
      setFilteredClientOptions(clientOptions);
      return;
    }

    const fetchedClients = await fetchAllClientsList();
    setFilteredClientOptions(fetchedClients);
  }, [clientList.length, clientOptions, fetchAllClientsList]);

  useEffect(() => {
    let isActive = true;

    const resolveSelectedClientOption = async () => {
      if (
        !isValidDashboardFilterValue(selectedClient) ||
        selectedClientOption
      ) {
        return;
      }

      const option = await fetchClientOptionById(selectedClient);
      if (!isActive || !option) return;

      setSelectedClientFallbackOption(option);
    };

    resolveSelectedClientOption();

    return () => {
      isActive = false;
    };
  }, [fetchClientOptionById, selectedClient, selectedClientOption]);

  const handleDashboardRecruiterFilterChange = useCallback((value) => {
    setSelectedRecruiter(normalizeRecruiterIds(value));
  }, []);

  const handlePipelineJobAssignedToChange = useCallback((value) => {
    const ids = Array.isArray(value)
      ? value.filter(
          (v) => v !== undefined && v !== null && v !== "" && v !== "All",
        )
      : [];
    formikRef.current.setFieldValue("assignedTo", ids);
  }, []);

  const setPipelineJobFormValues = useCallback(
    (job = {}) => {
      formik.setValues({
        title: job.title || "",
        client_id: job.client_id || "",
        minYearsExperience: job.min_years_experience?.toString() || "",
        maxYearsExperience: job.max_years_experience?.toString() || "",
        educationLevel: job.education_level || "",
        company: job.company || "",
        jobLocation: job.location || "",
        jobType: job.job_type || "",
        skills: Array.isArray(job.skills)
          ? job.skills
          : typeof job.skills === "string"
            ? [job.skills]
            : [],
        jobDescription: job.description || "",
        jobRole: job.job_role || "",
        salary_min: job.salary_min?.toString() || "",
        salary_max: job.salary_max?.toString() || "",
        tags: job.tags || [],
        languageProficiency: job.language_proficiency || "",
        openings: job.openings?.toString() || "",
        jobPriority: job.priority?.toString() || "",
        assignedTo: (job.assigned_to || []).map((u) => String(u.id)),
      });
    },
    [formik],
  );

  const fetchRecommendedCandidates = useCallback(async (jobId) => {
    try {
      setIsFetchingRecommendedCandidates(true);
      const jobRes = await get(
        VYLABS_BASE_URL,
        `${GET_JOB_DETAILS_BY_ID}?id=${jobId}`,
      );
      const job = jobRes?.job;

      if (!job) {
        console.error("Failed to fetch job details for recommendations");
        candidatesCache.set(jobId, []);
        setIsFetchingRecommendedCandidates(false);
        return [];
      }

      let skillsArray = [];
      if (Array.isArray(job.skills)) {
        skillsArray = job.skills;
      } else if (typeof job.skills === "string") {
        skillsArray = job.skills
          .split(",")
          .map((skill) => skill.trim())
          .filter((skill) => skill !== "");
      }

      const payload = {
        limit: 100,
        job_role: job.job_role || "",
        description: job.description || "",
        location: job.location || "",
        skills: skillsArray,
        rerank: true,
        job_id: jobId,
      };

      if (job.years_experience !== null && job.years_experience !== undefined) {
        const yearsExp = parseInt(job.years_experience, 10);

        if (!isNaN(yearsExp)) {
          payload.min_years_of_experience = yearsExp;
        }
      }

      Object.keys(payload).forEach((key) => {
        if (
          payload[key] === "" ||
          payload[key] === null ||
          payload[key] === undefined ||
          (Array.isArray(payload[key]) && payload[key].length === 0)
        ) {
          delete payload[key];
        }
      });

      const response = await post(
        VYLABS_BASE_URL,
        SIMPLE_SEARCH_CANDIDATES,
        payload,
      );

      if (
        !response ||
        !response.candidates ||
        response.candidates.length === 0
      ) {
        candidatesCache.set(jobId, []);
        setIsFetchingRecommendedCandidates(false);
        return [];
      }

      const detailedCandidates = response.candidates.map((candidate) => ({
        ...candidate,
        parsed_resume:
          typeof candidate.parsed_resume === "string"
            ? JSON.parse(candidate.parsed_resume || "{}")
            : candidate.parsed_resume || {},
        isFileBased: false,
      }));

      candidatesCache.set(jobId, detailedCandidates);
      setIsFetchingRecommendedCandidates(false);
      return detailedCandidates;
    } catch (err) {
      console.error("Error fetching recommended candidates:", err);
      setIsFetchingRecommendedCandidates(false);
      candidatesCache.set(jobId, []);
      return [];
    }
  }, []);

  const handleOpenPipelineJobDetails = useCallback(
    async (row) => {
      if (!row?.id) return;

      const fallbackJob = {
        id: row.id,
        title: row.job,
        subTitle: row.client,
        openings: row.req,
        active: row.active,
        created_at: row.created_at,
        updated_at: row.updated_at,
      };

      setRecommendedCandidateIds([]);
      setIsEditingPipelineJob(false);
      setOriginalPipelineJobData(null);
      setEditedPipelineJobData(null);
      setJobDetailsValidation(null);
      setPipelineJobDetailsActiveTab("Job Details");
      setPipelineAssignedCandidateCount(0);

      try {
        const jobRes = await get(
          VYLABS_BASE_URL,
          `${GET_JOB_DETAILS_BY_ID}?id=${row.id}`,
        );
        const detailedJob = jobRes?.job || fallbackJob;

        const updatedPipelineJob = {
          ...fallbackJob,
          ...detailedJob,
          id: detailedJob?.id || row.id,
          title: detailedJob?.title || row.job,
          subTitle:
            detailedJob?.subTitle ||
            detailedJob?.client?.name ||
            detailedJob?.client_name ||
            row.client,
          openings: detailedJob?.openings ?? row.req,
          active: detailedJob?.active ?? row.active,
          created_at: detailedJob?.created_at || row.created_at,
          updated_at: detailedJob?.updated_at || row.updated_at,
        };

        setSelectedPipelineJob(updatedPipelineJob);
        setPipelineJobFormValues(updatedPipelineJob);
        setIsPipelineJobDetailsOpen(true);
        const candidates = await fetchRecommendedCandidates(row.id);
        setRecommendedCandidateIds(candidates.map((candidate) => candidate.id));
      } catch (err) {
        console.error("Error fetching job details:", err);
        setSelectedPipelineJob(fallbackJob);
        setPipelineJobFormValues(fallbackJob);
        setIsPipelineJobDetailsOpen(true);
        const candidates = await fetchRecommendedCandidates(row.id);
        setRecommendedCandidateIds(candidates.map((candidate) => candidate.id));
      }
    },
    [fetchRecommendedCandidates, setPipelineJobFormValues],
  );

  const handleClosePipelineJobDetails = useCallback(() => {
    const pipelineJobId = selectedPipelineJob?.id;

    setIsPipelineJobDetailsOpen(false);
    setSelectedPipelineJob(null);
    setOriginalPipelineJobData(null);
    setEditedPipelineJobData(null);
    setIsEditingPipelineJob(false);
    setJobDetailsValidation(null);
    setRecommendedCandidateIds([]);
    setPipelineAssignedCandidateCount(0);
    if (pipelineJobId) {
      candidatesCache.delete(pipelineJobId);
      channelIdCache.delete(pipelineJobId);
    }
    formik.resetForm();
  }, [formik, selectedPipelineJob?.id]);

  const handlePipelineAssignedCountChange = useCallback((count) => {
    setPipelineAssignedCandidateCount(Number(count) || 0);
  }, []);

  const handleEditPipelineJob = useCallback(() => {
    setOriginalPipelineJobData({ ...selectedPipelineJob });
    setEditedPipelineJobData({ ...selectedPipelineJob });
    setPipelineJobFormValues(selectedPipelineJob);

    const assignedUsers = Array.isArray(selectedPipelineJob?.assigned_to)
      ? selectedPipelineJob.assigned_to.filter((u) => u?.id)
      : [];
    setMembersList((prev) => {
      const seeded = assignedUsers.map((u) => ({
        id: u.id,
        label: u.name || "N/A",
        value: String(u.id),
      }));
      const seededIds = new Set(seeded.map((s) => s.value));
      return [
        ...seeded,
        ...prev.filter((m) => !seededIds.has(String(m?.value))),
      ];
    });
    fetchAllMembers("");
    setIsEditingPipelineJob(true);
  }, [selectedPipelineJob, setPipelineJobFormValues, fetchAllMembers]);

  const resetPipelineJobEdit = useCallback(() => {
    if (originalPipelineJobData) {
      setSelectedPipelineJob(originalPipelineJobData);
      setPipelineJobFormValues(originalPipelineJobData);
    }
    setIsEditingPipelineJob(false);
    setOriginalPipelineJobData(null);
    setEditedPipelineJobData(null);
  }, [originalPipelineJobData, setPipelineJobFormValues]);

  const handlePipelineJobChange = useCallback(
    (data) => {
      if (data.validateForm) {
        setJobDetailsValidation(() => data.validateForm);
        return;
      }

      const updatedValues = {
        ...formik.values,
        title: data.title || formik.values.title,
        jobRole: data.job_role || formik.values.jobRole,
        jobDescription: data.description || formik.values.jobDescription,
        skills: data.skills || formik.values.skills,
        tags: data.tags || formik.values.tags,
        client_id: data.client_id || formik.values.client_id,
        openings: data.openings || formik.values.openings,
      };

      formik.setValues(updatedValues);
      setEditedPipelineJobData((prev) => ({
        ...prev,
        ...data,
        min_years_experience: formik.values.minYearsExperience,
        max_years_experience: formik.values.maxYearsExperience,
        location: formik.values.jobLocation,
        salary_min: formik.values.salary_min,
        salary_max: formik.values.salary_max,
        job_type: formik.values.jobType,
        openings: formik.values.openings,
      }));
    },
    [formik],
  );

  const handleOpenPipelineJobDeleteDialog = useCallback(() => {
    setOpenJobDeleteDialog(true);
  }, []);

  const handleDeletePipelineJob = useCallback(async () => {
    if (!selectedPipelineJob?.id) return;

    try {
      await deleteRequest(
        VYLABS_BASE_URL,
        `${DELETE_JOB_DETAILS}?id=${selectedPipelineJob.id}`,
      );
      Toast("success", "Job deleted successfully");
      setOpenJobDeleteDialog(false);
      handleClosePipelineJobDetails();
      setDashboardRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting job:", error);
      Toast(
        "error",
        "Failed to delete job: " +
          (error.response?.data?.message || error.message),
      );
    }
  }, [handleClosePipelineJobDetails, selectedPipelineJob?.id]);

  const savePipelineJobChanges = useCallback(async () => {
    if (!selectedPipelineJob?.id) return;

    const jobDetailsResult = jobDetailsValidation
      ? await jobDetailsValidation()
      : { isValid: true, values: {} };

    const parentErrors = await formik.validateForm();
    const isParentValid = Object.keys(parentErrors).length === 0;

    if (!jobDetailsResult.isValid || !isParentValid) {
      formik.setTouched({
        title: true,
        jobRole: true,
        jobType: true,
        jobLocation: true,
        minYearsExperience: true,
        maxYearsExperience: true,
        salary_min: true,
        salary_max: true,
        skills: true,
        openings: true,
      });
      Toast("error", "Please fill all required fields correctly");
      return;
    }

    const mainFormValues = formik.values;
    const combinedValues = {
      ...mainFormValues,
      title: mainFormValues.title || jobDetailsResult.values.title,
      jobRole: mainFormValues.jobRole || jobDetailsResult.values.job_role,
      jobDescription:
        jobDetailsResult.values.description ||
        mainFormValues.jobDescription ||
        "",
      skills: mainFormValues.skills || jobDetailsResult.values.skills,
      tags: mainFormValues.tags || jobDetailsResult.values.tags,
      openings: mainFormValues.openings || jobDetailsResult.values.openings,
      jobPriority: mainFormValues.jobPriority,
      assignedTo: mainFormValues.assignedTo || [],
    };

    const originalValues = {
      title: selectedPipelineJob.title,
      jobRole: selectedPipelineJob.job_role,
      jobType: selectedPipelineJob.job_type,
      jobMinYearsExperience: selectedPipelineJob.min_years_experience,
      jobMaxYearsExperience: selectedPipelineJob.max_years_experience,
      salary_min: selectedPipelineJob.salary_min,
      salary_max: selectedPipelineJob.salary_max,
      jobLocation: selectedPipelineJob.location,
      jobDescription: selectedPipelineJob.description,
      skills: selectedPipelineJob.skills,
      tags: selectedPipelineJob.tags,
      openings: selectedPipelineJob.openings,
      priority: selectedPipelineJob.priority,
      assignedTo: (selectedPipelineJob.assigned_to || []).map((u) =>
        String(u.id),
      ),
    };

    const normalizeSkillsTags = (value) =>
      Array.isArray(value) ? value.join(", ") : value || "";
    const normalizeNumber = (value) => {
      if (value === undefined || value === null || value === "") return 0;
      return Number(value);
    };
    const changedFields = {};

    if (combinedValues.title !== originalValues.title) {
      changedFields.title = combinedValues.title;
    }
    if (combinedValues.jobRole !== originalValues.jobRole) {
      changedFields.jobRole = combinedValues.jobRole;
    }
    if (combinedValues.jobType !== originalValues.jobType) {
      changedFields.jobType = combinedValues.jobType;
    }
    if (
      normalizeNumber(combinedValues.minYearsExperience) !==
      normalizeNumber(originalValues.jobMinYearsExperience)
    ) {
      changedFields.minYearsExperience = combinedValues.minYearsExperience;
    }
    if (
      normalizeNumber(combinedValues.maxYearsExperience) !==
      normalizeNumber(originalValues.jobMaxYearsExperience)
    ) {
      changedFields.maxYearsExperience = combinedValues.maxYearsExperience;
    }
    if (
      normalizeNumber(combinedValues.salary_min) !==
      normalizeNumber(originalValues.salary_min)
    ) {
      changedFields.salary_min = combinedValues.salary_min;
    }
    if (
      normalizeNumber(combinedValues.salary_max) !==
      normalizeNumber(originalValues.salary_max)
    ) {
      changedFields.salary_max = combinedValues.salary_max;
    }
    if (combinedValues.jobLocation !== originalValues.jobLocation) {
      changedFields.jobLocation = combinedValues.jobLocation;
    }
    if (combinedValues.jobDescription !== originalValues.jobDescription) {
      changedFields.jobDescription = combinedValues.jobDescription;
    }
    if (
      normalizeSkillsTags(combinedValues.skills) !==
      normalizeSkillsTags(originalValues.skills)
    ) {
      changedFields.skills = combinedValues.skills;
    }
    if (
      normalizeSkillsTags(combinedValues.tags) !==
      normalizeSkillsTags(originalValues.tags)
    ) {
      changedFields.tags = combinedValues.tags;
    }
    if (
      normalizeNumber(combinedValues.openings) !==
      normalizeNumber(originalValues.openings)
    ) {
      changedFields.openings = combinedValues.openings;
    }
    if (
      normalizeNumber(combinedValues.jobPriority) !==
      normalizeNumber(originalValues.priority)
    ) {
      changedFields.jobPriority = combinedValues.jobPriority;
    }
    const sameIdSet = (a = [], b = []) => {
      const setA = new Set(a.map(String));
      const setB = new Set(b.map(String));
      return setA.size === setB.size && [...setA].every((id) => setB.has(id));
    };
    if (!sameIdSet(combinedValues.assignedTo, originalValues.assignedTo)) {
      changedFields.assigned_to = (combinedValues.assignedTo || []).map(String);
    }

    if (Object.keys(changedFields).length === 0) {
      Toast("info", "No changes detected");
      setIsEditingPipelineJob(false);
      handleClosePipelineJobDetails();
      return;
    }

    try {
      const jobData = {
        jobID: selectedPipelineJob.id,
      };

      if (Object.prototype.hasOwnProperty.call(changedFields, "title")) {
        jobData.jobTitle = changedFields.title;
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "jobRole")) {
        jobData.jobRole = changedFields.jobRole;
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "jobType")) {
        jobData.jobType = changedFields.jobType;
      }
      if (
        Object.prototype.hasOwnProperty.call(
          changedFields,
          "minYearsExperience",
        )
      ) {
        jobData.jobMinYearsExperience = Number(
          changedFields.minYearsExperience || 0,
        );
      }
      if (
        Object.prototype.hasOwnProperty.call(
          changedFields,
          "maxYearsExperience",
        )
      ) {
        jobData.jobMaxYearsExperience = Number(
          changedFields.maxYearsExperience || 0,
        );
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "salary_min")) {
        jobData.jobSalaryMin = Number(changedFields.salary_min || 0);
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "salary_max")) {
        jobData.jobSalaryMax = Number(changedFields.salary_max || 0);
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "jobLocation")) {
        jobData.jobLocation = changedFields.jobLocation;
      }
      if (
        Object.prototype.hasOwnProperty.call(changedFields, "jobDescription")
      ) {
        jobData.jobDescription = changedFields.jobDescription;
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "skills")) {
        jobData.jobSkills = Array.isArray(changedFields.skills)
          ? changedFields.skills.join(", ")
          : changedFields.skills || "";
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "tags")) {
        jobData.jobTags = Array.isArray(changedFields.tags)
          ? changedFields.tags.join(", ")
          : changedFields.tags || "";
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "openings")) {
        jobData.jobOpenings = Number(changedFields.openings || 0);
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "jobPriority")) {
        jobData.jobPriority = Number(changedFields.jobPriority || 0);
      }
      if (Object.prototype.hasOwnProperty.call(changedFields, "assigned_to")) {
        jobData.assigned_to = changedFields.assigned_to;
      }

      await put(VYLABS_BASE_URL, UPDATE_JOB, jobData);

      const updatedJob = {
        ...selectedPipelineJob,
        id: selectedPipelineJob.id,
        ...(changedFields.title && { title: changedFields.title }),
        ...(changedFields.jobRole && { job_role: changedFields.jobRole }),
        ...(changedFields.jobType && { job_type: changedFields.jobType }),
        ...(changedFields.minYearsExperience && {
          min_years_experience: changedFields.minYearsExperience,
        }),
        ...(changedFields.maxYearsExperience && {
          max_years_experience: changedFields.maxYearsExperience,
        }),
        ...(changedFields.salary_min && {
          salary_min: changedFields.salary_min,
        }),
        ...(changedFields.salary_max && {
          salary_max: changedFields.salary_max,
        }),
        ...(changedFields.jobLocation && {
          location: changedFields.jobLocation,
        }),
        ...(changedFields.jobDescription && {
          description: changedFields.jobDescription,
        }),
        ...(changedFields.skills && { skills: changedFields.skills }),
        ...(changedFields.tags && { tags: changedFields.tags }),
        ...(changedFields.openings && { openings: changedFields.openings }),
        ...(changedFields.jobPriority && {
          priority: Number(changedFields.jobPriority),
        }),
        ...(changedFields.assigned_to && {
          assigned_to: changedFields.assigned_to.map((id) => {
            const member = membersList.find((m) => String(m?.value) === id);
            const prev = (selectedPipelineJob.assigned_to || []).find(
              (u) => String(u.id) === id,
            );
            return { id, name: member?.label || prev?.name || "" };
          }),
        }),
      };

      setSelectedPipelineJob(updatedJob);
      setIsEditingPipelineJob(false);
      Toast("success", "Job updated successfully");
      handleClosePipelineJobDetails();
      setDashboardRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating job:", error);
      Toast(
        "error",
        "Failed to update job: " +
          (error.response?.data?.message || error.message),
      );
    }
  }, [
    formik,
    handleClosePipelineJobDetails,
    jobDetailsValidation,
    membersList,
    selectedPipelineJob,
  ]);

  const filteredJobs = useMemo(() => jobs, [jobs]);
  const filteredRecruiterPerformance = useMemo(
    () => recruiterPerformance,
    [recruiterPerformance],
  );
  const filteredVendorsFromBuild = useMemo(
    () => vendorsFromBuild,
    [vendorsFromBuild],
  );
  const filteredTeamWorkload = useMemo(() => teamWorkload, [teamWorkload]);
  const maxRecruitmentFunnelValue = useMemo(
    () =>
      Math.max(
        ...recruitmentFunnel.map(
          (item) => Number(item.barValue ?? item.count) || 0,
        ),
        0,
      ),
    [recruitmentFunnel],
  );
  const formatFunnelCount = useCallback((value) => {
    const numericValue = Number(value);

    return Number.isFinite(numericValue)
      ? numericValue.toLocaleString("en-US")
      : value || 0;
  }, []);

  const handleCallGetApplicationsByJob = (job_id, stage, stageName) => {
    setApplicationSource("job");
    setJobPipelineJobId(job_id);
    setJobPipelineStage(stage);
    setJobPipelingStageName(stageName);
    getApplicationsByJob(job_id, stage, 1);
  };

  const renderJobPipelineMetric = (row, metricKey, metricName) => {
    const count = Number(row?.[metricKey]) || 0;

    return (
      <div
        className={
          count > 0
            ? "admin-dashboard-table-hover-number"
            : "admin-dashboard-table-not-hover-number"
        }
        onClick={() =>
          count > 0 &&
          handleCallGetApplicationsByJob(
            row.id,
            metricKey === "totalRecruiters"
              ? "recruiters"
              : metricKey === "assignedRecruiters"
                ? "assigned_recruiters"
                : "tracked_recruiters",
            metricName,
          )
        }
        style={{ cursor: count > 0 ? "pointer" : "auto" }}
      >
        {row?.[metricKey] ?? 0}
      </div>
    );
  };

  const allJobPipelineColumns = useMemo(() => {
    const stageDefinitions = getStageDefinitionsFromRows(filteredJobs);
    const stageColumns = stageDefinitions.map((stageDefinition) => {
      const systemKey = stageDefinition.system_key;

      return {
        columnKey: systemKey,
        isCustomizable: true,
        name: toCapitalize(stageDefinition.name),
        width: "110px",
        cell: (row) => {
          const rowStage = row?.stages?.find(
            (stage) => (stage.system_key || stage.systemKey) === systemKey,
          );

          return (
            <div
              className={
                rowStage?.count > 0
                  ? "admin-dashboard-table-hover-number"
                  : "admin-dashboard-table-not-hover-number"
              }
              onClick={() =>
                rowStage?.count > 0 &&
                handleCallGetApplicationsByJob(
                  row.id,
                  systemKey,
                  rowStage.name || stageDefinition.name,
                )
              }
              style={{
                cursor: rowStage?.count > 0 ? "pointer" : "auto",
              }}
            >
              {rowStage?.count ?? 0}
            </div>
          );
        },
        sortable: true,
        sortField: `stages.${stageDefinition.system_key}`,
      };
    });

    return [
      {
        columnKey: "job",
        name: "Job",
        grow: 1.5,
        minWidth: "190px",
        sortable: true,
        sortField: "jobName",
        cell: (row) => (
          <div className="admin-dashboard-job-cell">
            <CustomTooltip title={row.job} arrow>
              <div>{applyEllipsis(row.job, 15)}</div>
            </CustomTooltip>
          </div>
        ),
      },
      {
        columnKey: "client",
        name: isHiringTeamTenant ? "Hiring Team" : "Client",
        grow: 1.5,
        minWidth: "160px",
        sortable: true,
        sortField: "clientName",
        cell: (row) => (
          <div className="admin-dashboard-job-cell">
            <CustomTooltip title={row.client} arrow>
              <div>{applyEllipsis(row.client, 15)}</div>
            </CustomTooltip>
          </div>
        ),
      },
      {
        columnKey: "priority",
        name: "Priority",
        selector: (row) =>
          priority.find(
            (item) => Number(item.id ?? item.value) === Number(row.priority),
          )?.name || "N/A",
        minWidth: "120px",
        sortable: true,
        sortField: "priority",
        cell: (row) => {
          const priorityData = priority.find(
            (item) => Number(item.id ?? item.value) === Number(row.priority),
          );
          const priorityIcon = PRIORITY_ICON_MAP[priorityData?.icon];

          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: priorityData?.color || "#6B7280",
                fontWeight: 600,
              }}
            >
              {priorityIcon && (
                <img
                  src={priorityIcon}
                  alt={priorityData?.name || "Priority"}
                  style={{ width: "16px", height: "16px" }}
                />
              )}
              {priorityData?.name || priorityData?.title || "N/A"}
            </span>
          );
        },
      },
      {
        columnKey: "req",
        name: "Req",
        selector: (row) => row.req,
        width: "70px",
        sortable: true,
        sortField: "required",
      },

      {
        columnKey: "totalRecruiters",
        name: "Total Recruiters",
        selector: (row) => row.totalRecruiters,
        cell: (row) =>
          renderJobPipelineMetric(row, "totalRecruiters", "Total Recruiters"),
        width: "110px",
        sortable: true,
        sortField: "totalRecruiters",
      },
      {
        columnKey: "assignedRecruiters",
        name: "Assigned Recruiters",
        selector: (row) => row.assignedRecruiters,
        cell: (row) =>
          renderJobPipelineMetric(
            row,
            "assignedRecruiters",
            "Assigned Recruiters",
          ),
        width: "140px",
        sortable: true,
        sortField: "assignedRecruiters",
      },
      {
        columnKey: "trackedRecruiters",
        name: "Tracked Recruiters",
        selector: (row) => row.trackedRecruiters,
        cell: (row) =>
          renderJobPipelineMetric(
            row,
            "trackedRecruiters",
            "Tracked Recruiters",
          ),
        width: "140px",
        sortable: true,
        sortField: "trackedRecruiters",
      },

      ...stageColumns,

      {
        columnKey: "completion",
        name: "Completion",
        minWidth: "140px",
        sortable: true,
        sortField: "completion",
        cell: (row) => (
          <div className="admin-dashboard-progress">
            <span>
              <i
                style={{
                  width: `${Math.min(row.completion, 100)}%`,
                }}
              />
            </span>
            {roundCompletion(row.completion)}%
          </div>
        ),
      },
      {
        columnKey: "actions",
        name: "Actions",
        right: true,
        width: "112px",
        cell: (row) => (
          <div className="admin-dashboard-actions">
            <CustomTooltip title="View Job" arrow>
              <img
                src={EyeIcon}
                alt="view job"
                className="parser-table-action-icons"
                onClick={() => handleOpenPipelineJobDetails(row)}
              />
            </CustomTooltip>
          </div>
        ),
      },
    ];
  }, [
    filteredJobs,
    handleCallGetApplicationsByJob,
    handleOpenPipelineJobDetails,
    isHiringTeamTenant,
    priority,
  ]);

  const tableColumns = useMemo(
    () =>
      allJobPipelineColumns.filter(
        (column) =>
          !column.isCustomizable || visibleColumns.includes(column.columnKey),
      ),
    [allJobPipelineColumns, visibleColumns],
  );

  const customizableJobPipelineColumns = useMemo(
    () => allJobPipelineColumns.filter((column) => column.isCustomizable),
    [allJobPipelineColumns],
  );

  const jobPipelineColumnSignature = customizableJobPipelineColumns
    .map((column) => `${column.columnKey}:${column.name}`)
    .join("|");

  const columnsForCustomize = useMemo(
    () => getCustomizeColumnsFromTableColumns(customizableJobPipelineColumns),
    [jobPipelineColumnSignature],
  );

  useEffect(() => {
    const allColumnKeys = Object.keys(columnsForCustomize);
    const savedColumnKeys = getSavedColumnKeys(
      savedJobPipelineColumns,
      allColumnKeys,
    );

    const nextVisibleColumns = savedJobPipelineColumnViewId
      ? savedColumnKeys
      : allColumnKeys;
    setVisibleColumns(nextVisibleColumns);
    setDraftVisibleColumns(nextVisibleColumns);
  }, [
    columnsForCustomize,
    savedJobPipelineColumnViewId,
    savedJobPipelineColumns,
  ]);

  const toggleColumnVisibility = (columnKey) => {
    setDraftVisibleColumns((prev) => {
      if (prev.includes(columnKey)) {
        return prev.filter((key) => key !== columnKey);
      }

      return [...prev, columnKey];
    });
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => {
      if (!prev) {
        setDraftVisibleColumns(visibleColumns);
      }

      return !prev;
    });
  };

  const handleSelectAll = () => {
    setDraftVisibleColumns(Object.keys(columnsForCustomize));
  };

  const handleSelectNone = () => {
    setDraftVisibleColumns([]);
  };

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        dropdownBtnRef.current &&
        !dropdownBtnRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleGetJobPipelineColumnSavedViews = useCallback(async () => {
    try {
      const savedViewRes = await get(
        VYLABS_BASE_URL,
        `${GET_SAVED_VIEWS}?scope=${JOB_PIPELINE_COLUMN_SAVED_VIEW_SCOPE}`,
      );
      const views = savedViewRes?.views || [];
      const defaultView = views.find((item) => item.isDefault);

      setSavedJobPipelineColumns(defaultView?.filters || []);
      setSavedJobPipelineColumnViewId(defaultView?.id || null);
    } catch (error) {
      console.error("error in handleGetJobPipelineColumnSavedViews", error);
      setSavedJobPipelineColumns([]);
      setSavedJobPipelineColumnViewId(null);
    }
  }, []);

  useEffect(() => {
    handleGetJobPipelineColumnSavedViews();
  }, [handleGetJobPipelineColumnSavedViews]);

  const getSelectedJobPipelineColumns = () =>
    getSelectedCustomizeColumns(columnsForCustomize, draftVisibleColumns);

  const handleCreateSaveView = async () => {
    try {
      const filters = getSelectedJobPipelineColumns();
      const res = await post(VYLABS_BASE_URL, CREATE_SAVED_VIEWS, {
        filters,
        name: "Admin Job Pipeline Columns",
        scope: JOB_PIPELINE_COLUMN_SAVED_VIEW_SCOPE,
        isDefault: true,
      });

      if (res?.view?.id) {
        Toast("success", "Created Successfully!");
        setVisibleColumns(draftVisibleColumns);
        handleGetJobPipelineColumnSavedViews();
        setDropdownOpen(false);
      }
    } catch (error) {
      console.error("error in handleCreateSaveView", error);
      Toast(
        "error",
        error?.response?.data || "Failed to create customized columns",
      );
    }
  };

  const handleUpdateSaveView = async () => {
    try {
      if (!savedJobPipelineColumnViewId) {
        handleCreateSaveView();
        return;
      }

      const res = await patch(
        VYLABS_BASE_URL,
        `${UPDATE_SAVED_VIEWS}/${savedJobPipelineColumnViewId}`,
        { filters: getSelectedJobPipelineColumns() },
      );

      if (res?.view?.id) {
        Toast("success", "Updated Successfully!");
        setVisibleColumns(draftVisibleColumns);
        handleGetJobPipelineColumnSavedViews();
        setDropdownOpen(false);
      }
    } catch (error) {
      console.error("error in handleUpdateSaveView", error);
      Toast(
        "error",
        error?.response?.data || "Failed to update customized columns",
      );
    }
  };

  const getAdminDashboardFollowups = useCallback(async (filtersToUse = {}) => {
    const requestId = ++followUpsRequestRef.current;
    setFollowUpsLoader(true);
    const params = new URLSearchParams();
    ["stage", "jobId", "time", "fromDate", "toDate"].forEach((key) => {
      if (isValidDashboardFilterValue(filtersToUse[key])) {
        params.set(key, filtersToUse[key]);
      }
    });
    const query = params.toString();

    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${METRICS_RECRUITER_DASHBOARD_FOLLOWUPS}${query ? `?${query}` : ""}`,
      );
      if (requestId !== followUpsRequestRef.current) return;
      setFollowUps({
        today: Array.isArray(response?.today) ? response.today : [],
        overdue: Array.isArray(response?.overdue) ? response.overdue : [],
        tomorrow: Array.isArray(response?.tomorrow) ? response.tomorrow : [],
      });
    } catch (error) {
      if (requestId !== followUpsRequestRef.current) return;
      console.error("Error fetching admin dashboard follow-ups:", error);
      setFollowUps({ today: [], overdue: [], tomorrow: [] });
      Toast("error", "Unable to load follow-ups. Please try again.");
    } finally {
      if (requestId === followUpsRequestRef.current) {
        setFollowUpsLoader(false);
      }
    }
  }, []);

  const handleCallGetVendorPipelinesActions = (vendor_id, status) => {
    setApplicationSource("vendor");
    setJobPipelineJobId(vendor_id);
    setJobPipelineStage(status);
    getVendorPipelinesActions(vendor_id, status, 1);
  };

  const handleViewVendorDetails = useCallback(async (row) => {
    if (!row?.id) return;

    setVendorDetailsLoading(true);
    setSelectedVendorDetails(null);
    setShowVendorDetails(true);

    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${GET_VENDOR_DETAILS_BY_ID}?id=${row.id}`,
      );

      setSelectedVendorDetails(response);
    } catch (error) {
      console.error("Error fetching vendor details:", error);
      setShowVendorDetails(false);
      Toast(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch vendor details",
      );
    } finally {
      setVendorDetailsLoading(false);
    }
  }, []);

  const handleEditVendor = () => {
    setIsEditingVendor(true);
    setVendorDetailsActiveTab("Info");
  };

  const handleCancelEditVendor = () => {
    setIsEditingVendor(false);
    setEditAchFile(null);
    setEditVoidChequeFile(null);
    setEditAuditDocument(null);
    if (selectedVendorDetails) {
      const initialValues = {
        email: selectedVendorDetails?.email || "",
        nameOfVendor: selectedVendorDetails?.name || "",
        contactPersonName: selectedVendorDetails?.contact_person || "",
        contactDesignation: selectedVendorDetails?.contact_designation || "",
        mobile: selectedVendorDetails?.phone || "",
        country: selectedVendorDetails?.country || "",
        state: selectedVendorDetails?.state || "",
        city: selectedVendorDetails?.city || "",
        address: selectedVendorDetails?.address || "",
        zipCode: selectedVendorDetails?.zip_code || "",
        remarks: selectedVendorDetails?.remarks || "",
        vendorStatus: selectedVendorDetails?.status || "",
        vendorPriority: selectedVendorDetails?.priority || "",
        federalTax: selectedVendorDetails?.federal_tax || "",
        bankName: selectedVendorDetails?.bank_name || "",
        accountName: selectedVendorDetails?.account_name || "",
        accountNumber: selectedVendorDetails?.account_number || "",
        routingNumber: selectedVendorDetails?.routing_number || "",
        natureOfAccount: selectedVendorDetails?.account_nature || "",
        farcaCode: selectedVendorDetails?.farca_reporting_code || "",
        exemptPayeeCode: selectedVendorDetails?.exempt_payee_code || "",
      };
      vendorFormik.setValues(initialValues);
      vendorFormik.setTouched({});
      vendorFormik.setErrors({});
    }
  };

  const saveVendorChanges = async () => {
    if (!selectedVendorDetails) {
      Toast("error", "No vendor selected");
      return;
    }
    try {
      const payloadValues = {
        name: vendorFormik.values.nameOfVendor.trim(),
        email: vendorFormik.values.email.trim(),
        phone: vendorFormik.values.mobile
          ? vendorFormik.values.mobile.startsWith("+")
            ? vendorFormik.values.mobile
            : `+${vendorFormik.values.mobile}`
          : "",
        contact_person: vendorFormik.values.contactPersonName.trim(),
        contact_designation: vendorFormik.values.contactDesignation.trim(),
        address: vendorFormik.values.address.trim(),
        city: vendorFormik.values.city.trim(),
        country: vendorFormik.values.country.trim(),
        state: vendorFormik.values.state.trim(),
        zip_code: vendorFormik.values.zipCode.trim(),
        federal_tax: vendorFormik.values.federalTax.trim(),
        bank_name: vendorFormik.values.bankName.trim(),
        account_name: vendorFormik.values.accountName.trim(),
        account_number: vendorFormik.values.accountNumber.trim(),
        routing_number: vendorFormik.values.routingNumber.trim(),
        account_nature: vendorFormik.values.natureOfAccount.trim(),
        farca_reporting_code: vendorFormik.values.farcaCode.trim(),
        exempt_payee_code: vendorFormik.values.exemptPayeeCode.trim(),
      };

      const changes = {};
      for (const key in payloadValues) {
        const currentValue = payloadValues[key];
        const originalValue = selectedVendorDetails[key];
        const normalizedCurrentValue =
          currentValue === null || currentValue === undefined
            ? ""
            : String(currentValue).trim();
        const normalizedOriginalValue =
          originalValue === null || originalValue === undefined
            ? ""
            : String(originalValue).trim();
        if (normalizedCurrentValue !== normalizedOriginalValue) {
          changes[key] = currentValue;
        }
      }

      if (Object.keys(changes).length === 0) {
        Toast("info", "No changes detected");
        setIsEditingVendor(false);
        return;
      }

      const payload = {
        id: selectedVendorDetails.id,
        ...changes,
      };
      await post(VYLABS_BASE_URL, UPDATE_VENDORS_INFO_FIELDS, payload);
      Toast("success", "Vendor updated successfully");

      const updatedVendorState = { ...selectedVendorDetails, ...changes };
      setSelectedVendorDetails(updatedVendorState);
      setIsEditingVendor(false);
      setEditAchFile(null);
      setEditVoidChequeFile(null);
      setEditAuditDocument(null);
      fetchAdminDashboardData(
        vendorPipelineCurrentPage,
        false,
        false,
        true,
        fromDate,
        toDate,
      );
    } catch (error) {
      console.error("Error updating vendor:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update vendor. Please try again.";
      Toast("error", errorMessage);
    }
  };

  const handleOpenVendorDeleteDialog = () => {
    setIsVendorDeleteDialogOpen(true);
  };

  const handleDeleteVendors = async () => {
    if (!selectedVendorDetails?.id) {
      Toast("error", "No vendor selected for deletion");
      return;
    }
    try {
      await deleteRequest(
        VYLABS_BASE_URL,
        `vendors?id=${selectedVendorDetails.id}`,
        {
          id: selectedVendorDetails.id,
        },
      );
      Toast("success", "Vendor deleted successfully");
      setIsVendorDeleteDialogOpen(false);
      setShowVendorDetails(false);
      setSelectedVendorDetails(null);
      setIsEditingVendor(false);
      fetchAdminDashboardData(
        vendorPipelineCurrentPage,
        false,
        false,
        true,
        fromDate,
        toDate,
      );
    } catch (error) {
      console.error("Error deleting vendor:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete vendor. Please try again.";
      Toast("error", errorMessage);
    }
  };

  const handleVendorPhoneInputChange = useCallback(
    (phone) => {
      const normalizedPhone = phone?.startsWith("+") ? phone : `+${phone}`;
      vendorFormik.setFieldValue("mobile", normalizedPhone);
      vendorFormik.setFieldTouched("mobile", true, false);
      vendorFormik.validateField("mobile");
    },
    [vendorFormik],
  );

  const handleVendorTabClick = (tabName) => {
    if (isEditingVendor && tabName !== "Info") {
      Toast(
        "warning",
        "Please save or cancel your changes before switching tabs",
      );
      return;
    }
    setVendorDetailsActiveTab(tabName);
  };

  const handleCloseVendorDetails = useCallback(() => {
    setShowVendorDetails(false);
    setSelectedVendorDetails(null);
    setVendorDetailsActiveTab("Info");
    setIsEditingVendor(false);
    setEditAchFile(null);
    setEditVoidChequeFile(null);
    setEditAuditDocument(null);
  }, []);

  const fetchCandidateByIdForModal = useCallback(async (id) => {
    try {
      const res = await get(VYLABS_BASE_URL, `${GET_CANDIDATE_BY_ID}?id=${id}`);
      const candidate = res?.candidate;
      const candidatefiles = res?.files;
      const parsedResumeData = candidate?.parsed_resume
        ? JSON.parse(candidate.parsed_resume)
        : null;
      const presignedUrl =
        candidatefiles && candidatefiles.length > 0
          ? candidatefiles[0].presigned_url
          : null;

      const updatedCandidate = {
        ...candidate,
        secondaryEmail: candidate.secondary_email,
        secondaryPhone: candidate.secondary_phone,
        vendorID: candidate.vendor_id,
        lineAddress1: candidate.line_address_1,
        lineAddress2: candidate.line_address_2,
        preferredSalary: candidate.preferred_salary,
        zipCode: candidate.zip_code,
        parsedResumeData,
        files: candidatefiles || null,
        presignedUrl,
        isFileBased: false,
        avatar: res?.profile_img_url,
      };
      if (updatedCandidate.vendorID) {
        try {
          const vendorResponse = await get(
            VYLABS_BASE_URL,
            `${GET_VENDOR_DETAILS_BY_ID}?id=${updatedCandidate.vendorID}`,
          );
          setAssignedVendor(vendorResponse);
          updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
        } catch (err) {
          console.error("Error fetching vendor details:", err);
          updatedCandidate.vendorDetails = null;
          setAssignedVendor(null);
        }
      } else {
        updatedCandidate.vendorDetails = null;
        setAssignedVendor(null);
      }

      setSelectedVendorCandidateForModal(updatedCandidate);
      setIsVendorCandidateModalOpen(true);
      return updatedCandidate;
    } catch (error) {
      console.error("Error fetching candidate:", error);
      Toast(
        "error",
        error?.response?.data || "Failed to fetch candidate details",
      );
      throw error;
    }
  }, []);

  const fetchRecommendedJobs = useCallback(async (candidateId) => {
    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${RECOMMEND_JOBS}?candidate_id=${candidateId}`,
      );
      return response || [];
    } catch (error) {
      console.error("Error fetching recommended jobs:", error);
      return [];
    }
  }, []);

  const fetchJobDetailsById = useCallback(async (jobId) => {
    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${GET_JOB_DETAILS_BY_ID}?id=${jobId}`,
      );
      return response?.job || null;
    } catch (error) {
      console.error("Error fetching job details for ID", jobId, ":", error);
      return null;
    }
  }, []);

  const handleVendorCandidateCardView = useCallback(
    async (candidate) => {
      try {
        let candidateDetails;
        if (candidate.id) {
          candidateDetails = await fetchCandidateByIdForModal(candidate.id);
        } else {
          candidateDetails = candidate;
        }
        if (!candidateDetails) {
          console.error("Failed to fetch candidate details");
          Toast("error", "Failed to fetch candidate details");
          return;
        }
        const response = await fetchRecommendedJobs(candidateDetails.id);
        const jobIds = response || [];
        const jobDetailPromises = jobIds.map((job) =>
          fetchJobDetailsById(job.id || job.job_id),
        );
        const recommendedJobs = (await Promise.all(jobDetailPromises)).filter(
          Boolean,
        );
        setSelectedVendorCandidateForModal({
          ...candidateDetails,
          recommendedJobs,
        });
        setIsVendorCandidateModalOpen(true);
      } catch (error) {
        console.error("Error in handleVendorCandidateCardView:", error);
        Toast("error", "Error opening candidate details");
      }
    },
    [fetchCandidateByIdForModal, fetchRecommendedJobs, fetchJobDetailsById],
  );

  const closeVendorCandidateModal = () => {
    setIsVendorCandidateModalOpen(false);
    setSelectedVendorCandidateForModal(null);
    setVendorCandidateActiveTab(0);
    resetVendorCandidateEdit();
  };

  const resetVendorCandidateEdit = () => {
    if (originalVendorCandidateData) {
      setSelectedVendorCandidateForModal(originalVendorCandidateData);
    }
    setIsEditingVendorCandidate(false);
    setOriginalVendorCandidateData(null);
    setEditedVendorCandidateData(null);
    setVendorCandidateEmailError("");
    setVendorCandidateEmailTouched(false);
  };

  const handleVendorCandidateTabChange = (index) => {
    setVendorCandidateActiveTab(index);
  };

  const handleEditVendorCandidate = () => {
    const getValidExperience = (value) => {
      const num = Number(value);
      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };

    let experienceValue = "";
    const directExp = getValidExperience(
      selectedVendorCandidateForModal?.years_of_experience,
    );
    const parsedExp = getValidExperience(
      selectedVendorCandidateForModal?.parsedResumeData?.BasicInfo
        ?.YearsOfExperience,
    );

    if (directExp !== "") {
      experienceValue = directExp;
    } else if (parsedExp !== "") {
      experienceValue = parsedExp;
    }

    const candidateWithFormattedSkills = {
      ...selectedVendorCandidateForModal,
      name:
        selectedVendorCandidateForModal.name ||
        selectedVendorCandidateForModal.BasicInfo?.Name ||
        "",
      email:
        selectedVendorCandidateForModal.email ||
        selectedVendorCandidateForModal.BasicInfo?.Email ||
        "",
      secondaryEmail:
        selectedVendorCandidateForModal.secondaryEmail ||
        selectedVendorCandidateForModal.BasicInfo?.secondaryEmail ||
        "",
      phone:
        selectedVendorCandidateForModal.phone ||
        selectedVendorCandidateForModal.BasicInfo?.PhoneNumber ||
        "",
      secondaryPhone:
        selectedVendorCandidateForModal.secondaryPhone ||
        selectedVendorCandidateForModal.BasicInfo?.secondaryPhone ||
        "",
      city:
        selectedVendorCandidateForModal.city ||
        selectedVendorCandidateForModal.BasicInfo?.CurrentLocationCity ||
        "",
      state:
        selectedVendorCandidateForModal.state ||
        selectedVendorCandidateForModal.BasicInfo?.CurrentLocationState ||
        "",
      country:
        selectedVendorCandidateForModal.country ||
        selectedVendorCandidateForModal.BasicInfo?.CurrentLocationCountry ||
        "",
      skills: Array.isArray(selectedVendorCandidateForModal.skills)
        ? selectedVendorCandidateForModal.skills
        : selectedVendorCandidateForModal.parsedResumeData?.Skills || [],
      vendorID:
        selectedVendorCandidateForModal.vendorID ||
        selectedVendorCandidateForModal.BasicInfo?.vendorID ||
        "",
      designation:
        selectedVendorCandidateForModal.designation?.trim() ||
        selectedVendorCandidateForModal.parsedResumeData?.CareerHistory
          ?.JobDetails?.[0]?.JobTitle ||
        "",
      experience: experienceValue,
      lineAddress1:
        selectedVendorCandidateForModal.lineAddress1 ||
        selectedVendorCandidateForModal.BasicInfo?.LineAddress1 ||
        "",
      lineAddress2:
        selectedVendorCandidateForModal.lineAddress2 ||
        selectedVendorCandidateForModal.BasicInfo?.LineAddress2 ||
        "",
      zipCode:
        selectedVendorCandidateForModal.zipCode ||
        selectedVendorCandidateForModal.BasicInfo?.ZipCode ||
        "",
      preferredSalary:
        selectedVendorCandidateForModal.preferredSalary ||
        selectedVendorCandidateForModal.BasicInfo?.PreferredSalary ||
        "",
    };
    setOriginalVendorCandidateData({ ...selectedVendorCandidateForModal });
    setEditedVendorCandidateData(candidateWithFormattedSkills);
    setIsEditingVendorCandidate(true);
    setVendorCandidateEmailError("");
    setVendorCandidateEmailTouched(false);
  };

  const saveVendorCandidateChanges = async () => {
    if (!editedVendorCandidateData) {
      Toast("error", "No changes to save");
      return;
    }
    const email =
      editedVendorCandidateData.email !== undefined
        ? editedVendorCandidateData.email
        : selectedVendorCandidateForModal.email || "";
    if (!email.trim()) {
      setVendorCandidateEmailError("Email is required");
      setVendorCandidateEmailTouched(true);
      Toast("error", "Email is required");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setVendorCandidateEmailError("Invalid email format");
      setVendorCandidateEmailTouched(true);
      Toast("error", "Please fill all required fields correctly");
      return;
    }
    setVendorCandidateEmailError("");
    setVendorCandidateEmailTouched(false);
    try {
      const candidateData = { id: selectedVendorCandidateForModal.id };

      Object.keys(editedVendorCandidateData).forEach((key) => {
        const newValue = editedVendorCandidateData[key];
        const oldValue = selectedVendorCandidateForModal[key];

        const isChanged = Array.isArray(newValue)
          ? JSON.stringify(newValue) !== JSON.stringify(oldValue)
          : newValue !== oldValue;

        if (
          isChanged &&
          newValue !== undefined &&
          newValue !== null &&
          newValue !== ""
        ) {
          candidateData[key] = newValue;
        }
      });

      if (Object.keys(candidateData).length === 1) {
        Toast("error", "No changes to save");
        return;
      }

      Object.keys(candidateData).forEach((key) => {
        if (key === "preferredSalary") {
          candidateData[key] = Number(candidateData[key]);
        }
      });

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }

      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_CANDIDATE_DETAILS,
        candidateData,
      );
      if (response) {
        const updatedCandidate = {
          ...selectedVendorCandidateForModal,
          ...candidateData,
        };

        if (
          candidateData.vendorID !== undefined &&
          candidateData.vendorID !== selectedVendorCandidateForModal.vendorID
        ) {
          if (candidateData.vendorID) {
            try {
              const vendorResponse = await get(
                VYLABS_BASE_URL,
                `${GET_VENDOR_DETAILS_BY_ID}?id=${candidateData.vendorID}`,
              );
              updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
              setAssignedVendor(vendorResponse);
            } catch (err) {
              console.error("Error fetching vendor details:", err);
              updatedCandidate.vendorDetails = null;
              setAssignedVendor(null);
            }
          } else {
            updatedCandidate.vendorDetails = null;
            setAssignedVendor(null);
          }
        }
        setSelectedVendorCandidateForModal(updatedCandidate);
        setIsEditingVendorCandidate(false);
        setOriginalVendorCandidateData(null);
        setEditedVendorCandidateData(null);
        Toast("success", "Candidate updated successfully");
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      const backendMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null) ||
        error.message ||
        "Failed to update candidate";
      Toast("error", backendMessage);
    }
  };

  const handleVendorCandidateChange = (data) => {
    setEditedVendorCandidateData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const vendorPipelineColumns = useMemo(() => {
    const statusOrder = [
      "Emailed",
      "Call Back",
      "Interested",
      "Not Interested",
      "Onboarded",
    ];
    const statusDefinitions = filteredVendorsFromBuild[0]?.statuses || [];
    const statusColumns = statusOrder
      .map((systemKey) => {
        const statusDefinition = statusDefinitions.find(
          (stage) => stage.status === systemKey,
        );

        if (!statusDefinition) {
          return null;
        }
        return {
          name: toCapitalize(statusDefinition.status),
          width: "110px",
          cell: (row) => {
            const rowStage = row?.statuses?.find(
              (stage) => stage.status === systemKey,
            );

            return (
              <div
                className={
                  rowStage?.count > 0
                    ? "admin-dashboard-table-hover-number"
                    : "admin-dashboard-table-not-hover-number"
                }
                onClick={() =>
                  rowStage?.count > 0 &&
                  handleCallGetVendorPipelinesActions(row.id, rowStage.status)
                }
                style={{
                  cursor: rowStage?.count > 0 ? "pointer" : "auto",
                }}
              >
                {rowStage?.count ?? 0}
              </div>
            );
          },
        };
      })
      .filter(Boolean);

    return [
      {
        name: "Vendor",
        grow: 1.5,
        minWidth: "190px",
        sortable: true,
        sortField: "vendorName",
        cell: (row) => (
          <div className="admin-dashboard-job-cell">
            <CustomTooltip title={row.vendor} arrow>
              <div>{applyEllipsis(row.vendor, 15)}</div>
            </CustomTooltip>
          </div>
        ),
      },

      ...statusColumns,

      {
        name: "Actions",
        right: true,
        width: "100px",
        cell: (row) => (
          <div className="admin-dashboard-actions">
            <CustomTooltip title="View Vendor" arrow>
              <img
                src={EyeIcon}
                alt="view vendor"
                className="parser-table-action-icons"
                onClick={() => handleViewVendorDetails(row)}
              />
            </CustomTooltip>
          </div>
        ),
      },
    ];
  }, [filteredVendorsFromBuild, handleViewVendorDetails]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCallGetApplicationsByRecruiter = (
    user_id,
    stage,
    stageName,
    type = null,
  ) => {
    setApplicationSource("recruiter");
    setJobPipelineJobId(user_id);
    setJobPipelineStage(stage);
    setJobPipelingStageName(stageName);
    setRecruiterJobsType(type);
    getApplicationsByRecruiter(user_id, stage, 1, type);
  };

  const getApplicationsByRecruiter = async (
    user_id,
    stage,
    page = 1,
    type = recruiterJobsType,
  ) => {
    setJobAndRecruiterApplicationsLoading(true);
    try {
      const offset = (page - 1) * JOB_AND_RECRUITER_APPLICATIONS_LIMIT;
      const params = new URLSearchParams({
        user_id,
        filter_by: stage,
        limit: JOB_AND_RECRUITER_APPLICATIONS_LIMIT,
        offset,
      });
      // type is only valid together with filter_by=jobs
      if (stage === "jobs" && (type === "assigned" || type === "tracked")) {
        params.append("type", type);
      }
      if (selectedDashboardDate !== "all") {
        params.append("time", selectedDashboardDate);
      }

      const res = await get(
        VYLABS_BASE_URL,
        `${ADMIN_METRICS_RECRUITER_APPLICATIONS}?${params.toString()}`,
      );

      setShowCandidateDetails(true);
      setStoreApplicationsByJob(res?.candidates);
      if (res?.jobs) {
        setIsRecruiterApplicationsJobsClicked(true);
        setRecruiterApplicationsJobs(res?.jobs);
      } else {
        setIsRecruiterApplicationsJobsClicked(false);
        setRecruiterApplicationsJobs();
      }
      setStoreJobTitle(`by (${res?.firstName}`);
      setStoreJobTitleForTable(res?.firstName);
      setJobAndRecruiterApplicationsCurrentPage(page);
      setJobAndRecruiterApplicationsTotal(res?.total ?? 0);
      setJobAndRecruiterApplicationsLoading(false);
    } catch (error) {
      console.error("error in getApplicationsByRecruiter", error);
      Toast("error", error.response?.data);
      setJobAndRecruiterApplicationsLoading(false);
      setJobAndRecruiterApplicationsCurrentPage(1);
      setJobAndRecruiterApplicationsTotal(0);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleViewRecruiterDetails = (row) => {
    if (!row) return;
    setSelectedRecruiterDetails(row);
    setShowRecruiterDetails(true);
  };

  const allRecruiterPerformanceColumns = useMemo(
    () => [
      {
        columnKey: "recruiter",
        name: "Recruiter",
        grow: 1.5,
        minWidth: "210px",
        sortable: true,
        sortField: "name",
        cell: (row) => (
          <div className="admin-dashboard-recruiter-cell">
            <span className="admin-dashboard-recruiter-avatar">
              {row.name.slice(0, 2).toUpperCase()}
            </span>

            <CustomTooltip title={row.name} arrow>
              <div>{applyEllipsis(row.name, 10)}</div>
            </CustomTooltip>
          </div>
        ),
      },

      {
        columnKey: "jobs",
        name: "Jobs",
        width: "80px",
        sortable: true,
        sortField: "jobs",
        cell: (row) => (
          <div
            className={
              row.jobs > 0
                ? "admin-dashboard-table-hover-number"
                : "admin-dashboard-table-not-hover-number"
            }
            onClick={() =>
              row.jobs > 0 &&
              handleCallGetApplicationsByRecruiter(row.id, "jobs")
            }
            style={{
              cursor: row.jobs > 0 ? "pointer" : "auto",
            }}
          >
            {row.jobs}
          </div>
        ),
      },

      {
        columnKey: "reviewed",
        name: "Reviewed",
        width: "94px",
        sortable: true,
        sortField: "reviewed",
        cell: (row) => (
          <div
            className={
              row.reviewed > 0
                ? "admin-dashboard-table-hover-number"
                : "admin-dashboard-table-not-hover-number"
            }
            onClick={() =>
              row.reviewed > 0 &&
              handleCallGetApplicationsByRecruiter(
                row.id,
                "reviewed",
                "Reviewed",
              )
            }
            style={{
              cursor: row.reviewed > 0 ? "pointer" : "auto",
            }}
          >
            {row.reviewed}
          </div>
        ),
      },
      {
        columnKey: "assignedJobs",
        name: "Assigned Jobs",
        width: "120px",
        sortable: true,
        sortField: "assignedJobs",
        cell: (row) => (
          <div
            className={
              row.assignedJobs > 0
                ? "admin-dashboard-table-hover-number"
                : "admin-dashboard-table-not-hover-number"
            }
            onClick={() =>
              row.assignedJobs > 0 &&
              handleCallGetApplicationsByRecruiter(
                row.id,
                "jobs",
                "Assigned Jobs",
                "assigned",
              )
            }
            style={{
              cursor: row.assignedJobs > 0 ? "pointer" : "auto",
            }}
          >
            {row.assignedJobs}
          </div>
        ),
      },
      {
        columnKey: "trackedJobs",
        name: "Tracked Jobs",
        width: "120px",
        sortable: true,
        sortField: "trackedJobs",
        cell: (row) => (
          <div
            className={
              row.trackedJobs > 0
                ? "admin-dashboard-table-hover-number"
                : "admin-dashboard-table-not-hover-number"
            }
            onClick={() =>
              row.trackedJobs > 0 &&
              handleCallGetApplicationsByRecruiter(
                row.id,
                "jobs",
                "Tracked Jobs",
                "tracked",
              )
            }
            style={{
              cursor: row.trackedJobs > 0 ? "pointer" : "auto",
            }}
          >
            {row.trackedJobs}
          </div>
        ),
      },

      ...getStageDefinitionsFromRows(filteredRecruiterPerformance).map(
        (stageDefinition) => {
          const systemKey = stageDefinition.system_key;

          return {
            columnKey: systemKey,
            isCustomizable: true,
            name: toCapitalize(stageDefinition.name),
            width: "100px",
            sortable: true,
            sortField: `stages.${stageDefinition.system_key}`,

            cell: (row) => {
              const stage = row?.stages?.find(
                (stage) =>
                  (stage?.system_key || stage?.systemKey) === systemKey,
              );

              const count = stage?.count ?? 0;

              return (
                <div
                  className={
                    count > 0
                      ? "admin-dashboard-table-hover-number"
                      : "admin-dashboard-table-not-hover-number"
                  }
                  onClick={() =>
                    count > 0 &&
                    handleCallGetApplicationsByRecruiter(
                      row.id,
                      systemKey,
                      stage.name || stageDefinition.name,
                    )
                  }
                  style={{
                    cursor: count > 0 ? "pointer" : "auto",
                  }}
                >
                  {count}
                </div>
              );
            },
          };
        },
      ),

      {
        columnKey: "actions",
        name: "Actions",
        right: true,
        width: "100px",
        cell: (row) => (
          <div className="admin-dashboard-actions">
            <CustomTooltip title="View Recruiter" arrow>
              <img
                src={EyeIcon}
                alt="view recruiter"
                className="parser-table-action-icons"
                onClick={() => handleViewRecruiterDetails(row)}
              />
            </CustomTooltip>
          </div>
        ),
      },
    ],
    [
      filteredRecruiterPerformance,
      handleCallGetApplicationsByRecruiter,
      handleViewRecruiterDetails,
    ],
  );

  const recruiterPerformanceColumns = useMemo(
    () =>
      allRecruiterPerformanceColumns.filter(
        (column) =>
          !column.isCustomizable ||
          visibleRecruiterColumns.includes(column.columnKey),
      ),
    [allRecruiterPerformanceColumns, visibleRecruiterColumns],
  );

  const customizableRecruiterPerformanceColumns = useMemo(
    () =>
      allRecruiterPerformanceColumns.filter((column) => column.isCustomizable),
    [allRecruiterPerformanceColumns],
  );

  const recruiterPerformanceColumnSignature =
    customizableRecruiterPerformanceColumns
      .map((column) => `${column.columnKey}:${column.name}`)
      .join("|");

  const recruiterColumnsForCustomize = useMemo(
    () =>
      getCustomizeColumnsFromTableColumns(
        customizableRecruiterPerformanceColumns,
      ),
    [recruiterPerformanceColumnSignature],
  );

  useEffect(() => {
    const allColumnKeys = Object.keys(recruiterColumnsForCustomize);
    const savedColumnKeys = getSavedColumnKeys(
      savedRecruiterPerformanceColumns,
      allColumnKeys,
    );

    const nextVisibleColumns = savedRecruiterPerformanceColumnViewId
      ? savedColumnKeys
      : allColumnKeys;
    setVisibleRecruiterColumns(nextVisibleColumns);
    setDraftVisibleRecruiterColumns(nextVisibleColumns);
  }, [
    recruiterColumnsForCustomize,
    savedRecruiterPerformanceColumnViewId,
    savedRecruiterPerformanceColumns,
  ]);

  const toggleRecruiterColumnVisibility = (columnKey) => {
    setDraftVisibleRecruiterColumns((prev) => {
      if (prev.includes(columnKey)) {
        return prev.filter((key) => key !== columnKey);
      }

      return [...prev, columnKey];
    });
  };

  const toggleRecruiterDropdown = () => {
    setRecruiterDropdownOpen((prev) => {
      if (!prev) {
        setDraftVisibleRecruiterColumns(visibleRecruiterColumns);
      }

      return !prev;
    });
  };

  const handleRecruiterSelectAll = () => {
    setDraftVisibleRecruiterColumns(Object.keys(recruiterColumnsForCustomize));
  };

  const handleRecruiterSelectNone = () => {
    setDraftVisibleRecruiterColumns([]);
  };

  useEffect(() => {
    if (!recruiterDropdownOpen) return undefined;

    function handleClickOutside(event) {
      if (
        recruiterDropdownRef.current &&
        !recruiterDropdownRef.current.contains(event.target) &&
        recruiterDropdownBtnRef.current &&
        !recruiterDropdownBtnRef.current.contains(event.target)
      ) {
        setRecruiterDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [recruiterDropdownOpen]);

  const handleGetRecruiterPerformanceColumnSavedViews =
    useCallback(async () => {
      try {
        const savedViewRes = await get(
          VYLABS_BASE_URL,
          `${GET_SAVED_VIEWS}?scope=${RECRUITER_PERFORMANCE_COLUMN_SAVED_VIEW_SCOPE}`,
        );
        const views = savedViewRes?.views || [];
        const defaultView = views.find((item) => item.isDefault);

        setSavedRecruiterPerformanceColumns(defaultView?.filters || []);
        setSavedRecruiterPerformanceColumnViewId(defaultView?.id || null);
      } catch (error) {
        console.error(
          "error in handleGetRecruiterPerformanceColumnSavedViews",
          error,
        );
        setSavedRecruiterPerformanceColumns([]);
        setSavedRecruiterPerformanceColumnViewId(null);
      }
    }, []);

  useEffect(() => {
    handleGetRecruiterPerformanceColumnSavedViews();
  }, [handleGetRecruiterPerformanceColumnSavedViews]);

  const getSelectedRecruiterPerformanceColumns = () =>
    getSelectedCustomizeColumns(
      recruiterColumnsForCustomize,
      draftVisibleRecruiterColumns,
    );

  const handleCreateRecruiterSaveView = async () => {
    try {
      const res = await post(VYLABS_BASE_URL, CREATE_SAVED_VIEWS, {
        filters: getSelectedRecruiterPerformanceColumns(),
        name: "Admin Recruiter Performance Columns",
        scope: RECRUITER_PERFORMANCE_COLUMN_SAVED_VIEW_SCOPE,
        isDefault: true,
      });

      if (res?.view?.id) {
        Toast("success", "Created Successfully!");
        setVisibleRecruiterColumns(draftVisibleRecruiterColumns);
        handleGetRecruiterPerformanceColumnSavedViews();
        setRecruiterDropdownOpen(false);
      }
    } catch (error) {
      console.error("error in handleCreateRecruiterSaveView", error);
      Toast(
        "error",
        error?.response?.data || "Failed to create customized columns",
      );
    }
  };

  const handleUpdateRecruiterSaveView = async () => {
    try {
      if (!savedRecruiterPerformanceColumnViewId) {
        handleCreateRecruiterSaveView();
        return;
      }

      const res = await patch(
        VYLABS_BASE_URL,
        `${UPDATE_SAVED_VIEWS}/${savedRecruiterPerformanceColumnViewId}`,
        { filters: getSelectedRecruiterPerformanceColumns() },
      );

      if (res?.view?.id) {
        Toast("success", "Updated Successfully!");
        setVisibleRecruiterColumns(draftVisibleRecruiterColumns);
        handleGetRecruiterPerformanceColumnSavedViews();
        setRecruiterDropdownOpen(false);
      }
    } catch (error) {
      console.error("error in handleUpdateRecruiterSaveView", error);
      Toast(
        "error",
        error?.response?.data || "Failed to update customized columns",
      );
    }
  };

  const fetchOverallCandidatesCount = async () => {
    try {
      const response = await get(VYLABS_BASE_URL, GET_OVERALL_CANDIDATES_COUNT);
      if (!isMountedRef.current) return;
      setOverallCounts((prev) => ({
        ...prev,
        candidates: getFirstValue(response, ["count"], 0),
      }));
    } catch (error) {
      console.error("fetchOverallCandidatesCount error:", error);
    }
  };

  const fetchOverallClientsCount = async () => {
    try {
      const response = await get(VYLABS_BASE_URL, GET_OVERALL_CLIENTS_COUNT);
      if (!isMountedRef.current) return;
      setOverallCounts((prev) => ({
        ...prev,
        clients: getFirstValue(response, ["count"], 0),
      }));
    } catch (error) {
      console.error("fetchOverallClientsCount error:", error);
    }
  };

  const fetchOverallActiveJobsCount = async () => {
    try {
      const response = await get(
        VYLABS_BASE_URL,
        GET_OVERALL_ACTIVE_JOBS_COUNT,
      );
      if (!isMountedRef.current) return;
      setOverallCounts((prev) => ({
        ...prev,
        activeJobs: getFirstValue(response, ["count"], 0),
      }));
    } catch (error) {
      console.error("fetchOverallActiveJobsCount error:", error);
    }
  };

  useEffect(() => {
    fetchOverallActiveJobsCount();
    fetchOverallCandidatesCount();
    fetchOverallClientsCount();
  }, []);
  const fetchOverviewData = useCallback(async (payload) => {
    try {
      const overview = await post(
        VYLABS_BASE_URL,
        ADMIN_RECRUITMENT_OVERVIEW,
        payload,
      );
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({ ...prev, overview: overview ?? null }));
    } catch (error) {
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({ ...prev, overview: null }));
    } finally {
      if (isMountedRef.current) setIsDashboardLoading(false);
    }
  }, []);

  const fetchJobPipelineData = useCallback(async (payload, page) => {
    if (isMountedRef.current) setIsJobPipelineLoading(true);
    try {
      const jobPipeline = await post(VYLABS_BASE_URL, ADMIN_JOB_PIPELINE, {
        ...payload,
        limit: JOB_PIPELINE_LIMIT,
        page,
      });
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({
        ...prev,
        jobPipeline: jobPipeline ?? null,
      }));
      const jobRows = getFirstArray(jobPipeline, JOB_PIPELINE_ROW_KEYS);
      const jobRow = jobRows[0] || {};
      const jobStageOptions = getStageDefinitionsFromRows(jobRows).map(
        (stage) => ({
          title: stage.name,
          value: `stages.${stage.system_key}`,
        }),
      );
      const jobFieldOptions = Object.keys(jobRow)
        .filter((key) => key !== "id" && key !== "stages")
        .map((key) => ({
          title: key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (char) => char.toUpperCase())
            .replace(/_/g, " "),
          value: key,
        }));
      setJobPipelineAPIRes([...jobFieldOptions, ...jobStageOptions]);
      setJobPipelineTotalPages(jobPipeline?.total ?? 0);
      setJobPipelineCurrentPage(page);
    } catch (error) {
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({ ...prev, jobPipeline: null }));
      setJobPipelineTotalPages(0);
      setJobPipelineCurrentPage(1);
    } finally {
      if (isMountedRef.current) setIsJobPipelineLoading(false);
    }
  }, []);

  const fetchPerformanceData = useCallback(async (payload, page) => {
    if (isMountedRef.current) setIsRecruitmentPerformanceLoading(true);
    try {
      const performance = await post(
        VYLABS_BASE_URL,
        ADMIN_RECRUITMENT_PERFORMANCE,
        {
          ...payload,
          limit: RECRUITER_PERFORMANCE_LIMIT,
          page,
        },
      );
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({
        ...prev,
        performance: performance ?? null,
      }));
      setRecruiterPerformanceTotalPages(performance?.total ?? 0);
      setRecruiterPerformanceCurrentPage(page);
    } catch (error) {
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({ ...prev, performance: null }));
      setRecruiterPerformanceTotalPages(0);
      setRecruiterPerformanceCurrentPage(1);
    } finally {
      if (isMountedRef.current) setIsRecruitmentPerformanceLoading(false);
    }
  }, []);

  const fetchTeamWorkloadData = useCallback(
    async (payload, page = 1, append = false) => {
      if (teamWorkloadRequestInFlightRef.current) return;

      teamWorkloadRequestInFlightRef.current = true;
      if (isMountedRef.current) {
        if (append) {
          setIsTeamWorkloadFetchingMore(true);
        } else {
          setIsTeamWorkloadLoading(true);
          setTeamWorkloadHasMore(true);
          setTeamWorkloadCurrentPage(0);
        }
      }

      try {
        const workload = await post(VYLABS_BASE_URL, ADMIN_TEAM_WORKLOAD, {
          ...payload,
          limit: TEAM_WORKLOAD_LIMIT,
          page,
        });
        if (!isMountedRef.current) return;
        const workloadRows = getFirstArray(workload, [
          "workload",
          "team_workload",
          "teamWorkload",
          "items",
          "results",
          "rows",
        ]);
        const currentPage = Number.isFinite(Number(workload?.currentPage))
          ? Number(workload.currentPage)
          : page;
        const totalPages = Number(workload?.totalPages) || 0;

        setDashboardData((prev) => {
          if (!append) {
            return { ...prev, workload: workload ?? null };
          }

          const previousRows = getFirstArray(prev.workload, [
            "workload",
            "team_workload",
            "teamWorkload",
            "items",
            "results",
            "rows",
          ]);

          return {
            ...prev,
            workload: {
              ...(workload ?? {}),
              workload: [...previousRows, ...workloadRows],
            },
          };
        });
        setTeamWorkloadCurrentPage(currentPage);
        setTeamWorkloadHasMore(
          totalPages
            ? currentPage + 1 < totalPages
            : workloadRows.length >= TEAM_WORKLOAD_LIMIT,
        );
      } catch (error) {
        if (!isMountedRef.current) return;
        if (!append) {
          setDashboardData((prev) => ({ ...prev, workload: null }));
        }
        setTeamWorkloadHasMore(false);
      } finally {
        teamWorkloadRequestInFlightRef.current = false;
        if (isMountedRef.current) {
          setIsTeamWorkloadLoading(false);
          setIsTeamWorkloadFetchingMore(false);
        }
      }
    },
    [],
  );

  const fetchRecruitmentFunnelData = useCallback(async (payload, page = 1) => {
    try {
      const funnel = await post(VYLABS_BASE_URL, ADMIN_RECRUITMENT_FUNNEL, {
        ...payload,
        limit: RECRUITMENT_FUNNEL_LIMIT,
        page: page,
      });
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({ ...prev, funnel: funnel ?? null }));
    } catch (error) {
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({ ...prev, funnel: null }));
    } finally {
      if (isMountedRef.current) setIsRecruitmentFunnelLoading(false);
    }
  }, []);

  const vendorPipelineMetrics = useCallback(async (payload, page) => {
    if (isMountedRef.current) setIsVendorPipelineLoading(true);
    try {
      const vendorPipeline = await post(
        VYLABS_BASE_URL,
        VENDOR_PIPELINE_METRICS,
        {
          ...payload,
          limit: VENDOR_PIPELINE_METRICS_LIMIT,
          page,
        },
      );
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({
        ...prev,
        vendorPipeline: vendorPipeline ?? null,
      }));
      setVendorPipelineTotalPages(vendorPipeline?.total ?? 0);
      setVendorPipelineCurrentPage(page);
    } catch (error) {
      console.error("error in vendorPipelineMetrics", error);
      if (!isMountedRef.current) return;
      setDashboardData((prev) => ({ ...prev, vendorPipeline: null }));
      setVendorPipelineTotalPages(0);
      setVendorPipelineCurrentPage(1);
    } finally {
      if (isMountedRef.current) setIsVendorPipelineLoading(false);
    }
  }, []);

  const handleJobPipelineSort = () => {
    if (!selectedJobPipelineColumn) {
      Toast("error", "Please select a sort by");
      return;
    }

    const sortOrder =
      jobPipelineSort?.sortBy === selectedJobPipelineColumn &&
      jobPipelineSort?.sortOrder === "asc"
        ? "desc"
        : "asc";

    const sort = {
      sortBy: selectedJobPipelineColumn,
      sortOrder,
    };

    skipJobPipelinePageChangeRef.current = true;

    setJobPipelineSort(sort);

    fetchAdminDashboardData(1, false, true, false, fromDate, toDate, sort);
  };

  const handleRecruiterPerformanceSort = () => {
    if (!selectedRecruiterPerformanceColumn) {
      Toast("error", "Please select a sort by");
      return;
    }

    const sortOrder =
      recruiterPerformanceSort?.sortBy === selectedRecruiterPerformanceColumn &&
      recruiterPerformanceSort?.sortOrder === "asc"
        ? "desc"
        : "asc";

    const sort = {
      sortBy: selectedRecruiterPerformanceColumn,
      sortOrder,
    };

    skipRecruiterPerformancePageChangeRef.current = true;

    setRecruiterPerformanceSort(sort);

    fetchAdminDashboardData(1, true, false, false, fromDate, toDate, sort);
  };

  const handleVendorPipelineSort = () => {
    if (!selectedVendorPipelineColumn) {
      Toast("error", "Please select a sort by");
      return;
    }
    const sortOrder =
      vendorPipelineSort?.sortBy === selectedVendorPipelineColumn &&
      vendorPipelineSort.sortOrder === "asc"
        ? "desc"
        : "asc";
    const sort = {
      sortBy: selectedVendorPipelineColumn,
      sortOrder,
    };
    console.log("sort ven", sort);
    skipVendorPipelinePageChangeRef.current = true;
    setVendorPipelineSort(sort);
    fetchAdminDashboardData(1, false, false, true, fromDate, toDate, sort);
  };

  const formatDateForPayload = (date) => {
    const d = new Date(date);

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "";

    return new Date(date)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(",", "");
  };

  const getDashboardDateRange = useCallback(() => {
    const today = new Date();
    const startOfDay = (date) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (selectedDashboardDate === "custom") {
      return fromDate && toDate
        ? {
            start: new Date(fromDate),
            end: new Date(toDate),
          }
        : null;
    }

    if (selectedDashboardDate === "yesterday") {
      const yesterday = startOfDay(today);
      yesterday.setDate(yesterday.getDate() - 1);

      return { start: yesterday, end: yesterday };
    }

    if (selectedDashboardDate === "this_week") {
      const start = startOfDay(today);
      start.setDate(start.getDate() - start.getDay());

      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      return { start, end };
    }

    if (selectedDashboardDate === "this_month") {
      return {
        start: new Date(today.getFullYear(), today.getMonth(), 1),
        end: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    }

    if (selectedDashboardDate === "all") {
      return null;
    }

    const currentDay = startOfDay(today);
    return { start: currentDay, end: currentDay };
  }, [fromDate, selectedDashboardDate, toDate]);

  const dashboardDateRangeLabel = useMemo(() => {
    const range = getDashboardDateRange();

    if (!range) {
      return selectedDashboardDate === "custom"
        ? "Select date range"
        : "All time";
    }

    const startLabel = formatDateForDisplay(range.start);
    const endLabel = formatDateForDisplay(range.end);

    return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
  }, [getDashboardDateRange, selectedDashboardDate]);

  const fetchSavedViews = useCallback(async () => {
    try {
      setIsLoadingSavedViews(true);
      const response = await get(
        VYLABS_BASE_URL,
        `${GET_SAVED_VIEWS}?scope=admin_dashboard`,
      );
      if (!isMountedRef.current) return [];
      const views = Array.isArray(response?.views) ? response.views : [];
      setSavedViews(views);
      return views;
    } catch (error) {
      console.error("Admin dashboard fetchSavedViews error:", error);
      if (isMountedRef.current) setSavedViews([]);
      return [];
    } finally {
      if (isMountedRef.current) setIsLoadingSavedViews(false);
    }
  }, []);

  const savedViewOptions = useMemo(
    () =>
      savedViews
        .filter((view) => view?.id && view?.name)
        .map((view) => ({ label: view.name, value: view.id })),
    [savedViews],
  );

  const applySavedViewFilters = useCallback(
    (filters = {}) => {
      const validTimes = DASHBOARD_DATE_FILTER_OPTIONS.map(
        (option) => option.value,
      ).filter((value) => value !== "custom");
      const viewFrom = parseDateOnly(filters.fromDate);
      const viewTo = parseDateOnly(filters.toDate);

      if (viewFrom && viewTo) {
        setSelectedDashboardDate("custom");
        setFromDate(viewFrom);
        setToDate(viewTo);
        customFilterAppliedRef.current = true;
        previousDashboardDateRef.current = "custom";
      } else {
        const time = validTimes.includes(filters.time) ? filters.time : "all";
        setSelectedDashboardDate(time);
        setFromDate(null);
        setToDate(null);
        previousDashboardDateRef.current = time;
      }

      const clientId = getSavedViewClientId(filters);
      const clientName = getSavedViewClientName(filters);
      const clientOption =
        isValidDashboardFilterValue(clientId) && clientName
          ? { label: clientName, value: clientId }
          : null;

      setSelectedClient(
        isValidDashboardFilterValue(clientId) ? clientId : "All",
      );
      setSelectedClientFallbackOption(clientOption);
      setSelectedRecruiter(normalizeRecruiterIds(filters.userIds));

      if (isValidDashboardFilterValue(filters.vendorId)) {
        setSelectedVendor(
          vendorList.find(
            (vendor) => String(vendor.value) === String(filters.vendorId),
          ) || { label: "Vendor", value: filters.vendorId },
        );
      } else {
        setSelectedVendor(null);
      }
      setDashboardRefreshKey((prev) => prev + 1);
    },
    [vendorList],
  );

  const handleSavedViewChange = useCallback(
    (option) => {
      if (!option) {
        setSelectedSavedViewId(null);
        applySavedViewFilters({ time: "today" });
        return;
      }

      const view = savedViews.find((item) => item.id === option.value);
      if (!view) return;

      setSelectedSavedViewId(view.id);
      applySavedViewFilters(view.filters || {});
    },
    [applySavedViewFilters, savedViews],
  );
  const activeSavedViewOption = useMemo(() => {
    const view = savedViews.find((item) => item.id === selectedSavedViewId);
    if (!view) return null;

    const filters = view.filters || {};
    const viewClientId = getSavedViewClientId(filters);
    const viewHasRange = filters.fromDate && filters.toDate;
    const timeMatches = viewHasRange
      ? selectedDashboardDate === "custom" &&
        fromDate &&
        toDate &&
        formatDateForPayload(fromDate) ===
          String(filters.fromDate).slice(0, 10) &&
        formatDateForPayload(toDate) === String(filters.toDate).slice(0, 10)
      : selectedDashboardDate === (filters.time || "all");
    const clientMatches =
      String(selectedClient) ===
      String(isValidDashboardFilterValue(viewClientId) ? viewClientId : "All");
    const recruiterMatches = areSameIdLists(
      selectedRecruiter,
      normalizeRecruiterIds(filters.userIds),
    );
    const vendorMatches =
      String(selectedVendor?.value ?? "") === String(filters.vendorId ?? "");

    return timeMatches && clientMatches && recruiterMatches && vendorMatches
      ? { label: view.name, value: view.id }
      : null;
  }, [
    savedViews,
    selectedSavedViewId,
    selectedDashboardDate,
    fromDate,
    toDate,
    selectedClient,
    selectedRecruiter,
    selectedVendor,
  ]);

  useEffect(() => {
    fetchSavedViews().then((views) => {
      const defaultView = views.find((view) => view?.isDefault);
      if (defaultView && !hasAppliedDefaultViewRef.current) {
        hasAppliedDefaultViewRef.current = true;
        setSelectedSavedViewId(defaultView.id);
        applySavedViewFilters(defaultView.filters || {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSavedViews]);

  const fetchAdminDashboardData = useCallback(
    (
      page = 1,
      isRecruiterPerformance = false,
      isJobPipeline = false,
      isVendorPipeline = false,
      fromDate,
      toDate,
      sortOverride,
    ) => {
      const dashboardPayload = {
        userIds: selectedRecruiter,
      };

      if (selectedDashboardDate !== "all") {
        if (selectedDashboardDate === "custom" && fromDate && toDate) {
          dashboardPayload.fromDate = formatDateForPayload(fromDate);
          dashboardPayload.toDate = formatDateForPayload(toDate);
        } else {
          dashboardPayload.time = selectedDashboardDate;
        }
      }

      if (isValidDashboardFilterValue(selectedClient)) {
        dashboardPayload.clientId = selectedClient;
      }

      const vendorPipelineSortPayload = sortOverride || vendorPipelineSort;
      const vendorPipelinePayload = dashboardPayload;
      if (
        vendorPipelineSortPayload?.sortBy &&
        vendorPipelineSortPayload?.sortOrder
      ) {
        vendorPipelinePayload.sortBy = vendorPipelineSortPayload.sortBy;
        vendorPipelinePayload.sortOrder = vendorPipelineSortPayload.sortOrder;
      }
      if (isValidDashboardFilterValue(selectedVendor?.value)) {
        vendorPipelinePayload.vendorId = selectedVendor.value;
      }

      const activeSort = sortOverride || jobPipelineSort;
      const jobPipelinePayload =
        activeSort?.sortBy && activeSort?.sortOrder
          ? {
              ...dashboardPayload,
              sortBy: activeSort.sortBy,
              sortOrder: activeSort.sortOrder,
            }
          : { ...dashboardPayload };
      const performanceSort = sortOverride || recruiterPerformanceSort;
      const recruiterPerformancePayload =
        performanceSort?.sortBy && performanceSort?.sortOrder
          ? {
              ...dashboardPayload,
              sortBy: performanceSort.sortBy,
              sortOrder: performanceSort.sortOrder,
            }
          : { ...dashboardPayload };

      teamWorkloadPayloadRef.current = dashboardPayload;

      if (isRecruiterPerformance) {
        fetchPerformanceData(recruiterPerformancePayload, page);
        return;
      }

      if (isJobPipeline) {
        fetchJobPipelineData(jobPipelinePayload, page);
        return;
      }

      if (isVendorPipeline && isVendorFeatureEnabled) {
        vendorPipelineMetrics(vendorPipelinePayload, page);
        return;
      }

      setIsDashboardLoading(true);
      setIsJobPipelineLoading(true);
      setIsRecruitmentPerformanceLoading(true);
      setIsTeamWorkloadLoading(true);
      setIsRecruitmentFunnelLoading(true);
      fetchOverviewData(dashboardPayload);
      getAdminDashboardFollowups(dashboardPayload);
      fetchJobPipelineData(jobPipelinePayload, page);
      fetchPerformanceData(recruiterPerformancePayload, page);
      if (isVendorFeatureEnabled) {
        vendorPipelineMetrics(vendorPipelinePayload, page);
      }
      fetchTeamWorkloadData(dashboardPayload, 1, false);
      fetchRecruitmentFunnelData(dashboardPayload);
    },
    [
      selectedRecruiter,
      selectedVendor,
      selectedDashboardDate,
      selectedClient,
      jobPipelineSort,
      recruiterPerformanceSort,
      vendorPipelineSort,
      fetchOverviewData,
      getAdminDashboardFollowups,
      fetchJobPipelineData,
      fetchPerformanceData,
      vendorPipelineMetrics,
      fetchTeamWorkloadData,
      fetchRecruitmentFunnelData,
    ],
  );

  useEffect(() => {
    isMountedRef.current = true;
    if (selectedDashboardDate === "custom" && isFilterPopupOpen) {
      return;
    }
    fetchAdminDashboardData(1, false, false, false, fromDate, toDate);
    return () => {
      isMountedRef.current = false;
    };
  }, [
    dashboardRefreshKey,
    selectedClient,
    selectedDashboardDate,
    selectedRecruiter,
  ]);

  useEffect(() => {
    if (!isVendorFeatureEnabled) return;

    fetchAdminDashboardData(1, false, false, true);
  }, [selectedVendor, isVendorFeatureEnabled]);

  useEffect(() => {
    if (!isFilterPopupOpen || selectedDashboardDate !== "custom") {
      setCustomDatePopupStyle(undefined);
      return undefined;
    }

    const updatePosition = () => updateCustomDatePopupPosition();
    const frameId = requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isFilterPopupOpen, selectedDashboardDate, updateCustomDatePopupPosition]);

  const hasSavedViewEligibleFilters =
    isValidDashboardFilterValue(selectedClient) || selectedRecruiter.length > 0;

  const isCreateViewButtonActive =
    hasSavedViewEligibleFilters && !activeSavedViewOption;

  const selectedClientName = useMemo(() => {
    if (!isValidDashboardFilterValue(selectedClient)) return "";

    return selectedClientOption?.label || "";
  }, [selectedClient, selectedClientOption]);

  const selectedRecruiterNames = useMemo(
    () =>
      recruiterMultiSelectOptions
        .filter((recruiter) =>
          selectedRecruiter.some(
            (id) => String(id) === String(recruiter.value),
          ),
        )
        .map((recruiter) => recruiter.label),
    [recruiterMultiSelectOptions, selectedRecruiter],
  );

  const handleCreateViewOpen = useCallback(() => {
    if (!isCreateViewButtonActive) return;
    setViewOpen(true);
  }, [isCreateViewButtonActive]);

  const handleCreateViewClose = useCallback(() => {
    setViewOpen(false);
    setSavedViewName("");
    setSavedViewIsDefault(false);
  }, []);

  const createFilter = useCallback(async () => {
    const trimmedName = savedViewName.trim();

    if (!trimmedName) {
      Toast("error", "Please enter saved view name");
      return;
    }

    if (!isCreateViewButtonActive) {
      Toast("error", "Please select at least one filter");
      return;
    }

    const filters = {};

    if (selectedDashboardDate === "custom") {
      if (fromDate && toDate) {
        filters.fromDate = formatDateForPayload(fromDate);
        filters.toDate = formatDateForPayload(toDate);
      }
    } else if (selectedDashboardDate !== "all") {
      filters.time = selectedDashboardDate;
    }

    if (isValidDashboardFilterValue(selectedClient)) {
      filters.clientId = selectedClient;
      if (selectedClientOption?.label) {
        filters.clientName = selectedClientOption.label;
      }
    }

    if (selectedRecruiter.length > 0) {
      filters.userIds = selectedRecruiter;
    }

    if (isValidDashboardFilterValue(selectedVendor?.value)) {
      filters.vendorId = selectedVendor.value;
    }

    const payload = {
      filters,
      name: trimmedName,
      isDefault: savedViewIsDefault,
      scope: "admin_dashboard",
    };

    try {
      setIsCreatingSavedView(true);
      const response = await post(VYLABS_BASE_URL, CREATE_SAVED_VIEWS, payload);
      Toast("success", "Saved view created successfully");
      const views = await fetchSavedViews();
      const createdViewId = response?.view?.id || response?.id;
      const createdView =
        views.find((view) => String(view.id) === String(createdViewId)) ||
        views.find((view) => view.name === trimmedName);
      if (createdView?.id) {
        setSelectedSavedViewId(createdView.id);
      }
      handleCreateViewClose();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to create saved view";
      Toast("error", errorMessage);
    } finally {
      setIsCreatingSavedView(false);
    }
  }, [
    fetchSavedViews,
    fromDate,
    handleCreateViewClose,
    isCreateViewButtonActive,
    savedViewName,
    savedViewIsDefault,
    selectedClient,
    selectedClientOption,
    selectedDashboardDate,
    selectedRecruiter,
    selectedVendor,
    toDate,
  ]);

  const handleSavedViewDeleteClose = useCallback(() => {
    if (isDeletingSavedView) return;
    setIsSavedViewDeleteDialogOpen(false);
    setSavedViewPendingDelete(null);
  }, [isDeletingSavedView]);

  const handleDeleteViews = useCallback(
    async (savedViewId = selectedSavedViewId) => {
      if (!savedViewId) {
        Toast("error", "Please select a saved filter to delete");
        return;
      }

      try {
        setIsDeletingSavedView(true);
        await deleteRequest(
          VYLABS_BASE_URL,
          `${DELETE_SAVED_VIEWS}/${encodeURIComponent(savedViewId)}`,
        );
        Toast("success", "Saved filter deleted successfully");
        if (String(savedViewId) === String(selectedSavedViewId)) {
          setSelectedSavedViewId(null);
        }
        await fetchSavedViews();
        setIsSavedViewDeleteDialogOpen(false);
        setSavedViewPendingDelete(null);
      } catch (error) {
        console.error("Error deleting saved view", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to delete saved filter";
        Toast("error", errorMessage);
      } finally {
        setIsDeletingSavedView(false);
      }
    },
    [fetchSavedViews, selectedSavedViewId],
  );

  useEffect(() => {
    fetchAllMembers();
    fetchAllClientsList();
    fetchTenantDetails();
  }, [fetchAllClientsList, fetchAllMembers, fetchTenantDetails]);

  useEffect(() => {
    const fetchNotificationsForBadge = async () => {
      try {
        const response = await get(
          VYLABS_BASE_URL,
          `${NOTIFICATIONS_GET}?unread_only=true`,
        );
        if (response) {
          setNotificationsState({
            notifications: response?.notifications || [],
            unread_count: response?.unread_count || 0,
          });
        }
      } catch (error) {
        console.error("Admin dashboard notifications fetch error:", error);
      }
    };

    const token = localStorage.getItem("token");
    const isPublicRoute = [
      "/",
      "/login",
      "/signup",
      "/admin-signup",
      "/member-signup",
      "/forgot-password",
      "/reset-password",
    ].includes(window.location.pathname);
    if (!token || !userDetails?.id || isPublicRoute) return undefined;

    notificationUnmountedRef.current = false;
    fetchNotificationsForBadge();
    notificationConnectTimeoutRef.current = setTimeout(() => {
      initializeNotificationWebSocket();
    }, 250);

    return () => {
      notificationUnmountedRef.current = true;
      closeNotificationWebSocket();
    };
  }, [
    closeNotificationWebSocket,
    initializeNotificationWebSocket,
    setNotificationsState,
    userDetails?.id,
  ]);

  const getVendorPipelinesActions = async (id, status, page = 1) => {
    setVendorPipelineActionsLoading(true);

    try {
      const offset = (page - 1) * JOB_AND_RECRUITER_APPLICATIONS_LIMIT;
      const params = new URLSearchParams({
        vendor_id: id,
        status,
        limit: JOB_AND_RECRUITER_APPLICATIONS_LIMIT,
        offset,
      });

      const res = await get(
        VYLABS_BASE_URL,
        `${VENDOR_PIPELINES_ACTIONS}?${params.toString()}`,
      );

      if (!isMountedRef.current) return;

      setVendorPipelineActions(Array.isArray(res?.actions) ? res.actions : []);
      setVendorPipelineActionsTotal(res?.total ?? 0);
      setJobAndRecruiterApplicationsCurrentPage(page);
      setVendorPipelineActionVendor(res?.vendorName || "");
      setVendorPipelineActionStatus(res?.[0]?.newStatus || status || "");
      setShowCandidateDetails(true);
    } catch (error) {
      console.error("error in getVendorPipelinesActions", error);
      if (!isMountedRef.current) return;
      setVendorPipelineActions([]);
      setVendorPipelineActionsTotal(0);
      Toast(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch vendor pipeline actions",
      );
    } finally {
      if (isMountedRef.current) {
        setVendorPipelineActionsLoading(false);
      }
    }
  };

  const getApplicationsByJob = async (job_id, stage, page = 1) => {
    setGetApplicationsByJobCalled(true);
    setJobAndRecruiterApplicationsLoading(true);
    try {
      const offset = (page - 1) * JOB_AND_RECRUITER_APPLICATIONS_LIMIT;
      const params = new URLSearchParams({
        job_id,
        filter_by: stage,
        limit: JOB_AND_RECRUITER_APPLICATIONS_LIMIT,
        offset,
      });

      if (selectedDashboardDate !== "all") {
        params.append("time", selectedDashboardDate);
      }

      const res = await get(
        VYLABS_BASE_URL,
        `${ADMIN_METRICS_JOB_APPLICATIONS}?${params.toString()}`,
      );
      const isRecruiterMetric = [
        "recruiters",
        "assigned_recruiters",
        "tracked_recruiters",
      ].includes(stage);

      setShowCandidateDetails(true);
      setStoreApplicationsByJob(
        isRecruiterMetric
          ? Array.isArray(res?.recruiters)
            ? res.recruiters
            : []
          : Array.isArray(res?.candidates)
            ? res.candidates
            : [],
      );
      setStoreJobTitle(`for (${res?.jobTitle || ""}`);
      setStoreJobTitleForTable(res?.jobTitle || "");
      setJobAndRecruiterApplicationsCurrentPage(res?.currentPage ?? page);
      setJobAndRecruiterApplicationsTotal(res?.total ?? 0);
      setJobAndRecruiterApplicationsLoading(false);
    } catch (error) {
      console.error("error in getApplicationsByJob", error);
      Toast("error", error.response?.data);
      setJobAndRecruiterApplicationsLoading(false);
      setJobAndRecruiterApplicationsCurrentPage(1);
      setJobAndRecruiterApplicationsTotal(0);
    }
  };

  const closeApplicationsPopup = () => {
    setShowCandidateDetails(false);
    setIsApplicationCandidatePopupOpen(false);
    setTimeout(() => {
      setSelectedApplicationCandidate(null);
      setIsEditingApplicationCandidate(false);
      setOriginalApplicationCandidateData(null);
      setEditedApplicationCandidateData(null);
      setApplicationCandidateEmailError("");
      setApplicationCandidateEmailTouched(false);
      setIsDeleteApplicationCandidatePopupOpen(false);
      setJobPipelineJobId();
      setJobPipelineStage();
      setJobPipelingStageName();
      setRecruiterJobsType(null);
      setIsRecruiterApplicationsJobsClicked(false);
      setRecruiterApplicationsJobs();
      setStoreApplicationsByJob();
      setGetApplicationsByJobCalled(false);
      setApplicationSource(null);
      setVendorPipelineActions([]);
      setVendorPipelineActionsTotal(0);
      setVendorPipelineActionVendor("");
      setVendorPipelineActionStatus("");
      setJobAndRecruiterApplicationsCurrentPage(1);
      setJobAndRecruiterApplicationsTotal(0);
    }, 300);
  };

  const callJobOrRecruiterWithPage = (page) => {
    try {
      if (applicationSource === "job") {
        getApplicationsByJob(jobPipelineJobId, jobPipelineStage, page);
      } else if (applicationSource === "recruiter") {
        getApplicationsByRecruiter(
          jobPipelineJobId,
          jobPipelineStage,
          page,
          recruiterJobsType,
        );
      } else if (applicationSource === "vendor") {
        getVendorPipelinesActions(jobPipelineJobId, jobPipelineStage, page);
      }
    } catch (error) {
      console.error("error in callJobOrRecruiterWithPage");
    }
  };

  const closeApplicationCandidatePopup = () => {
    setIsApplicationCandidatePopupOpen(false);
    setSelectedApplicationCandidate(null);
    setIsEditingApplicationCandidate(false);
    setOriginalApplicationCandidateData(null);
    setEditedApplicationCandidateData(null);
    setApplicationCandidateEmailError("");
    setApplicationCandidateEmailTouched(false);
  };

  const getApplicationCandidateId = (row) => row?.id;

  const parseCandidateResume = (parsedResume) => {
    if (!parsedResume) return null;
    if (typeof parsedResume !== "string") return parsedResume;

    try {
      return JSON.parse(parsedResume);
    } catch (error) {
      console.error("error parsing candidate resume", error);
      return null;
    }
  };

  const getApplicationCandidateById = async (candidateId) => {
    try {
      setApplicationCandidateLoading(true);
      setSelectedApplicationCandidate(null);
      setIsApplicationCandidatePopupOpen(true);

      const res = await get(
        VYLABS_BASE_URL,
        `${GET_CANDIDATE_BY_ID}?id=${candidateId}`,
      );

      const fetchedCandidate = res?.candidate;
      if (!fetchedCandidate) {
        throw new Error("Candidate details not found");
      }

      const updatedCandidate = {
        ...fetchedCandidate,
        secondaryEmail: fetchedCandidate.secondary_email,
        secondaryPhone: fetchedCandidate.secondary_phone,
        vendorID: fetchedCandidate.vendor_id,
        parsedResumeData: parseCandidateResume(fetchedCandidate.parsed_resume),
        files: res?.files || null,
        avatar: res?.profile_img_url,
        experience: fetchedCandidate.years_of_experience,
        shouldSkipFetch: true,
      };

      setSelectedApplicationCandidate(updatedCandidate);
    } catch (error) {
      console.error("error in getApplicationCandidateById", error);
      Toast(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch candidate details",
      );
      setIsApplicationCandidatePopupOpen(false);
    } finally {
      setApplicationCandidateLoading(false);
    }
  };

  const handleViewApplicationCandidate = (row) => {
    const candidateId = getApplicationCandidateId(row);

    if (!candidateId) {
      Toast("error", "Candidate id not found");
      return;
    }

    getApplicationCandidateById(candidateId);
  };

  const handleEditApplicationCandidate = () => {
    const getValidExperience = (value) => {
      const num = Number(value);

      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };

    let experienceValue = "";
    const directExp = getValidExperience(
      selectedApplicationCandidate?.years_of_experience,
    );
    const parsedExp = getValidExperience(
      selectedApplicationCandidate?.parsedResumeData?.BasicInfo
        ?.YearsOfExperience,
    );

    if (directExp !== "") {
      experienceValue = directExp;
    } else if (parsedExp !== "") {
      experienceValue = parsedExp;
    }

    const candidateWithFormattedSkills = {
      ...selectedApplicationCandidate,
      name:
        selectedApplicationCandidate.name ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo?.Name ||
        "",
      email:
        selectedApplicationCandidate.email ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo?.Email ||
        "",
      secondaryEmail:
        selectedApplicationCandidate.secondaryEmail ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo
          ?.secondaryEmail ||
        "",
      phone:
        selectedApplicationCandidate.phone ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo?.PhoneNumber ||
        "",
      lineAddress1:
        selectedApplicationCandidate.lineAddress1 ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo
          ?.lineAddress1 ||
        "",
      lineAddress2:
        selectedApplicationCandidate.lineAddress2 ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo
          ?.lineAddress2 ||
        "",
      preferredSalary:
        selectedApplicationCandidate.preferredSalary ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo
          ?.preferredSalary ||
        "",
      zipCode:
        selectedApplicationCandidate.zipCode ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo?.zipCode ||
        "",
      secondaryPhone:
        selectedApplicationCandidate.secondaryPhone ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo
          ?.secondaryPhone ||
        "",
      city:
        selectedApplicationCandidate.city ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo
          ?.CurrentLocationCity ||
        "",
      state:
        selectedApplicationCandidate.state ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo
          ?.CurrentLocationState ||
        "",
      country:
        selectedApplicationCandidate.country ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo
          ?.CurrentLocationCountry ||
        "",
      skills: Array.isArray(selectedApplicationCandidate.skills)
        ? selectedApplicationCandidate.skills
        : selectedApplicationCandidate.parsedResumeData?.Skills || [],
      vendorID:
        selectedApplicationCandidate.vendorID ||
        selectedApplicationCandidate.parsedResumeData?.BasicInfo?.vendorID ||
        "",
      designation:
        selectedApplicationCandidate.designation?.trim() ||
        selectedApplicationCandidate.parsedResumeData?.CareerHistory
          ?.JobDetails?.[0]?.JobTitle ||
        "",
      source: selectedApplicationCandidate.source,
      experience: experienceValue,
      custom_fields: selectedApplicationCandidate?.custom_fields || {},
    };

    setOriginalApplicationCandidateData({ ...selectedApplicationCandidate });
    setEditedApplicationCandidateData(candidateWithFormattedSkills);
    setIsEditingApplicationCandidate(true);
    setApplicationCandidateEmailError("");
    setApplicationCandidateEmailTouched(false);
  };

  const resetApplicationCandidateEdit = () => {
    if (originalApplicationCandidateData) {
      setSelectedApplicationCandidate(originalApplicationCandidateData);
    }
    setIsEditingApplicationCandidate(false);
    setOriginalApplicationCandidateData(null);
    setEditedApplicationCandidateData(null);
    setApplicationCandidateEmailError("");
    setApplicationCandidateEmailTouched(false);
  };

  const handleApplicationCandidateChange = (data) => {
    setEditedApplicationCandidateData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const saveApplicationCandidateChanges = async () => {
    if (!editedApplicationCandidateData) return;

    const email =
      editedApplicationCandidateData.email !== undefined
        ? editedApplicationCandidateData.email
        : selectedApplicationCandidate.email || "";

    if (!email.trim()) {
      setApplicationCandidateEmailError("Email is required");
      setApplicationCandidateEmailTouched(true);
      Toast("error", "Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setApplicationCandidateEmailError("Invalid email format");
      setApplicationCandidateEmailTouched(true);
      Toast("error", "Please fill all required fields correctly");
      return;
    }

    setApplicationCandidateEmailError("");
    setApplicationCandidateEmailTouched(false);

    try {
      const candidateData = { id: selectedApplicationCandidate.id };

      Object.keys(editedApplicationCandidateData).forEach((key) => {
        const newValue = editedApplicationCandidateData[key];
        const oldValue = selectedApplicationCandidate[key];
        const isChanged = Array.isArray(newValue)
          ? JSON.stringify(newValue) !== JSON.stringify(oldValue)
          : newValue !== oldValue;

        if (
          isChanged &&
          newValue !== undefined &&
          newValue !== null &&
          newValue !== ""
        ) {
          candidateData[key] = newValue;
        }
      });

      if (
        !candidateData.designation &&
        selectedApplicationCandidate.designation
      ) {
        candidateData.designation = selectedApplicationCandidate.designation;
      }

      Object.keys(candidateData).forEach((key) => {
        if (key === "preferredSalary") {
          candidateData[key] = Number(candidateData[key]);
        }
      });

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }

      if (candidateData.source !== undefined) {
        candidateData.source = toCamelCase(candidateData.source);
      }

      if (editedApplicationCandidateData.custom_fields) {
        const oldCustomFields =
          selectedApplicationCandidate.custom_fields || {};
        const newCustomFields = editedApplicationCandidateData.custom_fields;
        const hasCustomFieldChanges = Object.keys(newCustomFields).some(
          (key) => newCustomFields[key] !== oldCustomFields[key],
        );

        if (hasCustomFieldChanges) {
          candidateData.custom_fields = filterPayload({
            ...oldCustomFields,
            ...newCustomFields,
          });
        }
      }

      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_CANDIDATE_DETAILS,
        candidateData,
      );

      if (response) {
        await getApplicationCandidateById(selectedApplicationCandidate.id);
        setIsEditingApplicationCandidate(false);
        setOriginalApplicationCandidateData(null);
        setEditedApplicationCandidateData(null);
        if (applicationSource === "job") {
          getApplicationsByJob(
            jobPipelineJobId,
            jobPipelineStage,
            jobAndRecruiterApplicationsCurrentPage,
          );
        } else {
          getApplicationsByRecruiter(
            jobPipelineJobId,
            jobPipelineStage,
            jobAndRecruiterApplicationsCurrentPage,
          );
        }
        Toast("success", "Candidate updated successfully");
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      const backendMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to update candidate";
      Toast("error", backendMessage);
    }
  };

  const handleOpenApplicationCandidateDeleteDialog = () => {
    setIsDeleteApplicationCandidatePopupOpen(true);
  };

  const handleDeleteApplicationCandidate = async () => {
    setIsDeleteApplicationCandidatePopupOpen(false);
    if (!selectedApplicationCandidate?.id) return;

    try {
      await deleteRequest(VYLABS_BASE_URL, DELETE_CANDIDATE_DETAILS, {
        id: selectedApplicationCandidate.id,
      });
      Toast("success", "Candidate deleted successfully");
      if (applicationSource === "job") {
        getApplicationsByJob(
          jobPipelineJobId,
          jobPipelineStage,
          jobAndRecruiterApplicationsCurrentPage,
        );
      } else {
        getApplicationsByRecruiter(
          jobPipelineJobId,
          jobPipelineStage,
          jobAndRecruiterApplicationsCurrentPage,
        );
      }
      setSelectedApplicationCandidate(null);
      closeApplicationCandidatePopup();
    } catch (error) {
      console.error("Error deleting candidate:", error);
      const backendMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to delete candidate";
      Toast("error", backendMessage);
    }
  };

  const recruiterMetricColumns = [
    {
      name: "Recruiter Name",
      cell: (row) => (
        <CustomTooltip
          title={row.firstName + " " + row?.lastName || "N/A"}
          arrow
        >
          <span>{row.firstName + " " + row?.lastName || "N/A"}</span>
        </CustomTooltip>
      ),
    },
    {
      name: "Email",
      cell: (row) => (
        <CustomTooltip title={row.email || "N/A"} arrow>
          <span>{row.email || "N/A"}</span>
        </CustomTooltip>
      ),
    },
    {
      name: "Role",
      cell: (row) => <span>{row.role || "N/A"}</span>,
    },
    {
      name: "Type",
      cell: (row) => <span>{row.type || "N/A"}</span>,
    },
  ];

  const isJobRecruiterMetric =
    applicationSource === "job" &&
    ["recruiters", "assigned_recruiters", "tracked_recruiters"].includes(
      jobPipelineStage,
    );

  const applicationColumns = [
    {
      name: "Candidate Name",
      cell: (row) => (
        <CustomTooltip title={row.name || "N/A"} arrow>
          <span>{row.name || "N/A"}</span>
        </CustomTooltip>
      ),
    },
    {
      name: "Email",
      cell: (row) => (
        <CustomTooltip title={row.email || "N/A"} arrow>
          <span>{row.email || "N/A"}</span>
        </CustomTooltip>
      ),
    },
    {
      name: "Phone",
      cell: (row) => (
        <CustomTooltip title={row.phone || "N/A"} arrow>
          <span>{row.phone || "N/A"}</span>
        </CustomTooltip>
      ),
    },
    ...(applicationSource === "job" || applicationSource === "recruiter"
      ? [
          {
            name: "Job",
            cell: (row) => {
              const jobTitle =
                applicationSource === "job"
                  ? storeJobTitleForTable
                  : row.jobTitle;
              return (
                <CustomTooltip title={jobTitle || "N/A"} arrow>
                  <span>{jobTitle || "N/A"}</span>
                </CustomTooltip>
              );
            },
          },
        ]
      : []),
    {
      name: "Stage",
      cell: (row) => {
        const stageName = callStageMap[row?.stage] || row?.stage || "N/A";

        return (
          <CustomTooltip title={stageName} arrow>
            <span>{stageName}</span>
          </CustomTooltip>
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="admin-dashboard-actions">
          <CustomTooltip title="View Candidate" arrow>
            <img
              src={EyeIcon}
              alt="view candidate"
              className="parser-table-action-icons"
              onClick={() => handleViewApplicationCandidate(row)}
            />
          </CustomTooltip>
        </div>
      ),
    },
  ];

  const vendorPipelineActionColumns = useMemo(
    () => [
      {
        name: "Recruiter",
        minWidth: "170px",
        grow: 1.2,
        cell: (row) => {
          const recruiterName =
            [row?.firstName, row?.lastName].filter(Boolean).join(" ").trim() ||
            row?.recruiterName ||
            "N/A";

          return (
            <CustomTooltip title={recruiterName} arrow>
              <span>{applyEllipsis(recruiterName, 22)}</span>
            </CustomTooltip>
          );
        },
      },
      {
        name: "New Status",
        minWidth: "130px",
        cell: (row) => (
          <span>
            {toCapitalize(row?.newStatus || row?.normalizedStatus || "N/A")}
          </span>
        ),
      },
      {
        name: "Action At",
        minWidth: "180px",
        cell: (row) => {
          const value = row?.actionAt;
          const formatted = value
            ? new Date(value).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A";

          return (
            <CustomTooltip
              title={value ? new Date(value).toLocaleString() : "N/A"}
              arrow
            >
              <span>{formatted}</span>
            </CustomTooltip>
          );
        },
      },
    ],
    [],
  );

  const handleTeamWorkloadScroll = useCallback(() => {
    try {
      const container = teamWorkloadScrollRef.current;
      if (!container) return;
      const reachedBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 20;

      if (
        !reachedBottom ||
        !teamWorkloadHasMore ||
        isTeamWorkloadFetchingMore
      ) {
        return;
      }

      fetchTeamWorkloadData(
        teamWorkloadPayloadRef.current,
        teamWorkloadCurrentPage + 1,
        true,
      );
    } catch (error) {
      console.error("error in handleTeamWorkloadScroll", error);
    }
  }, [
    fetchTeamWorkloadData,
    isTeamWorkloadFetchingMore,
    teamWorkloadCurrentPage,
    teamWorkloadHasMore,
  ]);

  useEffect(() => {
    const container = teamWorkloadScrollRef.current;

    if (!container) return;

    container.addEventListener("scroll", handleTeamWorkloadScroll);

    return () =>
      container.removeEventListener("scroll", handleTeamWorkloadScroll);
  }, [handleTeamWorkloadScroll, filteredTeamWorkload.length]);

  const jobRecruiterApplicationColumns = useMemo(
    () => [
      {
        name: "Job",
        grow: 1.5,
        minWidth: "190px",
        cell: (row) => (
          <div className="admin-dashboard-job-cell">
            <CustomTooltip title={row.title} arrow>
              <div>{applyEllipsis(row.title, 15)}</div>
            </CustomTooltip>
          </div>
        ),
      },
      {
        name: isHiringTeamTenant ? "Hiring Team" : "Client",
        selector: (row) => row.clientName,
        minWidth: "160px",
      },
      {
        name: "Req",
        selector: (row) => row.required,
        width: "70px",
      },
      {
        name: "Type",
        width: "100px",
        cell: (row) => (
          <span>
            {row?.recruiter_flag ? toCapitalize(row.recruiter_flag) : "-"}
          </span>
        ),
      },
      ...(recruiterApplicationsJobs?.[0]?.stages || []).map((stage) => ({
        name: stage.name,
        width: "100px",
        cell: (row) => {
          const rowStage = row?.stages?.find(
            (item) => item.system_key === stage.system_key,
          );

          return <div>{rowStage?.count ?? 0}</div>;
        },
      })),
      {
        name: "Completion",
        minWidth: "140px",
        cell: (row) => (
          <div className="admin-dashboard-progress">
            <span>
              <i style={{ width: `${Math.min(row.completion, 100)}` }} />
            </span>
            {row.completion}
          </div>
        ),
      },
      {
        name: "Actions",
        right: true,
        width: "112px",
        cell: (row) => (
          <div className="admin-dashboard-actions">
            <CustomTooltip title="View Job" arrow>
              <img
                src={EyeIcon}
                alt="view job"
                className="parser-table-action-icons"
                onClick={() => handleOpenPipelineJobDetails(row)}
              />
            </CustomTooltip>
          </div>
        ),
      },
    ],
    [handleOpenPipelineJobDetails],
  );

  const applyCustomFilters = (filtersWithValues) => {
    try {
      let selectedFromDate;
      let selectedToDate;

      filtersWithValues.forEach((date) => {
        if (date.type === "from_date") {
          selectedFromDate = date.value;
        }
        if (date.type === "to_date") {
          selectedToDate = date.value;
        }
      });
      if (!selectedFromDate || !selectedToDate) {
        Toast("error", "Please Select both From and To dates to continue");
        return;
      }
      if (selectedFromDate > selectedToDate) {
        Toast("error", "From Date cannot be later than To Date");
        return;
      }
      setFromDate(selectedFromDate);
      setToDate(selectedToDate);
      customFilterAppliedRef.current = true;
      fetchAdminDashboardData(
        1,
        false,
        false,
        false,
        selectedFromDate,
        selectedToDate,
      );
      setIsFilterPopupOpen(false);
    } catch (error) {
      console.error("error in applyCustomFilters", error);
    }
  };

  const handleCloseCustomFilter = useCallback(() => {
    setIsFilterPopupOpen(false);
    if (customFilterAppliedRef.current) {
      customFilterAppliedRef.current = false;
      return;
    }
    setSelectedDashboardDate(previousDashboardDateRef.current);
  }, []);

  const handleCustomFilterClear = () => {
    setSelectedDashboardDate("today");
    setFromDate(null);
    setToDate(null);

    customFilterAppliedRef.current = false;
    previousDashboardDateRef.current = "today";

    setIsFilterPopupOpen(false);
  };

  return (
    <div>
      {renderAdmin ? (
        <div
          className={`dashboard-container ${isDarkMode ? "admin-dashboard-dark" : ""}`}
          style={{ display: "flex", flexDirection: "column", gap: "1vw" }}
        >
          {showTrialSnackbar && (
            <TrialSnackbar
              daysLeft={daysLeftInTrial}
              onClose={handleCloseSnackbar}
            />
          )}
          <div className="admin-dashboard-header-main-container">
            <div className="hearder-inner-container">
              <div className="cephea-span-container">
                <h3 className="cephea-text">
                  Hello, {firstName}!
                  <img className="hello-icon" src={HelloIcon} alt="hello" />
                </h3>
              </div>
              <div className="cephea-span-container">
                <h5 style={{ fontSize: 20, marginTop: 10, fontWeight: 400 }}>
                  Welcome to VybogTAL, your recruitment companion
                </h5>
              </div>
            </div>
            <div
              className="cephea-span-container-tenant-name"
              style={{ marginTop: "2px" }}
            >
              {SuperAdminAccess && (
                <div className="super-admin-toggle">
                  <Button
                    label="Super Admin"
                    event={() => setRenderAdmin(false)}
                    style={{
                      padding: "5px",
                      fontSize: "12px",
                      borderRadius: "50px",
                    }}
                    hoverd={false}
                    backgroundColor="var(--grid-bg)"
                    color="var(--grid-na-text)"
                  />
                  <Button
                    label="Admin"
                    event={() => setRenderAdmin(true)}
                    style={{
                      padding: "5px",
                      fontSize: "12px",
                      borderRadius: "50px",
                    }}
                    hoverd={false}
                    backgroundColor="var(--grid-a-bg)"
                    color="var(--grid-a-text)"
                  />
                </div>
              )}
              {tenantDetails?.logo_key ? (
                <div className="admin-dashboard-tenant-logo-shell">
                  <img
                    alt="Tenant Logo"
                    src={tenantDetails?.logo_key}
                    className="admin-dashboard-tenant-logo"
                  />
                </div>
              ) : (
                <h3 className="cephea-tentnt-text">{tenantName}</h3>
              )}
              <div
                className="dashboard-notification-icon-container"
                onClick={toggleNotificationPopup}
                role="button"
                tabIndex={0}
                aria-label="Notifications"
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    toggleNotificationPopup(event);
                  }
                }}
              >
                <img
                  src={isDarkMode ? notificationDarkIcon : notificationIcon}
                  alt="Notification"
                  style={{ width: 30, height: 30 }}
                />
                {notificationUnreadCount > 0 && (
                  <span className="dashboard-notification-badge">
                    {notificationUnreadCount > 99
                      ? "99+"
                      : notificationUnreadCount}
                  </span>
                )}
              </div>
            </div>
          </div>
          {AdminAccess && showAddMemberBanner && (
            <div className="add-member-banner" style={{ marginBottom: "10px" }}>
              <img
                alt="Add Member Icon"
                src={addMemberImage}
                className="banner-icon"
              />
              <div className="banner-content">
                <h3>Add Member!</h3>
                <p>
                  As an admin, you can add members to your organization. Expand
                  your team and boost productivity right now!
                </p>
              </div>
              <Button label="Add Member" event={handleAddMemberNavigate} />
            </div>
          )}
          <div className="admin-section-header">
            <div className="admin-section-title-wrapper">
              <h3 className="section-title">Workspace Overview</h3>
            </div>
            <div className="admin-stats-subtitle">
              A quick snapshot of your recruitment workspace
            </div>
          </div>
          <div className="dashboard-metrics-row">
            {metrics.map((metric) => (
              <DashboardCard
                key={metric.title}
                animate={hasAnimated}
                {...metric}
              />
            ))}
          </div>
          <div className="admin-section-header">
            <div className="admin-dashboard-performance-header">
              <div className="admin-dashboard-performance-copy">
                <div className="admin-section-title-wrapper">
                  <h3 className="section-title">Performance Overview</h3>
                </div>
                <div className="admin-stats-subtitle">
                  Track your recruitment activity and progress.
                </div>
              </div>
              <div
                className="admin-dashboard-filter-panel"
                ref={dashboardDateToolbarRef}
              >
                <div className="admin-dashboard-filter-panel-top">
                  <div className="admin-dashboard-saved-view-group">
                    <div className="admin-dashboard-header-client-filter admin-dashboard-header-saved-view-filter">
                      <DynamicAutocomplete
                        options={savedViewOptions}
                        value={activeSavedViewOption}
                        onChange={handleSavedViewChange}
                        placeholder="Select View"
                        noOptionsText="No saved views found..."
                        loading={isLoadingSavedViews}
                        sx={dashboardHeaderFilterAutocompleteSx}
                        componentsProps={AutocompleteComponentsProps}
                        renderOption={(props, option) => {
                          const { key, ...optionProps } = props;

                          return (
                            <li {...optionProps} key={key}>
                              <span className="admin-dashboard-saved-view-option-label">
                                {option.label}
                              </span>
                              <Button
                                type="button"
                                className="admin-dashboard-saved-view-delete-button"
                                ariaLabel={`Delete ${option.label}`}
                                iconSrc={deleteIcon}
                                iconWidth="15px"
                                backgroundColor="transparent"
                                border="none"
                                hoverd={false}
                                style={{
                                  width: "26px",
                                  height: "26px",
                                  minWidth: "26px",
                                  marginLeft: "10px",
                                  padding: 0,
                                  borderRadius: "50%",
                                }}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                }}
                                event={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setSavedViewPendingDelete(option);
                                  setIsSavedViewDeleteDialogOpen(true);
                                }}
                              />
                            </li>
                          );
                        }}
                      />
                    </div>
                    {isCreateViewButtonActive && (
                      <Button
                        label="Save View"
                        event={handleCreateViewOpen}
                        className="admin-dashboard-save-filter-button"
                        style={{
                          padding: "8px 15px",
                        }}
                        backgroundColor="#6860EE"
                        color="#FFFFFF"
                        border="none"
                      />
                    )}
                  </div>
                  <div className="admin-dashboard-date-range-pill admin-dashboard-date-range-pill-top">
                    <CalendarDays size={20} strokeWidth={2} />
                    <span>{dashboardDateRangeLabel}</span>
                  </div>
                </div>
                <div className="admin-dashboard-date-range-toolbar">
                  <MonthFilter
                    value={selectedDashboardDate}
                    onChange={handleDashboardDateFilterChange}
                    options={DASHBOARD_DATE_FILTER_OPTIONS}
                    ariaLabel="Select dashboard date filter"
                    className="admin-dashboard-performance-date-filter"
                  />
                  <div className="admin-dashboard-header-client-filter">
                    <DynamicAutocomplete
                      options={filteredClientOptions}
                      value={selectedClientOption}
                      onChange={handleDashboardClientFilterChange}
                      placeholder={
                        isHiringTeamTenant ? "Hiring Team" : "Client"
                      }
                      onInputChange={handleDashboardClientSearch}
                      onOpen={handleDashboardClientOpen}
                      noOptionsText={
                        filteredClientOptions.length === 0
                          ? "No clients found..."
                          : "No options..."
                      }
                      loading={isLoadingClients}
                      sx={dashboardHeaderFilterAutocompleteSx}
                      componentsProps={AutocompleteComponentsProps}
                    />
                  </div>
                  <div className="admin-dashboard-header-recruiter-filter">
                    <DynamicMultipleSelectCheckmarks
                      options={recruiterMultiSelectOptions}
                      label=""
                      labelKey="label"
                      valueKey="value"
                      value={selectedRecruiter}
                      onChange={handleDashboardRecruiterFilterChange}
                      onSearchChange={handleDashboardRecruiterSearch}
                      loading={isLoadingMembers}
                      placeholder="Recruiter"
                      width="100%"
                    />
                  </div>
                </div>
                <StageFilterPopup
                  isOpen={isFilterPopupOpen}
                  onClose={handleCloseCustomFilter}
                  onClear={handleCustomFilterClear}
                  showDateRangeFilter={true}
                  isCallStageActive={false}
                  showDateFilter={false}
                  isUsersFilterActive={false}
                  showCandidateViewSelection={false}
                  subtitle="Filter by custom date range"
                  isDarkMode={isDarkMode}
                  onApplyFull={applyCustomFilters}
                  popupClassName="admin-dashboard-custom-date-filter-popup"
                  popupStyle={customDatePopupStyle || { visibility: "hidden" }}
                  fromDate={fromDate}
                  toDate={toDate}
                  callOnApplyFullDuringClear={false}
                />
              </div>
            </div>
          </div>
          <div className="admin-dashboard-metrics-row">
            {dashboardCards.map((metric) => (
              <DashboardCard key={metric.title} {...metric} />
            ))}
          </div>
          <div className="admin-section-header">
            <div className="admin-section-title-wrapper">
              <h3 className="section-title">Job Operations</h3>
            </div>
            <div className="admin-stats-subtitle">
              Which jobs are moving, which need attention.
            </div>
          </div>

          <div className="admin-dashboard-table-content">
            <div className="admin-dashboard-table-pipeline">
              <div className="admin-dashboard-table-heading">
                <div>
                  <h3>Job Pipeline</h3>
                  <span className="admin-dashboard-table-heading-count">
                    {jobPipelineTotalPages} active
                  </span>
                </div>

                <div className="admin-dashboard-table-heading-actions">
                  {filteredJobs.length > 0 && (
                    <ColumnCustomizeDropdown
                      buttonRef={dropdownBtnRef}
                      columns={columnsForCustomize}
                      draftColumns={draftVisibleColumns}
                      dropdownRef={dropdownRef}
                      idPrefix="column"
                      isDarkMode={isDarkMode}
                      isOpen={dropdownOpen}
                      hasSavedView={!!savedJobPipelineColumnViewId}
                      onCreateSaveView={handleCreateSaveView}
                      onSelectAll={handleSelectAll}
                      onSelectNone={handleSelectNone}
                      onToggleColumn={toggleColumnVisibility}
                      onToggleDropdown={toggleDropdown}
                      onUpdateSaveView={handleUpdateSaveView}
                    />
                  )}
                  {filteredJobs.length > 0 && (
                    <div className="admin-dashboard-sort-control">
                      <label style={{ whiteSpace: "nowrap" }}>Sort by</label>
                      <DynamicSelectInput
                        defaultOption="Select"
                        options={jobPipelineAPIRes}
                        value={selectedJobPipelineColumn}
                        onChange={(e) =>
                          setSelectedJobPipelineColumn(e.target.value)
                        }
                        labelKey="title"
                      />
                      <img
                        src={
                          jobPipelineSort?.sortOrder === "desc"
                            ? isDarkMode
                              ? sortDscDarkIcon
                              : sortDscLightIcon
                            : isDarkMode
                              ? sortAscDarkIcon
                              : sortAscLightIcon
                        }
                        alt={
                          jobPipelineSort?.sortOrder === "desc"
                            ? "Sort descending"
                            : "Sort ascending"
                        }
                        onClick={handleJobPipelineSort}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <Table
                columns={tableColumns}
                data={filteredJobs}
                fixedHeader
                fixedHeaderScrollHeight="320px"
                pagination
                paginationServer
                paginationTotalRows={jobPipelineTotalPages}
                paginationPerPage={JOB_PIPELINE_LIMIT}
                currentPage={jobPipelineCurrentPage}
                onChangePage={(page) => {
                  if (skipJobPipelinePageChangeRef.current && page === 1) {
                    skipJobPipelinePageChangeRef.current = false;
                    return;
                  }
                  fetchAdminDashboardData(
                    page,
                    false,
                    true,
                    false,
                    fromDate,
                    toDate,
                  );
                }}
                sortServer
                onSort={(column, direction) => {
                  const sortBy = column?.sortField;
                  skipJobPipelinePageChangeRef.current = true;
                  const sort = { sortBy, sortOrder: direction };
                  setJobPipelineSort(sort);
                  fetchAdminDashboardData(
                    1,
                    false,
                    true,
                    false,
                    fromDate,
                    toDate,
                    sort,
                  );
                }}
                progressPending={isJobPipelineLoading}
                className="global-data-table admin-dashboard-pipeline-table"
                backgroundColor="var(--table__background)"
                color="var(--header__color)"
                customNoDataFound={
                  <div className="admin-dashboard-fallbacks">
                    <img
                      alt="nojobsicon"
                      src={isDarkMode ? NoJobsDarkIcon : NoJobLightIcon}
                      className="admin-dashboard-fallback-img"
                    />
                    <span
                      style={{
                        color: "var(--text_color)",
                        fontSize: "19px",
                        fontWeight: "500",
                      }}
                    >
                      No jobs in pipeline
                    </span>
                    <span className="admin-dashboard-fallback-text">
                      Start creating jobs to build your hiring pipeline.
                    </span>
                  </div>
                }
              />
            </div>
          </div>

          {isVendorFeatureEnabled && (
            <div>
              <div className="admin-section-header">
                <div className="admin-section-title-wrapper">
                  <h3 className="section-title">Vendor Operations</h3>
                </div>
                <div className="admin-stats-subtitle">
                  Which jobs are moving, which need attention.
                </div>
              </div>
              <div className="admin-dashboard-table-content">
                <div className="admin-dashboard-table-pipeline">
                  <div className="admin-dashboard-table-heading">
                    <div>
                      <h3>Vendor Pipeline</h3>
                      <span className="admin-dashboard-table-heading-count">
                        {vendorPipelineTotalPages} active
                      </span>
                    </div>
                    <div className="admin-dashboard-header-recruiter-filter">
                      {/* {filteredVendorsFromBuild.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexDirection: "row",
                          gap: "5px",
                          width: "50%",
                        }}
                      >
                        <label style={{ whiteSpace: "nowrap" }}>Sort by</label>
                        <DynamicSelectInput
                          defaultOption="Select"
                          options={[
                            ...Object.keys(
                              dashboardData?.vendorPipeline?.vendors?.[0] || {},
                            )
                              .filter(
                                (key) => key !== "id" && key !== "statuses",
                              )
                              .map((key) => ({
                                title: key
                                  .replace(/([A-Z])/g, " $1")
                                  .replace(/^./, (char) => char.toUpperCase())
                                  .replace(/\_/g, " "),
                                value: key,
                              })),

                            ...(
                              dashboardData?.vendorPipeline?.vendors?.[0]
                                ?.statuses || []
                            ).map((item) => ({
                              title: item.status,
                              value: `statuses.${toSnakeCase(item.status)}`,
                            })),
                          ]}
                          value={selectedVendorPipelineColumn}
                          onChange={(e) =>
                            setSelectedVendorPipelineColumn(e.target.value)
                          }
                          labelKey="title"
                        />
                        <img
                          src={
                            vendorPipelineSort?.sortOrder === "desc"
                              ? isDarkMode
                                ? sortDscDarkIcon
                                : sortDscLightIcon
                              : isDarkMode
                                ? sortAscDarkIcon
                                : sortAscLightIcon
                          }
                          alt="sortOrderIcon"
                          style={{ cursor: "pointer" }}
                          onClick={handleVendorPipelineSort}
                        />
                      </div>
                      )} */}
                      {(filteredVendorsFromBuild.length > 0 ||
                        selectedVendor) && (
                        <DynamicAutocomplete
                          options={vendorList}
                          value={selectedVendor}
                          onChange={handleDashboardVendorFilterChange}
                          placeholder="Vendor"
                          onInputChange={handleDashboardVendorSearch}
                          onOpen={handleDashboardVendorOpen}
                          noOptionsText={
                            vendorList.length === 0
                              ? "No vendors found..."
                              : "No options..."
                          }
                          loading={isLoadingVendors}
                          sx={dashboardHeaderFilterAutocompleteSx}
                          componentsProps={AutocompleteComponentsProps}
                        />
                      )}
                    </div>
                  </div>

                  <Table
                    columns={vendorPipelineColumns}
                    data={filteredVendorsFromBuild}
                    fixedHeader
                    fixedHeaderScrollHeight="320px"
                    pagination
                    paginationServer
                    paginationTotalRows={vendorPipelineTotalPages}
                    paginationPerPage={VENDOR_PIPELINE_METRICS_LIMIT}
                    currentPage={vendorPipelineCurrentPage}
                    onChangePage={(page) => {
                      if (
                        skipVendorPipelinePageChangeRef.current &&
                        page === 1
                      ) {
                        skipVendorPipelinePageChangeRef.current = false;
                        return;
                      }
                      fetchAdminDashboardData(
                        page,
                        false,
                        false,
                        true,
                        fromDate,
                        toDate,
                      );
                    }}
                    sortServer
                    onSort={(column, direction) => {
                      const sortBy = column?.sortField;
                      if (!sortBy) return;
                      skipVendorPipelinePageChangeRef.current = true;
                      const sort = { sortBy, sortOrder: direction };
                      setVendorPipelineSort(sort);
                      fetchAdminDashboardData(
                        1,
                        false,
                        false,
                        true,
                        fromDate,
                        toDate,
                        sort,
                      );
                    }}
                    progressPending={isVendorPipelineLoading}
                    className="global-data-table admin-dashboard-pipeline-table"
                    backgroundColor="var(--table__background)"
                    color="var(--header__color)"
                    customNoDataFound={
                      <div className="admin-dashboard-fallbacks">
                        <img
                          alt="nojobsicon"
                          src={
                            isDarkMode ? noVendorDarkIcon : noVendorLightIcon
                          }
                          className="admin-dashboard-fallback-img"
                        />
                        <span
                          style={{
                            color: "var(--text_color)",
                            fontSize: "19px",
                            fontWeight: "500",
                          }}
                        >
                          No vendors in pipeline
                        </span>
                        <span className="admin-dashboard-fallback-text">
                          Start creating vendors to build your hiring pipeline.
                        </span>
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <div className="admin-recruiter-header">
            <div className="admin-recruiter-title-wrapper">
              <h3 className="section-title">Recruiter Operations</h3>
            </div>
            <div className="admin-recruiter-stats-subtitle">
              Who's driving results, who needs support.
            </div>
          </div>

          <div className="admin-dashboard-table-content">
            <div className="admin-dashboard-table-pipeline">
              <div className="admin-dashboard-table-heading">
                <div>
                  <h3>Recruiter Performance</h3>
                  <span className="admin-dashboard-table-heading-count">
                    {recruiterPerformanceTotalPages} recruiters
                  </span>
                </div>
                <div className="admin-dashboard-table-heading-actions">
                  {filteredRecruiterPerformance.length > 0 && (
                    <ColumnCustomizeDropdown
                      buttonRef={recruiterDropdownBtnRef}
                      columns={recruiterColumnsForCustomize}
                      draftColumns={draftVisibleRecruiterColumns}
                      dropdownRef={recruiterDropdownRef}
                      idPrefix="recruiter-column"
                      isDarkMode={isDarkMode}
                      isOpen={recruiterDropdownOpen}
                      hasSavedView={!!savedRecruiterPerformanceColumnViewId}
                      onCreateSaveView={handleCreateRecruiterSaveView}
                      onSelectAll={handleRecruiterSelectAll}
                      onSelectNone={handleRecruiterSelectNone}
                      onToggleColumn={toggleRecruiterColumnVisibility}
                      onToggleDropdown={toggleRecruiterDropdown}
                      onUpdateSaveView={handleUpdateRecruiterSaveView}
                    />
                  )}
                  {filteredRecruiterPerformance.length > 0 && (
                    <div className="admin-dashboard-sort-control">
                      <label style={{ whiteSpace: "nowrap" }}>Sort by</label>
                      <DynamicSelectInput
                        defaultOption="Select"
                        options={[
                          ...Object.keys(
                            dashboardData.performance?.recruiters?.[0] || {},
                          )
                            .filter((key) => key !== "id" && key !== "stages")
                            .map((key) => ({
                              title: key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (char) => char.toUpperCase())
                                .replace(/_/g, " "),
                              value: key,
                            })),
                          ...(
                            dashboardData.performance?.recruiters?.[0]
                              ?.stages || []
                          ).map((stage) => ({
                            title: stage.name,
                            value: `stages.${stage.system_key}`,
                          })),
                        ]}
                        value={selectedRecruiterPerformanceColumn}
                        onChange={(e) =>
                          setSelectedRecruiterPerformanceColumn(e.target.value)
                        }
                        labelKey="title"
                      />
                      <img
                        src={
                          recruiterPerformanceSort?.sortOrder === "desc"
                            ? isDarkMode
                              ? sortDscDarkIcon
                              : sortDscLightIcon
                            : isDarkMode
                              ? sortAscDarkIcon
                              : sortAscLightIcon
                        }
                        alt={
                          recruiterPerformanceSort?.sortOrder === "desc"
                            ? "Sort descending"
                            : "Sort ascending"
                        }
                        onClick={handleRecruiterPerformanceSort}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <Table
                columns={recruiterPerformanceColumns}
                data={filteredRecruiterPerformance}
                pagination
                paginationServer
                paginationTotalRows={recruiterPerformanceTotalPages}
                paginationPerPage={RECRUITER_PERFORMANCE_LIMIT}
                currentPage={recruiterPerformanceCurrentPage}
                onChangePage={(page) => {
                  if (
                    skipRecruiterPerformancePageChangeRef.current &&
                    page === 1
                  ) {
                    skipRecruiterPerformancePageChangeRef.current = false;
                    return;
                  }
                  fetchAdminDashboardData(
                    page,
                    true,
                    false,
                    false,
                    fromDate,
                    toDate,
                  );
                }}
                sortServer
                onSort={(column, direction) => {
                  const sortBy = column?.sortField;
                  if (!sortBy) return;
                  skipRecruiterPerformancePageChangeRef.current = true;
                  const sort = { sortBy, sortOrder: direction };
                  setRecruiterPerformanceSort(sort);
                  fetchAdminDashboardData(
                    1,
                    true,
                    false,
                    false,
                    fromDate,
                    toDate,
                    sort,
                  );
                }}
                progressPending={isRecruitmentPerformanceLoading}
                className="global-data-table admin-dashboard-pipeline-table"
                backgroundColor="var(--table__background)"
                color="var(--header__color)"
                customNoDataFound={
                  <div className="admin-dashboard-fallbacks">
                    <img
                      alt="noreqicon"
                      src={isDarkMode ? NoReqDarkIcon : NoReqLightIcon}
                      className="admin-dashboard-fallback-img"
                    />
                    <span
                      style={{
                        color: "var(--text_color)",
                        fontSize: "19px",
                        fontWeight: "500",
                      }}
                    >
                      No recruiter activity yet
                    </span>
                    <span className="admin-dashboard-fallback-text">
                      Recruiter performance data will appear once activity
                      begins.
                    </span>
                  </div>
                }
              />
            </div>
          </div>

          <div className="admin-dashboard-followups-schedule">
            <AdminFollowUps
              followUps={followUps}
              loading={followUpsLoader}
              timezone={currentTimezone}
            />

            <div className="admin-dashboard-main-interview admin-dashboard-interviews">
              <div className="recent-section-title-wrapper">
                <div className="recent-title">
                  <img
                    alt="calendar"
                    src={isDarkMode ? trackedTodayDarkIcon : trackedTodayIcon}
                    className="calendar-icon"
                    style={{ width: 24, height: 24 }}
                  />
                  <h3 className="section-title">Today's Schedule</h3>
                </div>
              </div>
              <div
                className="calendar-section"
                role="region"
                aria-label="Today's scheduled interviews"
                tabIndex={0}
              >
                {isScheduleLoading ? (
                  <p role="status">Loading today's interviews...</p>
                ) : scheduleError ? (
                  <div role="alert" className="admin-schedule-error">
                    <p>{scheduleError}</p>
                    <Button
                      label="Retry"
                      event={() => setScheduleRefresh((value) => value + 1)}
                    />
                  </div>
                ) : calendarEvents.length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    {calendarEvents
                      .slice()
                      .sort((a, b) => new Date(a.start) - new Date(b.start))
                      .map((event, idx) => (
                        <EventComponent key={event.id || idx} event={event} />
                      ))}
                  </div>
                ) : (
                  <div className="no-tracking-history">
                    <div>
                      <div
                        className="no-tracking-history-text"
                        style={{ marginBottom: "-40px" }}
                      >
                        No interviews scheduled for today.
                      </div>
                      <AnimationPlayer
                        animationSrc={NoMeetingAnimation}
                        playerHeight="300px"
                        playerWidth="300px"
                      />
                    </div>
                  </div>
                )}
                <SidePopup
                  isOpen={showInterviewDetails}
                  onClose={() => setShowInterviewDetails(false)}
                  title={selectedInterviewCandidate?.position}
                  titleColor="var(--sidepopup-header-color)"
                  headerBackgroundColor="var(--sidepopup-header-background)"
                  sidePopupWidth="min(100%, 540px)"
                  sidePopupStyle={{
                    boxShadow: "0 0 30px rgba(0, 0, 0, 0.15)",
                    borderRadius: "16px 0 0 16px",
                  }}
                >
                  {selectedInterviewCandidate && (
                    <div className="candidate-details-popup admin-interview-details">
                      <div className="candidate-details-outer-container">
                        <div className="candidate-details-inner-container">
                          <h3 className="schedule-header">Candidate Details</h3>
                          <div className="schedule-detail">
                            <div className="candidate-detail">
                              <div className="icon-with-header">
                                <img
                                  src={candidateIcon}
                                  alt="candidateIcon"
                                  className="candidate-detail-icon"
                                />
                                <h4>Candidate</h4>
                              </div>
                              <p>
                                {applyEllipsis(
                                  selectedInterviewCandidate.name,
                                  30,
                                )}
                              </p>
                            </div>
                            <div className="candidate-detail">
                              <div className="icon-with-header">
                                <img
                                  src={emailIcon}
                                  alt="emailIcon"
                                  className="candidate-detail-icon"
                                />
                                <h4>Email</h4>
                              </div>
                              <p>
                                {applyEllipsis(
                                  selectedInterviewCandidate.email,
                                  30,
                                )}
                              </p>
                            </div>
                            <div className="candidate-detail">
                              <div className="icon-with-header">
                                <img
                                  src={contactNoIcon}
                                  alt="contactIcon"
                                  className="candidate-detail-icon"
                                />
                                <h4>Contact No.</h4>
                              </div>
                              <p>{selectedInterviewCandidate.contactNo}</p>
                            </div>
                            <div className="candidate-detail">
                              <div className="icon-with-header">
                                <img
                                  src={scheduleJobIcon}
                                  alt="jobIcon"
                                  className="candidate-detail-icon"
                                />
                                <h4>Applied for</h4>
                              </div>
                              <p>
                                {applyEllipsis(
                                  selectedInterviewCandidate.position,
                                  30,
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="candidate-details-inner-container">
                          <h3 className="schedule-header">Interview Details</h3>
                          <div className="schedule-detail">
                            <div className="candidate-detail">
                              <div className="icon-with-header">
                                <img
                                  src={interviewPersonIcon}
                                  alt="interviewerIcon"
                                  className="candidate-detail-icon"
                                />
                                <h4>Interviewer</h4>
                              </div>
                              <p>
                                {applyEllipsis(
                                  selectedInterviewCandidate.interviewPerson,
                                  30,
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="candidate-detail">
                            <div className="icon-with-header">
                              <img
                                src={dateIcon}
                                alt="dateIcon"
                                className="candidate-detail-icon"
                              />
                              <h4>When</h4>
                            </div>
                            <div className="date-and-time-container">
                              <span>{selectedInterviewCandidate.date}</span>
                              <span>{`${selectedInterviewCandidate.startTime} - ${selectedInterviewCandidate.endTime}`}</span>
                            </div>
                          </div>
                          {selectedInterviewCandidate.meetingLink && (
                            <div className="candidate-detail">
                              <div className="icon-with-header">
                                <img
                                  src={linkIcon}
                                  alt="linkIcon"
                                  className="candidate-detail-icon"
                                />
                                <h4>Meeting Link</h4>
                              </div>
                              <a
                                href={selectedInterviewCandidate.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="detail-value link"
                              >
                                {selectedInterviewCandidate.meetingLink}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </SidePopup>
              </div>
            </div>
          </div>

          <div className="admin-dashboard-recruiter-layout">
            <div className="admin-dashboard-funnel-pipeline">
              <div className="admin-dashboard-funnel-header">
                <div>
                  <h3>Recruitment Funnel</h3>
                  <span>Company-wide pipeline health</span>
                </div>
              </div>
              {isRecruitmentFunnelLoading ? (
                <div className="admin-dashboard-loader-card">
                  <VybogTALLoaderAnimation
                    animationHeight="110px"
                    animationWidth="110px"
                  />
                </div>
              ) : recruitmentFunnel.length &&
                recruitmentFunnel[0].stage !== "stages" ? (
                <div className="admin-dashboard-funnel-list">
                  {recruitmentFunnel.map((item, index) => {
                    const value = Number(item.barValue ?? item.count) || 0;
                    const width = maxRecruitmentFunnelValue
                      ? `${Math.max((value / maxRecruitmentFunnelValue) * 100, 4)}%`
                      : "0%";

                    return (
                      <div
                        key={item.id || item.stage || index}
                        className="admin-dashboard-funnel-row"
                      >
                        <div className="admin-dashboard-funnel-stage">
                          {item.stage}
                        </div>
                        <div className="admin-dashboard-funnel-track">
                          <div
                            className="admin-dashboard-funnel-bar"
                            style={{
                              width,
                              background: item.gradient,
                            }}
                          />
                        </div>
                        <div className="admin-dashboard-funnel-count">
                          {formatFunnelCount(item.count)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="admin-dashboard-empty-card">
                  <img
                    alt="nofunnelicon"
                    src={isDarkMode ? NoFunnelDarkIcon : NoFunnelLightIcon}
                    className="admin-dashboard-empty-img"
                  />
                  <span className="admin-dashboard-empty-text">
                    No recruiter activity
                  </span>
                  <span className="admin-dashboard-fallback-text">
                    Start adding candidates to track your recruitment progress.
                  </span>
                </div>
              )}
            </div>

            <div className="admin-dashboard-table-card admin-dashboard-workload-card">
              <div className="admin-dashboard-table-header">
                <div>
                  <h3>Team Workload</h3>
                  <span>Each Recruiter's Candidate tracking for a Job</span>
                </div>
              </div>
              {isTeamWorkloadLoading ? (
                <div className="admin-dashboard-loader-card">
                  <VybogTALLoaderAnimation
                    animationHeight="110px"
                    animationWidth="110px"
                  />
                </div>
              ) : filteredTeamWorkload.length ? (
                <PipelineStatusCard
                  data={filteredTeamWorkload}
                  containerRef={teamWorkloadScrollRef}
                  isDarkMode={isDarkMode}
                  isLoadingMore={isTeamWorkloadFetchingMore}
                />
              ) : (
                <div className="admin-dashboard-empty-card">
                  <img
                    alt="noworkicon"
                    src={isDarkMode ? NoWorkDarkIcon : NoWorkLightIcon}
                    className="admin-dashboard-empty-img"
                  />
                  <span className="admin-dashboard-empty-text">
                    No workload data
                  </span>
                  <span className="admin-dashboard-fallback-text">
                    Assign jobs or candidates to see your team’s workload.
                  </span>
                </div>
              )}
            </div>
          </div>
          <ConfirmationPopup
            isOpen={openJobDeleteDialog}
            onClose={() => setOpenJobDeleteDialog(false)}
            onConfirm={handleDeletePipelineJob}
            title="Delete Job?"
            subTitle="Confirmation required"
            message={
              <span>
                This will delete{" "}
                <span
                  style={{
                    color: "#6665ea",
                    fontWeight: "bold",
                  }}
                >
                  "{applyEllipsis(selectedPipelineJob?.title, 35)}"
                </span>
                Are you sure you want to delete this job? This action cannot be
                undone.
              </span>
            }
            cancelLabel="No, Cancel"
            confirmLabel="Yes, Delete"
            confirmButtonClass="popup-button-danger"
          />
          {isNotificationPopupOpen && (
            <NotificationPopup
              isOpen={isNotificationPopupOpen}
              onClose={() => setIsNotificationPopupOpen(false)}
              notifications={notificationsState.notifications}
              unreadCount={notificationsState.unread_count}
              isDarkMode={isDarkMode}
            />
          )}

          <SidePopup
            isOpen={showRecruiterDetails}
            onClose={() => {
              setShowRecruiterDetails(false);
              setSelectedRecruiterDetails(null);
            }}
            title={
              [
                selectedRecruiterDetails?.firstName,
                selectedRecruiterDetails?.lastName,
              ]
                .filter(Boolean)
                .join(" ") ||
              selectedRecruiterDetails?.name ||
              "Recruiter Details"
            }
            sidePopupWidth="420px"
          >
            <div>
              <div
                style={{
                  color: "var(--header__color)",
                  fontSize: "12px",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                  marginTop: "4px",
                  marginBottom: "14px",
                }}
              >
                ACTIVITY BREAKDOWN
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px",
                }}
              >
                {[
                  {
                    label: "Resumes Parsed",
                    value: selectedRecruiterDetails?.resumesParsed,
                  },
                  {
                    label: "Jobs Created",
                    value: selectedRecruiterDetails?.jobsCreated,
                  },
                  {
                    label: "Candidates Created",
                    value: selectedRecruiterDetails?.candidatesCreated,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      minHeight: "82px",
                      padding: "14px 16px",
                      borderRadius: "10px",
                      backgroundColor: "var(--table__background, #ffffff)",
                      border: "1px solid var(--border__color, #e1e5ef)",
                      boxSizing: "border-box",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--text_color, #6b7280)",
                        fontSize: "11px",
                        fontWeight: "500",
                        marginBottom: "8px",
                      }}
                    >
                      {item.label}
                    </span>
                    <span
                      style={{
                        color: "var(--header__color)",
                        fontSize: "20px",
                        fontWeight: "600",
                      }}
                    >
                      {Number(item.value || 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              <div
                style={{
                  color: "var(--header__color)",
                  fontSize: "12px",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                  marginTop: "24px",
                  marginBottom: "14px",
                }}
              >
                CALL STAGES
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px",
                }}
              >
                {(selectedRecruiterDetails?.stages || []).map((stage) => (
                  <div
                    key={stage.system_key || stage.name}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      minHeight: "82px",
                      padding: "14px 16px",
                      borderRadius: "10px",
                      backgroundColor: "var(--table__background, #ffffff)",
                      border: "1px solid var(--border__color, #e1e5ef)",
                      boxSizing: "border-box",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--text_color, #6b7280)",
                        fontSize: "11px",
                        fontWeight: "500",
                        marginBottom: "8px",
                      }}
                    >
                      {toCapitalize(stage.name) || "N/A"}
                    </span>
                    <span
                      style={{
                        color: "var(--header__color)",
                        fontSize: "20px",
                        fontWeight: "600",
                      }}
                    >
                      {Number(stage.count || 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </SidePopup>

          <SidePopup
            isOpen={showVendorDetails}
            onClose={handleCloseVendorDetails}
            title={selectedVendorDetails?.name || "Vendor Details"}
            sidePopupStyle={{ width: "80%" }}
            headerRightComponent={
              <div style={{ display: "flex", alignItems: "center" }}>
                {isEditingVendor ? (
                  <>
                    <Button
                      label="Save"
                      event={saveVendorChanges}
                      style={{ marginRight: "10px", padding: "8px 15px" }}
                      backgroundColor="#6860EE"
                      color="#FFFFFF"
                      border="none"
                    />
                    <Button
                      label="Cancel"
                      event={handleCancelEditVendor}
                      style={{ padding: "8px 15px" }}
                      backgroundColor="#fff"
                      color="#000"
                      border="1px solid #ddd"
                    />
                  </>
                ) : (
                  <>
                    <CustomTooltip title="Edit Vendor" arrow>
                      <img
                        src={editIcon}
                        alt="Edit"
                        onClick={handleEditVendor}
                        style={{
                          cursor: "pointer",
                          width: "22px",
                          padding: "8px 15px",
                        }}
                      />
                    </CustomTooltip>
                    {selectedVendorDetails?.id &&
                      !isCepheaVendor(selectedVendorDetails.id) && (
                        <CustomTooltip title="Delete Vendor" arrow>
                          <img
                            src={deleteIcon}
                            alt="Delete"
                            onClick={handleOpenVendorDeleteDialog}
                            style={{
                              cursor: "pointer",
                              width: "22px",
                              padding: "8px",
                            }}
                          />
                        </CustomTooltip>
                      )}
                  </>
                )}
              </div>
            }
          >
            {vendorDetailsLoading ? (
              <div
                className="admin-dashboard-loader-card"
                style={{
                  minHeight: "300px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VybogTALLoaderAnimation
                  animationHeight="110px"
                  animationWidth="110px"
                />
              </div>
            ) : selectedVendorDetails ? (
              <div className="client-main-container">
                {isEditingVendor && (
                  <div className="client-name-edit">
                    <TextInput
                      label="Vendor Name"
                      name="nameOfVendor"
                      placeholder="Vendor Name"
                      required={true}
                      onBlur={vendorFormik.handleBlur}
                      error={
                        vendorFormik.touched.nameOfVendor &&
                        vendorFormik.errors.nameOfVendor
                      }
                      errorMessage={vendorFormik.errors.nameOfVendor}
                      value={vendorFormik.values.nameOfVendor}
                      onChange={vendorFormik.handleChange}
                    />
                  </div>
                )}

                <div
                  style={{
                    padding: "0px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div
                    className={`vendor-candidate-info-outer-container ${
                      isEditingVendor ? "edit-mode" : ""
                    }`}
                  >
                    <div className="candidate-information">
                      <div className="info-header">
                        <img
                          src={accountIcon}
                          alt="company-icon"
                          style={{ width: "20px", height: "20px" }}
                        />
                        <h4 className="info-header-value">
                          Contact Person{" "}
                          {isEditingVendor && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </h4>
                      </div>

                      <div className="detail-content">
                        {isEditingVendor ? (
                          <TextInput
                            placeholder="Contact Person"
                            name="contactPersonName"
                            value={vendorFormik.values.contactPersonName}
                            required={true}
                            onBlur={vendorFormik.handleBlur}
                            error={
                              vendorFormik.touched.contactPersonName &&
                              vendorFormik.errors.contactPersonName
                            }
                            errorMessage={vendorFormik.errors.contactPersonName}
                            onChange={vendorFormik.handleChange}
                          />
                        ) : (
                          <span className="candidate-email">
                            {selectedVendorDetails?.contact_person || "N/A"}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="candidate-information">
                      <div className="info-header">
                        <img
                          src={jobIcon}
                          alt="job-icon"
                          style={{ width: "20px", height: "20px" }}
                        />
                        <h4 className="info-header-value">
                          Contact Designation{" "}
                        </h4>
                      </div>

                      <div className="detail-content">
                        {isEditingVendor ? (
                          <TextInput
                            name="contactDesignation"
                            placeholder="Designation"
                            value={vendorFormik.values.contactDesignation}
                            required={true}
                            onBlur={vendorFormik.handleBlur}
                            error={
                              vendorFormik.touched.contactDesignation &&
                              vendorFormik.errors.contactDesignation
                            }
                            errorMessage={
                              vendorFormik.errors.contactDesignation
                            }
                            onChange={vendorFormik.handleChange}
                          />
                        ) : (
                          <span className="candidate-email">
                            {selectedVendorDetails?.contact_designation ||
                              "N/A"}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="candidate-information">
                      <div className="info-header">
                        <img
                          src={mailIcon}
                          alt="mail-icon"
                          style={{ width: "20px", height: "20px" }}
                        />
                        <h4 className="info-header-value">
                          Email{" "}
                          {isEditingVendor && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </h4>
                      </div>

                      <div className="detail-content">
                        {isEditingVendor ? (
                          <TextInput
                            placeholder="Email"
                            name="email"
                            type="email"
                            value={vendorFormik.values.email}
                            required={true}
                            onBlur={vendorFormik.handleBlur}
                            error={
                              vendorFormik.touched.email &&
                              vendorFormik.errors.email
                            }
                            errorMessage={vendorFormik.errors.email}
                            onChange={vendorFormik.handleChange}
                          />
                        ) : (
                          <h5 className="client-details-value">
                            {selectedVendorDetails?.email || "N/A"}
                          </h5>
                        )}
                      </div>
                    </div>

                    <div className="candidate-information">
                      <div className="info-header">
                        <img
                          src={phoneIcon}
                          alt="phone-icon"
                          style={{ width: "20px", height: "20px" }}
                        />
                        <h4 className="info-header-value">
                          Phone{" "}
                          {isEditingVendor && (
                            <span style={{ color: "red" }}>*</span>
                          )}
                        </h4>
                      </div>

                      <div className="detail-content">
                        {isEditingVendor ? (
                          <PhoneNumberInput
                            value={vendorFormik.values.mobile}
                            onChange={handleVendorPhoneInputChange}
                            onBlur={vendorFormik.handleBlur}
                            error={vendorFormik.errors.mobile}
                            touched={vendorFormik.touched.mobile}
                            placeholder="e.g. (415) 555-0198"
                            width="100%"
                            height="40px"
                            countryListWidth="300px"
                            phoneContainerStyle={{ marginBottom: "15px" }}
                            required={true}
                          />
                        ) : (
                          <h5 className="client-details-value">
                            {selectedVendorDetails?.phone || "N/A"}
                          </h5>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="client-tabs-container">
                    {[
                      "Info",
                      "History",
                      "Documents",
                      "Vendor's Candidate",
                      "Additional Contacts",
                    ].map((tab) => (
                      <div
                        key={tab}
                        className={`tab ${
                          vendorDetailsActiveTab === tab ? "job-tab-active" : ""
                        }`}
                        onClick={() => handleVendorTabClick(tab)}
                      >
                        {tab}
                      </div>
                    ))}
                  </div>

                  <div className="client-details-content-container">
                    {vendorDetailsActiveTab === "Info" && (
                      <VendorInfo
                        selectedVendor={selectedVendorDetails}
                        setSelectedVendor={setSelectedVendorDetails}
                        setChangedVendor={() => {}}
                        isEditingVendor={isEditingVendor}
                        setIsEditingVendor={setIsEditingVendor}
                        fetchAllVendorsList={async () => {}}
                        formik={vendorFormik}
                        handlePhoneInputChange={handleVendorPhoneInputChange}
                      />
                    )}

                    {vendorDetailsActiveTab === "History" && (
                      <VendorTrackingHistory
                        selectedVendor={selectedVendorDetails}
                        getVendorID={selectedVendorDetails?.id}
                      />
                    )}

                    {vendorDetailsActiveTab === "Documents" && (
                      <VendorDocuments
                        selectedVendor={selectedVendorDetails}
                        isEditingVendor={isEditingVendor}
                        editAchFile={editAchFile}
                        editVoidChequeFile={editVoidChequeFile}
                        editAuditDocument={editAuditDocument}
                        handleEditAchFileChange={(e) =>
                          setEditAchFile(e.target.files[0])
                        }
                        handleEditVoidChequeFileChange={(e) =>
                          setEditVoidChequeFile(e.target.files[0])
                        }
                        handleEditAuditDocumentChange={(e) =>
                          setEditAuditDocument(e.target.files[0])
                        }
                        onVendorUpdate={async () => {
                          if (!selectedVendorDetails?.id) return;
                          try {
                            const response = await get(
                              VYLABS_BASE_URL,
                              `${GET_VENDOR_DETAILS_BY_ID}?id=${selectedVendorDetails.id}`,
                            );
                            setSelectedVendorDetails(response);
                          } catch (error) {
                            console.error(
                              "Error refreshing vendor details:",
                              error,
                            );
                          }
                        }}
                      />
                    )}

                    {vendorDetailsActiveTab === "Vendor's Candidate" && (
                      <VendorsCandidateList
                        selectedVendor={selectedVendorDetails}
                        getVendorID={selectedVendorDetails?.id}
                        onCandidateCardView={handleVendorCandidateCardView}
                      />
                    )}

                    {vendorDetailsActiveTab === "Additional Contacts" &&
                      (selectedVendorDetails?.contacts?.length ? (
                        <ContactCard
                          contacts={selectedVendorDetails.contacts}
                          title="Additional Contacts"
                        />
                      ) : (
                        <div
                          className="no-contacts-container"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "40px 20px",
                            textAlign: "center",
                          }}
                        >
                          <span
                            style={{
                              color: "var(--text_color)",
                              fontSize: "15px",
                              marginBottom: "15px",
                            }}
                          >
                            No additional contacts have been added for this
                            vendor yet.
                          </span>
                          <AnimationPlayer
                            animationSrc={NoDataFoundAnimation}
                            playerHeight="250px"
                            playerWidth="300px"
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : null}
          </SidePopup>
          <ConfirmationPopup
            isOpen={isVendorDeleteDialogOpen}
            onClose={() => setIsVendorDeleteDialogOpen(false)}
            onConfirm={handleDeleteVendors}
            title="Delete Vendor?"
            subTitle="Confirmation required"
            message={
              <span>
                This will delete{" "}
                <span
                  style={{
                    color: "#6665ea",
                    fontWeight: "bold",
                  }}
                >
                  "{applyEllipsis(selectedVendorDetails?.name, 35)}"
                </span>
                , Are you sure you want to delete this Vendor? This action
                cannot be undone.
              </span>
            }
            cancelLabel="No, Cancel"
            confirmLabel="Yes, Delete"
            confirmButtonClass="popup-button-danger"
          />
          {isVendorCandidateModalOpen && selectedVendorCandidateForModal && (
            <SidePopup
              isOpen={isVendorCandidateModalOpen}
              onClose={closeVendorCandidateModal}
              title="Candidate Details"
              width="80%"
            >
              <CandidateDetailsContainer
                candidate={selectedVendorCandidateForModal}
                activeTab={vendorCandidateActiveTab}
                setActiveTab={setVendorCandidateActiveTab}
                handleTabChange={handleVendorCandidateTabChange}
                hasSearched={false}
                hasRecommendedCandidates={false}
                isEditingCandidate={isEditingVendorCandidate}
                setIsEditingCandidate={setIsEditingVendorCandidate}
                handleEditCandidate={handleEditVendorCandidate}
                saveCandidateChanges={saveVendorCandidateChanges}
                resetCandidateEdit={resetVendorCandidateEdit}
                handleCandidateChange={handleVendorCandidateChange}
                editedCandidateData={editedVendorCandidateData}
                emailError={vendorCandidateEmailError}
                setEmailError={setVendorCandidateEmailError}
                emailTouched={vendorCandidateEmailTouched}
                setEmailTouched={setVendorCandidateEmailTouched}
                hideSearchBox={true}
                assignedVendor={assignedVendor}
              />
            </SidePopup>
          )}

          <StandardPopup
            isOpen={viewOpen}
            onClose={handleCreateViewClose}
            onSubmit={createFilter}
            submitLabel="Save"
            isSubmitting={isCreatingSavedView}
            title="Save Dashboard View"
            width="700px"
            maxHeight="95vh"
            popupStyle={{
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TextInput
              label="View Name"
              name="savedViewName"
              value={savedViewName}
              onChange={(event) => setSavedViewName(event.target.value)}
              placeholder="Save Name"
              required
            />
            {(selectedClientName || selectedRecruiterNames.length > 0) && (
              <div className="admin-saved-view-filter-summary">
                {selectedClientName && (
                  <div className="admin-saved-view-filter-row">
                    <span className="admin-saved-view-filter-label">
                      {isHiringTeamTenant ? "Hiring Team" : "Client"}
                    </span>
                    <span
                      className="admin-saved-view-filter-badge"
                      key={selectedClientName}
                    >
                      {selectedClientName}
                    </span>
                  </div>
                )}
                {selectedRecruiterNames.length > 0 && (
                  <div className="admin-saved-view-filter-row">
                    <span className="admin-saved-view-filter-label">
                      Recruiter
                    </span>
                    <div className="admin-saved-view-filter-badges">
                      {selectedRecruiterNames.map((filterName) => (
                        <span
                          className="admin-saved-view-filter-badge"
                          key={filterName}
                        >
                          {filterName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="required-field-card">
              <div className="required-field-content">
                <h4>Default</h4>
                <p>Use this saved view as the default dashboard view</p>
              </div>

              <ToggleSwitcher
                name="savedViewIsDefault"
                value={savedViewIsDefault}
                onChange={(_, checked) => setSavedViewIsDefault(checked)}
              />
            </div>
          </StandardPopup>

          <ConfirmationPopup
            isOpen={isSavedViewDeleteDialogOpen}
            onClose={handleSavedViewDeleteClose}
            onConfirm={() => handleDeleteViews(savedViewPendingDelete?.value)}
            title="Delete Saved Filter?"
            subTitle="Confirmation required"
            message={
              <span>
                This will delete{" "}
                <span
                  style={{
                    color: "#6665ea",
                    fontWeight: "bold",
                  }}
                >
                  "{applyEllipsis(savedViewPendingDelete?.label, 35)}"
                </span>
                . Are you sure you want to delete this saved filter?
              </span>
            }
            cancelLabel="No, Cancel"
            confirmLabel="Yes, Delete"
            confirmButtonClass="popup-button-danger"
            isSubmitting={isDeletingSavedView}
          />

          <SidePopup
            isOpen={showCandidateDetails}
            onClose={closeApplicationsPopup}
            title={
              applicationSource === "vendor"
                ? `${toCapitalize(vendorPipelineActionStatus || jobPipelineStage || "")} Recruiters${
                    vendorPipelineActionVendor
                      ? ` for ${applyEllipsis(vendorPipelineActionVendor, 30)}`
                      : ""
                  }`
                : isJobRecruiterMetric
                  ? `${jobPipelineStage === "recruiters" ? "Total" : jobPipelineStage === "assigned_recruiters" ? "Assigned" : "Tracked"} Recruiters For Job`
                  : jobPipelineStage !== undefined &&
                      storeJobTitle !== undefined &&
                      !isRecruiterApplicationsJobsClicked
                    ? `${toCapitalize(jobPipelineStageName)} Candidates ${applyEllipsis(storeJobTitle, 30)})`
                    : `Jobs ${applyEllipsis(storeJobTitle, 30)})`
            }
            sidePopupWidth={applicationSource === "vendor" ? "50%" : "70%"}
            children={
              <>
                {applicationSource === "vendor" ? (
                  <Table
                    columns={vendorPipelineActionColumns}
                    data={vendorPipelineActions}
                    pagination
                    paginationServer
                    paginationTotalRows={vendorPipelineActionsTotal}
                    paginationPerPage={JOB_AND_RECRUITER_APPLICATIONS_LIMIT}
                    currentPage={jobAndRecruiterApplicationsCurrentPage}
                    onChangePage={(page) => callJobOrRecruiterWithPage(page)}
                    progressPending={vendorPipelineActionsLoading}
                  />
                ) : isRecruiterApplicationsJobsClicked ? (
                  <Table
                    columns={jobRecruiterApplicationColumns}
                    data={recruiterApplicationsJobs}
                    pagination
                    paginationServer
                    paginationTotalRows={jobAndRecruiterApplicationsTotal}
                    paginationPerPage={JOB_AND_RECRUITER_APPLICATIONS_LIMIT}
                    currentPage={jobAndRecruiterApplicationsCurrentPage}
                    onChangePage={(page) => callJobOrRecruiterWithPage(page)}
                    progressPending={jobAndRecruiterApplicationsLoading}
                  />
                ) : (
                  <Table
                    columns={
                      isJobRecruiterMetric
                        ? recruiterMetricColumns
                        : applicationColumns
                    }
                    data={storeApplicationsByJob}
                    pagination
                    paginationServer
                    paginationTotalRows={jobAndRecruiterApplicationsTotal}
                    paginationPerPage={JOB_AND_RECRUITER_APPLICATIONS_LIMIT}
                    currentPage={jobAndRecruiterApplicationsCurrentPage}
                    onChangePage={(page) => callJobOrRecruiterWithPage(page)}
                    progressPending={jobAndRecruiterApplicationsLoading}
                  />
                )}
                <SidePopup
                  isOpen={isApplicationCandidatePopupOpen}
                  onClose={closeApplicationCandidatePopup}
                  sidePopupWidth="85%"
                  contentPadding="0px"
                  title="Candidate Details"
                  showDeleteButton={false}
                  headerRightComponent={
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                        marginRight: "10px",
                      }}
                    >
                      {applicationCandidateLoading ||
                      !selectedApplicationCandidate ? null : isEditingApplicationCandidate ? (
                        <div style={{ display: "flex", gap: "10px" }}>
                          <Button
                            label="Save"
                            event={saveApplicationCandidateChanges}
                            backgroundColor="#6860EE"
                            color="#FFFFFF"
                            border="none"
                            style={{ borderRadius: "12px" }}
                          />
                          <Button
                            label="Cancel"
                            event={resetApplicationCandidateEdit}
                            isCancel={true}
                            color="#000"
                            border="1px solid #ddd"
                            style={{ borderRadius: "12px", marginRight: "0px" }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            gap: "20px",
                            marginTop: "5px",
                          }}
                        >
                          {!selectedApplicationCandidate?.isFileBased &&
                            (selectedApplicationCandidate?.email ||
                              selectedApplicationCandidate?.parsedResumeData
                                ?.BasicInfo?.Email) && (
                              <CustomTooltip title="Reach Out" arrow>
                                <img
                                  src={ReachOutIcon}
                                  alt="Reach Out"
                                  onClick={() =>
                                    applicationCandidateDetailsRef.current?.openReachOut()
                                  }
                                  style={{ width: "24px", cursor: "pointer" }}
                                />
                              </CustomTooltip>
                            )}
                          {!selectedApplicationCandidate?.isFileBased && (
                            <CustomTooltip title="Edit Candidate" arrow>
                              <img
                                src={editIcon}
                                alt="Edit"
                                onClick={handleEditApplicationCandidate}
                                style={{ width: "22px", cursor: "pointer" }}
                              />
                            </CustomTooltip>
                          )}
                          <CustomTooltip title="Delete Candidate" arrow>
                            <img
                              src={deleteIcon}
                              alt="Delete"
                              onClick={
                                handleOpenApplicationCandidateDeleteDialog
                              }
                              style={{ width: "22px", cursor: "pointer" }}
                            />
                          </CustomTooltip>
                        </div>
                      )}
                    </div>
                  }
                >
                  <div style={{ padding: "20px" }}>
                    {applicationCandidateLoading ? (
                      <div
                        style={{
                          minHeight: "320px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <VybogTALLoaderAnimation
                          animationHeight="110px"
                          animationWidth="110px"
                        />
                      </div>
                    ) : (
                      selectedApplicationCandidate && (
                        <CandidateDetailsContainer
                          ref={applicationCandidateDetailsRef}
                          candidate={selectedApplicationCandidate}
                          isEditingCandidate={isEditingApplicationCandidate}
                          setIsEditingCandidate={
                            setIsEditingApplicationCandidate
                          }
                          handleEditCandidate={handleEditApplicationCandidate}
                          saveCandidateChanges={saveApplicationCandidateChanges}
                          resetCandidateEdit={resetApplicationCandidateEdit}
                          handleCandidateChange={
                            handleApplicationCandidateChange
                          }
                          editedCandidateData={editedApplicationCandidateData}
                          emailError={applicationCandidateEmailError}
                          setEmailError={setApplicationCandidateEmailError}
                          emailTouched={applicationCandidateEmailTouched}
                          setEmailTouched={setApplicationCandidateEmailTouched}
                          showCandidateRecommendedJobsTab={false}
                        />
                      )
                    )}
                  </div>
                </SidePopup>
              </>
            }
          />
          <ConfirmationPopup
            isOpen={isDeleteApplicationCandidatePopupOpen}
            onClose={() => setIsDeleteApplicationCandidatePopupOpen(false)}
            onConfirm={handleDeleteApplicationCandidate}
            title="Delete Candidate?"
            subTitle="Confirmation required"
            message={
              <span>
                You are about to delete{" "}
                <span
                  style={{
                    color: "#6665ea",
                    fontWeight: "bold",
                  }}
                >
                  "{selectedApplicationCandidate?.name}"
                </span>
                .
                <br />
                <br />
                This action will permanently remove this candidate details,
                including any associations with jobs or vendors.
                <br />
                <br />
                This action cannot be undone. Are you sure you want to delete?
              </span>
            }
            cancelLabel="No, Cancel"
            confirmLabel="Yes, Delete"
            confirmButtonClass="popup-button-danger"
          />
          {selectedPipelineJob && (
            <JobsDetailsContainer
              isOpen={isPipelineJobDetailsOpen}
              onClose={handleClosePipelineJobDetails}
              jobId={selectedPipelineJob.id}
              selectedJob={selectedPipelineJob}
              isEditingJob={isEditingPipelineJob}
              activeTab={pipelineJobDetailsActiveTab}
              setActiveTab={setPipelineJobDetailsActiveTab}
              formik={formik}
              recruiterOptions={recruiterMultiSelectOptions}
              onRecruiterSearch={handleDashboardRecruiterSearch}
              onAssignedToChange={handlePipelineJobAssignedToChange}
              isLoadingRecruiters={isLoadingMembers}
              editedJobData={editedPipelineJobData}
              handleEditJob={handleEditPipelineJob}
              handleJobChange={handlePipelineJobChange}
              saveJobChanges={savePipelineJobChanges}
              handleDeleteJob={handleOpenPipelineJobDeleteDialog}
              showDeleteJobButton={true}
              resetJobEdit={resetPipelineJobEdit}
              isEditable={true}
              assignedCount={pipelineAssignedCandidateCount}
              onAssignedCountChange={handlePipelineAssignedCountChange}
              isFetchingRecommendedCandidates={isFetchingRecommendedCandidates}
              recommendedCandidateIds={recommendedCandidateIds}
            />
          )}
        </div>
      ) : (
        <SuperAdminDashboard />
      )}
    </div>
  );
};

export default AdminDashboard;
